import {reconcileRecoveredRunCards} from './recovered-run-cards';
import {recordConceptPoll} from './concept-progress-liveness';
import {persistTaskInspire} from './task-inspire';
import { canvasImageSelection, SUNBURST_RATIOS } from '@shared/sunburst';
import type {CanvasFollowUpOrigin} from '@shared/canvasFollowUp';
import { consumeComposerReferences, type ComposerReferenceSnapshot } from './composer-references';
import { mergePlaybookJobs, setPlaybookStreamActive } from './playbook-queue';
import { recoverDetachedConceptRun } from './concept-stream-recovery';
import { reconcileImagePositions } from './reconcile-image-positions';
import { CANVAS_AD_ROW_GAP, canvasCellSize, canvasBatchOrigin } from '@shared/canvasGeometry';
import { create } from 'zustand';
import { resolveProjectNavigation, loadProjectNavigation } from './project-navigation';
import { scheduleRequestWatchdog } from './request-watchdog';
import { withTaskPanels, taskPanelPatch, type TaskPanels } from './task-panels';
import { queryClient } from '../../lib/queryClient';
import { triggerCreditsGate } from '../../lib/credits-gate';
import type {
  CanvasImage,
  CanvasMark,
  CanvasComment,
  CanvasProject,
  CanvasTask,
  CanvasChatMessage,
  CanvasTool,
  CanvasView,
  ContextMenuState,
  CommentPopoverState,
  MarkPopoverState,
  CanvasSettings,
  UndoAction,
  TaskStep,
  CanvasPlaceholder,
  CanvasGenerationRow,
  PendingEdit,
  ActiveResearchTask,
  ResearchTaskStep,
  IntentType,
  WebResearchSource,
  AdMapRecord,
  AgentActionCard,
  AgentTaskGroup,
  AgentTaskStep,
  AgentDocument,
  AdCardData,
  WorkflowState,
  WorkflowNudge,
  WorkflowCheckpointPayload,
  WorkflowTaskCardData,
  QAStatus,
  WorkerProgressEntry,
  ProjectState,
  PlaybookProgressState,
} from './canvas-types';
import { defaultProjectState } from './canvas-types';
import { hydrateConversation, toLlmHistory, settleTaskGroups } from './conversation-hydrate';
import { computeGridLayout, hasOverlaps } from './canvas-layout';
import { recoverRunPlaceholders, persistedCanvasImageId, recoveredRunImageSlots, isActiveRun, type RecoverableRun } from './canvas-run-recovery';
import type { AdViewData } from './SidebarPanel';
import type { ProjectState as _ProjectStateType } from './canvas-types';

// Internal helper: returns the active project's state, or a fresh default
// when none is active. Lets internal canvas-store actions read project-scoped
// fields without each call site having to do the projects[id] dance.
function activeProj(state: { projects: Record<number, _ProjectStateType>; activeProjectId: number | null }): _ProjectStateType {
  if (state.activeProjectId == null) return defaultProjectState(-1);
  return state.projects[state.activeProjectId] ?? defaultProjectState(state.activeProjectId);
}

interface AdPerfToolAd {
  name?: string;
  headline?: string;
  spend?: string | number;
  roas?: string | number;
  ctr?: string | number;
  clicks?: string | number;
  impressions?: string | number;
  status?: string;
  previewUrl?: string;
  image_url?: string;
  video_url?: string;
  body?: string;
  description?: string;
  cta?: string;
  reach?: number;
  cpc?: string | number;
  purchases?: number;
  revenue?: string | number;
  campaignName?: string;
  adSetName?: string;
}

interface AdCampaignToolAd {
  id?: string | number;
  name?: string;
  status?: string;
  previewUrl?: string;
  image_url?: string;
  video_url?: string;
  headline?: string;
  body?: string;
  cta?: string;
}

interface AdCampaignToolAdGroup {
  id?: string | number;
  name?: string;
  status?: string;
  ads?: AdCampaignToolAd[];
}

interface AdCampaignToolCampaign {
  id?: string | number;
  name?: string;
  status?: string;
  adGroups?: AdCampaignToolAdGroup[];
}

interface GetAdPerfToolData {
  topPerformingAds?: AdPerfToolAd[];
  ads?: AdPerfToolAd[];
  results?: AdPerfToolAd[];
}

type GetCampaignToolData = AdCampaignToolCampaign[];

function normalizeAdPerfEntry(ad: AdPerfToolAd, index: number): AdViewData {
  const key = ad.name || `ad-${index}`;
  return {
    id: key,
    name: ad.name || key,
    headline: ad.headline,
    adCopy: ad.body || ad.description,
    cta: ad.cta,
    status: ad.status || 'ACTIVE',
    campaignName: ad.campaignName,
    adSetName: ad.adSetName,
    thumbnailUrl: ad.previewUrl || ad.image_url || undefined,
    videoUrl: ad.video_url || undefined,
    spend: ad.spend !== undefined ? parseFloat(String(ad.spend)) : undefined,
    roas: ad.roas !== undefined ? parseFloat(String(ad.roas)) : undefined,
    ctr: ad.ctr !== undefined ? parseFloat(String(ad.ctr)) : undefined,
    clicks: ad.clicks !== undefined ? parseInt(String(ad.clicks), 10) : undefined,
    impressions: ad.impressions !== undefined ? parseInt(String(ad.impressions), 10) : undefined,
    reach: ad.reach,
    cpc: ad.cpc !== undefined ? parseFloat(String(ad.cpc)) : undefined,
    purchases: ad.purchases,
    revenue: ad.revenue !== undefined ? parseFloat(String(ad.revenue)) : undefined,
  };
}

function normalizeCampaignAd(ad: AdCampaignToolAd, campaignName?: string, adSetName?: string): AdViewData {
  const key = String(ad.id || ad.name || 'unknown');
  return {
    id: key,
    name: ad.name || key,
    headline: ad.headline,
    adCopy: ad.body,
    cta: ad.cta,
    status: ad.status || 'ACTIVE',
    campaignName,
    adSetName,
    thumbnailUrl: ad.previewUrl || ad.image_url || undefined,
    videoUrl: ad.video_url || undefined,
  };
}

function extractAdViewEntries(toolData: Record<string, unknown>): AdViewData[] {
  const entries: AdViewData[] = [];
  const perfData = toolData as unknown as GetAdPerfToolData;

  const topAds = perfData.topPerformingAds || perfData.ads || perfData.results || [];
  topAds.forEach((ad, i) => {
    if (ad && (ad.name || ad.ctr)) {
      entries.push(normalizeAdPerfEntry(ad, i));
    }
  });

  if (Array.isArray(toolData)) {
    const campaigns = toolData as unknown as GetCampaignToolData;
    for (const campaign of campaigns) {
      for (const adGroup of campaign.adGroups || []) {
        for (const ad of adGroup.ads || []) {
          if (ad.id || ad.name) {
            entries.push(normalizeCampaignAd(ad, campaign.name, adGroup.name));
          }
        }
      }
    }
  }

  return entries;
}

const WEB_RESEARCH_INTERNAL_PATTERNS = [
  'my ads', 'my brand', 'my performance', 'my spend', 'my roas', 'my ctr', 'my campaigns',
  'my kpi', 'my budget', 'my account', 'my metrics', 'my conversion', 'my funnel',
  'our ads', 'our brand', 'our performance', 'our spend', 'our campaigns',
  'our kpi', 'our budget', 'our account', 'our metrics', 'our conversion', 'our funnel',
  'my ad account', 'our ad account', 'my results', 'our results',
  'my impressions', 'our impressions', 'my clicks', 'our clicks',
  'my reach', 'our reach', 'my cost', 'our cost',
];

const STRONG_WEB_KEYWORDS = [
  'market trend', 'market research', 'industry trend', 'industry report',
  'competitor analysis', 'competitive landscape', 'market overview',
  'market forecast', 'market statistics', 'market data',
  'industry statistics', 'industry data', 'industry overview',
  'best practices for', 'benchmarks for',
  'ad strategies', 'advertising strategies', 'marketing strategies',
  'top performing', 'top strategies', 'search for', 'look up', 'find out about',
  'research the', 'research top', 'research best',
];

const WEB_TOPIC_KEYWORDS = [
  'market', 'trend', 'trends', 'industry', 'competitor', 'competitors', 'landscape',
  'statistics', 'stats', 'data on', 'overview of', 'summary of',
  'best practices', 'benchmarks', 'growth', 'forecast',
  'strategies', 'strategy', 'tactics', 'latest', 'current',
  'ecommerce', 'e-commerce', 'social media',
];

const GENERIC_PHRASES = ['what is', 'what are', 'how does', 'how do', 'explain', 'tell me about', 'learn about'];

function isWebResearchClientIntent(message: string): boolean {
  const m = message.toLowerCase();
  if (WEB_RESEARCH_INTERNAL_PATTERNS.some((kw) => m.includes(kw))) return false;
  if (STRONG_WEB_KEYWORDS.some((kw) => m.includes(kw))) return true;
  const hasYearRef = /\b20\d{2}\b/.test(m);
  const hasGenericPhrase = GENERIC_PHRASES.some((kw) => m.includes(kw));
  const hasWebTopic = WEB_TOPIC_KEYWORDS.some((kw) => m.includes(kw));
  if (hasGenericPhrase && hasWebTopic) return true;
  if (hasWebTopic && hasYearRef) return true;
  return hasWebTopic && !hasGenericPhrase;
}

function detectMessageIntent(message: string): IntentType {
  const lower = message.toLowerCase().trim();
  const adMapKeywords = ['admap', 'ad map', 'campaign map', 'campaign strategy', 'strategy planner', 'ad strategy', 'ad tree', 'campaign tree', 'build an admap', 'create an admap', 'plan my campaign', 'plan my ads', 'campaign plan'];
  if (adMapKeywords.some((kw) => lower.includes(kw))) return 'admap';
  if (isWebResearchClientIntent(lower)) return 'conversation';
  const urlPattern = /\b(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+\.(?:com|io|co|net|org|app|ai|gg|ly|me|us|uk|ca|au|de|fr|es|it|jp|in|br)\b/i;
  if (urlPattern.test(message)) return 'research';
  const researchKeywords = ['research', 'analyze', 'analyse', 'brand analysis', 'brand research', 'competitor', 'competitors', 'competitive', 'market research', 'investigate', 'look into', 'tell me about', 'find out about', 'audit', 'brand audit', 'ad library', 'facebook ads', 'instagram ads', 'brand intelligence', 'brand report', 'deep dive'];
  if (researchKeywords.some((kw) => lower.includes(kw))) return 'research';
  const analysisKeywords = ['performance', 'analytics', 'metrics', 'kpi', 'roas', 'ctr', 'cpc', 'attribution', 'funnel', 'conversion'];
  if (analysisKeywords.some((kw) => lower.includes(kw))) return 'analysis';
  const creationKeywords = ['create', 'generate', 'make', 'design', 'build', 'produce', 'image', 'images', 'ad', 'ads', 'creative', 'creatives', 'banner', 'poster', 'visual', 'artwork', 'photo', 'picture'];
  if (creationKeywords.some((kw) => lower.includes(kw))) return 'creation';
  const editKeywords = ['edit', 'modify', 'change', 'update', 'rewrite', 'revise', 'fix', 'improve'];
  if (editKeywords.some((kw) => lower.includes(kw))) return 'edit';
  return 'conversation';
}

let conversationSaveTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedSaveConversation(saveFn: () => void, delay = 200) {
  if (conversationSaveTimer) clearTimeout(conversationSaveTimer);
  conversationSaveTimer = setTimeout(() => { saveFn(); conversationSaveTimer = null; }, delay);
}

// v4.50 — one catch-up poll loop per project at a time.
const runCatchUpPolls = new Set<number>();
// v7.43 — one concepts-run watcher per assistant message id at a time.
const angleSetRunWatches = new Set<string>();

const imagePatchTimers = new Map<string, ReturnType<typeof setTimeout>>();
function debouncedPatchImage(imageId: string, patchFn: () => void, delay = 500) {
  const existing = imagePatchTimers.get(imageId);
  if (existing) clearTimeout(existing);
  imagePatchTimers.set(imageId, setTimeout(() => { patchFn(); imagePatchTimers.delete(imageId); }, delay));
}

// v4.47 — reload-proof run cards. Conversation history is CLIENT-saved
// (debounced after the stream's `done`), so a laptop sleep / tab close
// mid-run means the playbook card only ever existed in the dead tab's
// memory — the chat reloads with "Starting your ad campaign now." and
// nothing else while the ads sit on canvas ("the cards in the chat are
// missing"). workflow_runs IS the durable record: when a hydrated chat
// has completed runs but no playbook card, synthesize a completed card
// per run from the run row + the project's persisted images/QA.
const RUN_CARD_LABELS: Record<string, string> = {
  'campaign-v2': 'Campaign Start',
  'campaign-targeted': 'Campaign Start',
  'campaign-v3': 'Winning Ads Clone',
  'variations-v3': 'Ad Variations',
  'edit-fixes': 'Edit Fixes',
  'angles-to-campaign': 'Ad angles to campaign',
};

async function synthesizeRunCards(
  projectId: number,
  messages: CanvasChatMessage[],
  images: CanvasImage[],
): Promise<CanvasChatMessage[]> {
  try {
    const resp = await fetch(`/api/workflows?projectId=${projectId}`);
    if (!resp.ok) return messages;
    const runs = (await resp.json()) as RecoverableRun[];
    if (!Array.isArray(runs)) return messages;
    messages = recoverActiveRunCards(runs, messages, images);
    if (messages.some((m) => m.playbookProgress)) return messages;
    const completed = runs
      .filter((r) => r.status === 'completed' && RUN_CARD_LABELS[String(r.skeletonKey ?? '')])
      .sort((a, b) => a.id - b.id)
      .slice(-3);
    if (completed.length === 0) return messages;
    const cards: CanvasChatMessage[] = completed.map((run) => {
      // Prefer exact run attribution; single-run chats fall back to every
      // QA'd image (runs 965/966 predate reliable workflow_run_id stamping).
      const tagged = images.filter((img) => img.workflowRunId === run.id);
      const runImages = tagged.length > 0
        ? tagged
        : completed.length === 1
          ? images.filter((img) => img.qaPassed != null)
          : [];
      const qaResults: PlaybookProgressState['qaResults'] = runImages.map((img, i) => ({
        conceptIndex: i,
        attempt: 1,
        score: img.qaPassed ? 8 : 5,
        passed: !!img.qaPassed,
        status: img.qaPassed ? 'passed' : 'needs_review',
      }));
      const passed = qaResults.filter((r) => r.passed).length;
      return {
        id: `run-card-${run.id}`,
        role: 'assistant',
        content: runImages.length > 0
          ? `Run complete — ${passed}/${qaResults.length} ads passed QA. They're on your canvas.`
          : `Run complete — your ads are on the canvas.`,
        imageSlots: runImages.map((img, i) => ({ index: i, resolved: img.src })),
        playbookProgress: {
          playbookName: RUN_CARD_LABELS[String(run.skeletonKey ?? '')],
          totalPhases: 1,
          phases: [{ id: 'ad_generation', name: 'Ad Generation', phaseType: 'generation' }],
          phaseStatuses: { ad_generation: 'completed' },
          phaseStatusTexts: { ad_generation: runImages.length > 0 ? `Generated ${runImages.length} ads` : 'Ad Generation' },
          activePhaseId: null,
          qaResults,
          conceptProgress: null,
          completedSummary: null,
          isDone: true,
        },
      };
    });
    return reconcileRecoveredRunCards([...messages, ...cards]);
  } catch {
    return messages;
  }
}

function recoverActiveRunCards(runs: RecoverableRun[], messages: CanvasChatMessage[], images: CanvasImage[]): CanvasChatMessage[] {
  let next = messages;
  for (const run of runs) {
    const label = RUN_CARD_LABELS[String(run.skeletonKey ?? '')];
    if (!label) continue;
    const runImages = images.filter(image=>image.workflowRunId===run.id && image.src);
    const normalizeName = (name: string) => name.toLowerCase().replace(/^ad\s+/, '').replace(/[^a-z]/g, '');
    const index = next.findIndex(m => m.playbookProgress?.workflowRunId === run.id || m.id === `run-card-${run.id}` ||
      (m.role === 'assistant' && !!m.playbookProgress && !m.playbookProgress.workflowRunId && runImages.some(image=>m.imageSlots?.some(slot=>slot.resolved===image.src) || m.thumbnails?.includes(image.src))) ||
      (isActiveRun(run) && m.playbookProgress && !m.playbookProgress.isDone && !m.playbookProgress.workflowRunId && normalizeName(m.playbookProgress.playbookName) === normalizeName(label)));
    if (!isActiveRun(run) && index < 0 && runImages.length === 0) continue;
    // A connected stream owns its detailed phase events. Recovery owns only
    // synthetic cards, so a poll cannot erase richer live progress.
    if (index >= 0 && next[index].id !== `run-card-${run.id}`) {
      const existing = next[index];
      if (!existing.isStreaming) next = next.map((m,i)=>i===index ? { ...m, imageSlots: recoveredRunImageSlots(m, run.id, images), playbookProgress: {
        ...m.playbookProgress!, workflowRunId: run.id,
        ...(!isActiveRun(run) ? { isDone: true, activePhaseId: null, phaseStatuses: Object.fromEntries(m.playbookProgress!.phases.map(phase=>[phase.id, run.status === 'completed' ? 'completed' as const : 'failed' as const])) } : {}),
      } } : m);
      continue;
    }
    const done = !isActiveRun(run);
    const count = images.filter(image => image.workflowRunId === run.id && image.src).length;
    const message: CanvasChatMessage = {
      id: `run-card-${run.id}`, role: 'assistant', content: '',
      imageSlots: recoveredRunImageSlots(index >= 0 ? next[index] : {id:'',role:'assistant',content:''}, run.id, images),
      playbookProgress: {
        workflowRunId: run.id, playbookName: label, totalPhases: 1,
        phases: [{ id: 'ad_generation', name: 'Ad Generation', phaseType: 'generation' }],
        phaseStatuses: { ad_generation: done ? (run.status === 'completed' ? 'completed' : 'failed') : 'running' },
        phaseStatusTexts: { ad_generation: done ? `${count} ads generated` : `${count} ads ready — generation in progress` },
        activePhaseId: done ? null : 'ad_generation', qaResults: [], conceptProgress: null, completedSummary: null, isDone: done,
      },
    };
    next = index < 0 ? [...next, message] : next.map((m,i)=>i===index ? message : m);
  }
  return reconcileRecoveredRunCards(next);
}

function escXml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function makeSvgUrl(w: number, h: number, headline: string, origW: number, origH: number, idx: number): string {
  const palettes = [
    ['#16a34a', '#052e16'], ['#15803d', '#064e3b'], ['#166534', '#022c22'],
    ['#14532d', '#052e16'], ['#22c55e', '#166534'], ['#4ade80', '#166534'],
    ['#86efac', '#14532d'], ['#34d399', '#064e3b'], ['#6ee7b7', '#065f46'],
    ['#a7f3d0', '#052e16'], ['#d1fae5', '#166534'], ['#ecfdf5', '#15803d'],
  ];
  const [c1, c2] = palettes[idx % palettes.length];
  const fs = Math.round(Math.min(w * 0.075, 22));
  const hs = Math.round(Math.min(w * 0.1, 30));
  const ds = Math.round(Math.min(w * 0.058, 16));
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="${c1}"/><stop offset="100%" stop-color="${c2}"/></linearGradient></defs><rect width="${w}" height="${h}" fill="url(#g)" rx="10"/><rect x="20" y="20" width="${Math.round(w*0.3)}" height="3" fill="rgba(255,255,255,0.25)" rx="1"/><rect x="20" y="29" width="${Math.round(w*0.18)}" height="3" fill="rgba(255,255,255,0.15)" rx="1"/><text x="${Math.round(w/2)}" y="${Math.round(h*0.22)}" text-anchor="middle" fill="rgba(255,255,255,0.7)" font-family="system-ui,sans-serif" font-size="${fs}px" font-weight="500">Balanced Botanicals</text><line x1="${Math.round(w*0.3)}" y1="${Math.round(h*0.33)}" x2="${Math.round(w*0.7)}" y2="${Math.round(h*0.33)}" stroke="rgba(255,255,255,0.2)" stroke-width="1"/><text x="${Math.round(w/2)}" y="${Math.round(h*0.52)}" text-anchor="middle" dominant-baseline="middle" fill="white" font-family="system-ui,sans-serif" font-size="${hs}px" font-weight="700">${escXml(headline)}</text><rect x="${Math.round(w/2-30)}" y="${Math.round(h*0.7)}" width="60" height="${Math.round(hs*1.5)}" fill="rgba(255,255,255,0.15)" rx="${Math.round(hs*0.75)}"/><text x="${Math.round(w/2)}" y="${Math.round(h*0.7+hs*0.9)}" text-anchor="middle" dominant-baseline="middle" fill="white" font-family="system-ui,sans-serif" font-size="${Math.round(hs*0.65)}px" font-weight="600">Shop Now</text><text x="${Math.round(w/2)}" y="${Math.round(h*0.88)}" text-anchor="middle" fill="rgba(255,255,255,0.4)" font-family="system-ui,sans-serif" font-size="${ds}px">${origW} × ${origH}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const HEADLINES = ['Feel Calm.', "Nature's Answer", 'Sleep Better', 'Daily Calm Ritual', 'Variety Pack', 'Feel Calm.', "Nature's Answer", 'Sleep Better', 'Daily Calm Ritual', 'Variety Pack', 'Feel Calm.', "Nature's Answer"];
const RAW_IMAGES: Array<{ origW: number; origH: number; dispW: number; dispH: number }> = [
  { origW: 1080, origH: 1080, dispW: 270, dispH: 270 }, { origW: 1080, origH: 1350, dispW: 270, dispH: 338 },
  { origW: 1200, origH: 628, dispW: 300, dispH: 157 }, { origW: 1080, origH: 1920, dispW: 270, dispH: 480 },
  { origW: 1080, origH: 1080, dispW: 270, dispH: 270 }, { origW: 1200, origH: 628, dispW: 300, dispH: 157 },
  { origW: 1080, origH: 1350, dispW: 270, dispH: 338 }, { origW: 1080, origH: 1080, dispW: 270, dispH: 270 },
  { origW: 1200, origH: 628, dispW: 300, dispH: 157 }, { origW: 1080, origH: 1920, dispW: 270, dispH: 480 },
  { origW: 1080, origH: 1080, dispW: 270, dispH: 270 }, { origW: 1080, origH: 1350, dispW: 270, dispH: 338 },
];
const COL_WIDTH = 270, GAP = 24, COL_START = 40;
const COL_X = [COL_START, COL_START + COL_WIDTH + GAP, COL_START + 2 * (COL_WIDTH + GAP)];

function buildSeedImages(): CanvasImage[] {
  const colY = [40, 40, 40];
  const images: CanvasImage[] = [];
  for (let i = 0; i < RAW_IMAGES.length; i++) {
    const col = i % 3;
    const { origW, origH, dispW, dispH } = RAW_IMAGES[i];
    const x = COL_X[col], y = colY[col];
    images.push({ id: `img-${i}`, name: `bb_spring_0${i + 1}.jpg`, src: makeSvgUrl(dispW, dispH, HEADLINES[i], origW, origH, i), x, y, displayWidth: dispW, displayHeight: dispH, originalWidth: origW, originalHeight: origH, colorIndex: i });
    colY[col] = y + dispH + GAP;
  }
  return images;
}

const SEED_IMAGES = buildSeedImages();
const SEED_PROJECTS: CanvasProject[] = [
  { id: 'p1', name: 'Balanced Botanicals — Spring 2026', imageCount: 12, color: '#16a34a' },
  { id: 'p2', name: 'Summer Collection', imageCount: 8, color: '#0ea5e9' },
  { id: 'p3', name: 'Email Re-engagement', imageCount: 5, color: '#8b5cf6' },
];
const SEED_TASKS: CanvasTask[] = [
  { id: 't1', label: 'Generate hero variants (×4)', status: 'done' }, { id: 't2', label: 'Upscale portrait creatives', status: 'done' },
  { id: 't3', label: 'Remove backgrounds — batch', status: 'done' }, { id: 't4', label: 'Analyze competitor hooks', status: 'running' },
  { id: 't5', label: 'Write story copy variants', status: 'pending' }, { id: 't6', label: 'Export for Meta Ads Manager', status: 'pending' },
];

const MAX_UNDO = 50;

// 1×N row-per-batch layout constants (task spec)
const START_X = 60;
const START_Y = 60;
// 40px gap so the QA/info badges (which hang ~28px below the image bottom
// via `bottom: -28` in ImageNode) clear the next tile with breathing room.
// Was 24px, which made badges clip into the tile to the right.
const IMAGE_GAP = 40;
const ROW_GAP = CANVAS_AD_ROW_GAP;
const batchGridSteps = new Map<string, { x: number; y: number }>();
const DEFAULT_WIDTH = 300;
const DEFAULT_HEIGHT = 300;

// Legacy constants kept for addImageNextTo (edit/regen neighbour placement)
const GEN_GAP = 24;
const GEN_START_X = 60;
const GEN_START_Y = 60;

function isVirtualImage(img: CanvasImage): boolean {
  return /^img-\d+$/.test(img.id) || img.id.startsWith('img-gen-demo-') || img.src.startsWith('data:');
}

/**
 * Defensive de-stack: two images at the EXACT same (x,y) render as one — the
 * later one hidden directly behind the earlier. That's the recurring symptom of
 * a slot collision during generation (e.g. a QA-retry tail landing on a slot a
 * sibling concept already took). Exact pixel overlap is virtually always a bug,
 * never intent, so nudge any colliding image to the next free column to the
 * right until every image has a unique spot. Non-mutating: only the moved images
 * are new objects.
 */
// v2.70 — tidyGrid replaces the old unstackOverlaps. The old version only ever
// shifted a colliding tile RIGHTWARD on the same Y and checked only against
// already-placed tiles (order-dependent), so two tiles dropped at the exact
// same slot stayed stacked (one hidden behind the other) and off-grid coords
// (e.g. 804.8, -189.6 from drags or buggy sync) were never corrected. Real
// canvas dumps showed 5 same-slot collisions + 4 off-grid tiles after multi-
// batch runs ("the last 3 generations stacked weirdly, hiding them").
//
// tidyGrid is a no-op when the canvas is already clean (no overlaps) — so a
// user's manual arrangement is preserved. When ANY two real tiles overlap, it
// re-lays ALL real tiles into a clean ordered 5-column grid via the shared,
// unit-tested computeGridLayout (same grid the server uses). Virtual
// placeholders/seeds are left untouched. Used on project load + reconcile;
// the live-generation path uses relayoutCanvasContent (images + placeholders).
function tidyGrid(images: CanvasImage[]): CanvasImage[] {
  const real = images.filter((im) => !isVirtualImage(im));
  if (real.length < 2) return images;
  const moves = computeGridLayout(
    real.map((im) => ({ key: im.id, x: im.x, y: im.y, w: im.displayWidth || DEFAULT_WIDTH, h: im.displayHeight || DEFAULT_HEIGHT })),
  );
  if (moves.size === 0) return images; // already clean — same reference, no render churn
  return images.map((im) => {
    const p = moves.get(im.id);
    return p ? { ...im, x: p.x, y: p.y } : im;
  });
}

/**
 * Compute the Y offset for the next new batch row, based on all previously tracked rows
 * and any real (non-seed) images that may have been placed before row tracking existed.
 */
function getNextRowY(existingRows: CanvasGenerationRow[], existingImages?: CanvasImage[]): number {
  if (existingRows.length > 0) {
    const lastRow = existingRows[existingRows.length - 1];
    return lastRow.rowY + lastRow.rowHeight + ROW_GAP;
  }
  // No rows tracked yet — start below any real images, or at START_Y
  const realImages = existingImages ? existingImages.filter(img => !isVirtualImage(img)) : [];
  if (realImages.length > 0) {
    const maxBottom = Math.max(...realImages.map(img => img.y + img.displayHeight));
    return maxBottom + ROW_GAP;
  }
  return START_Y;
}

/**
 * Calculate the canvas positions for all `count` images in a new batch row.
 * All images are placed in a single horizontal row at `rowY`.
 * Returns an array of {x, y} positions, one per image in the batch.
 */
function calculateBatchPositions(
  count: number,
  rowY: number,
  displayWidth: number = DEFAULT_WIDTH,
): Array<{ x: number; y: number }> {
  return Array.from({ length: count }, (_, i) => ({
    x: START_X + i * (displayWidth + IMAGE_GAP),
    y: rowY,
  }));
}

// Build the keyed-by-imageId index from a flat comments array
function buildCommentsByImageId(comments: CanvasComment[]): Record<string, CanvasComment[]> {
  return comments.reduce<Record<string, CanvasComment[]>>((acc, c) => {
    if (!acc[c.imageId]) acc[c.imageId] = [];
    acc[c.imageId].push(c);
    return acc;
  }, {});
}

// Compute the display dimensions for a given original width/height (mirrors addImageToCanvas logic)
function computeDisplaySize(width: number, height: number): { displayWidth: number; displayHeight: number } {
  const scale = Math.min(DEFAULT_WIDTH / width, DEFAULT_HEIGHT / height, 1);
  return {
    displayWidth: Math.round(width * scale),
    displayHeight: Math.round(height * scale),
  };
}

function stripCommandTags(text: string): string {
  return text
    .replace(/\[GENERATE_IMAGE:\s*\{[^}]*\}(\s*\})*\]/g, '')
    .replace(/\[GENERATE_IMAGE:[^\]]*\]/g, '')
    .replace(/\[EDIT_IMAGE:\s*\{[^}]*\}(\s*\})*\]/g, '')
    .replace(/\[EDIT_IMAGE:[^\]]*\]/g, '')
    .replace(/\[KPI_CARDS:\s*\{[\s\S]*?\}\s*\]/g, '')
    .trim();
}

function suppressIncompleteArtifact(text: string): string {
  const artifactStart = text.indexOf('[ARTIFACT:');
  if (artifactStart === -1) return text;
  return text.slice(0, artifactStart).trim();
}

function stripArtifactTags(text: string): string {
  return text.replace(/\[ARTIFACT:\s*\{[\s\S]*?\}\s*\]/g, '').trim();
}

function generateFollowUpChips(assistantResponse: string, imagesGenerated: number): string[] {
  const chips: string[] = [];
  const lower = assistantResponse.toLowerCase();
  if (imagesGenerated > 0) {
    chips.push(`Generate ${imagesGenerated} more variations`);
    chips.push('Upscale all to 4K');
    chips.push('Remove backgrounds');
    chips.push('Create ad copy for these');
  }
  if (lower.includes('brand') || lower.includes('competitor') || lower.includes('analysis')) {
    chips.push('Show competitor patterns');
  }
  if (lower.includes('style') || lower.includes('creative') || lower.includes('design')) {
    chips.push('Try a different style');
  }
  if (!chips.some((c) => c.includes('story'))) {
    chips.push('Create story format versions');
  }
  return chips.slice(0, 4);
}

function parseSSEBlock(block: string): { type: string; data: any } | null {
  const lines = block.split('\n');
  let eventType = '';
  let dataStr = '';
  for (const line of lines) {
    if (line.startsWith('event: ')) eventType = line.slice(7).trim();
    else if (line.startsWith('data: ')) dataStr = line.slice(6);
  }
  if (!eventType || !dataStr) return null;
  try {
    return { type: eventType, data: JSON.parse(dataStr) };
  } catch {
    return { type: eventType, data: dataStr };
  }
}

interface ApplyResult {
  images?: CanvasImage[];
  comments?: CanvasComment[];
}

function applyUndoActionBackward(action: UndoAction, images: CanvasImage[], comments: CanvasComment[]): ApplyResult {
  switch (action.type) {
    case 'MOVE':
      return {
        images: images.map((img) => {
          const u = action.updates.find((u) => u.id === img.id);
          return u ? { ...img, x: u.fromX, y: u.fromY } : img;
        }),
      };
    case 'RESIZE':
      return {
        images: images.map((img) => {
          if (img.id !== action.id) return img;
          const updated = { ...img, displayWidth: action.fromW, displayHeight: action.fromH };
          if (action.fromX !== undefined) updated.x = action.fromX;
          if (action.fromY !== undefined) updated.y = action.fromY;
          return updated;
        }),
      };
    case 'DELETE':
      return { images: [...images, ...action.images] };
    case 'ADD':
      return { images: images.filter((img) => !action.images.some((a) => a.id === img.id)) };
    case 'DUPLICATE':
      return { images: images.filter((img) => !action.images.some((a) => a.id === img.id)) };
    case 'ADD_COMMENT':
      return { comments: comments.filter((c) => c.id !== action.comment.id) };
    case 'DELETE_COMMENT':
      return { comments: [...comments, action.comment] };
    case 'APPLY_COMMENTS':
      return { comments: comments.map((c) => action.commentIds.includes(c.id) ? { ...c, isPending: true } : c) };
  }
}

function applyUndoActionForward(action: UndoAction, images: CanvasImage[], comments: CanvasComment[]): ApplyResult {
  switch (action.type) {
    case 'MOVE':
      return {
        images: images.map((img) => {
          const u = action.updates.find((u) => u.id === img.id);
          return u ? { ...img, x: u.toX, y: u.toY } : img;
        }),
      };
    case 'RESIZE':
      return {
        images: images.map((img) => {
          if (img.id !== action.id) return img;
          const updated = { ...img, displayWidth: action.toW, displayHeight: action.toH };
          if (action.toX !== undefined) updated.x = action.toX;
          if (action.toY !== undefined) updated.y = action.toY;
          return updated;
        }),
      };
    case 'DELETE':
      return { images: images.filter((img) => !action.images.some((a) => a.id === img.id)) };
    case 'ADD':
      return { images: [...images, ...action.images] };
    case 'DUPLICATE':
      return { images: [...images, ...action.images] };
    case 'ADD_COMMENT':
      return { comments: [...comments, action.comment] };
    case 'DELETE_COMMENT':
      return { comments: comments.filter((c) => c.id !== action.comment.id) };
    case 'APPLY_COMMENTS':
      return { comments: comments.map((c) => action.commentIds.includes(c.id) ? { ...c, isPending: false } : c) };
  }
}

interface StoreState {
  zoom: number;
  panX: number;
  panY: number;
  activeTool: CanvasTool;
  selectedImageIds: string[];
  marks: CanvasMark[];
  pendingMarkCount: number;
  comments: CanvasComment[];
  commentsByImageId: Record<string, CanvasComment[]>;
  pendingCommentCount: number;
  isFullscreen: boolean;
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  /** v7.44 — first-run tour. tourRequest is the start signal ('auto' = first
   *  sign-in, 'forced' = admin Dev Tools button, never stamps completion);
   *  tourActive is true while the overlay owns the screen (App.tsx hides the
   *  verify-email banner off it). */
  tourRequest: 'auto' | 'forced' | null;
  tourActive: boolean;
  chatWidth: number;
  contextMenu: ContextMenuState | null;
  commentPopover: CommentPopoverState | null;
  markPopover: MarkPopoverState | null;
  sidebarProjects: CanvasProject[];
  sidebarActiveProjectId: string;
  tasks: CanvasTask[];

  // Normalized per-project state. Conversation/canvas/view fields live in
  // state.projects[projectId] (read via store-selectors useActiveProject*).
  projects: Record<number, ProjectState>;
  activeProjectId: number | null;
  // Standalone view shown when no project is active — used by sidebar menu
  // items (Heartbeat, Brands, Analytics, Ads Manager, Ad Library) which
  // explicitly clear activeProjectId. After the Task 11 rewrite, activeView
  // moved into per-project state, which silently broke these menus because
  // setActiveView with a null projectId was a no-op. This top-level slot is
  // the destination for setActiveView when there's no project in scope.
  standaloneActiveView: CanvasView;
  /** Style tags to pre-apply when the Ad Library opens from a Discover section.
      Consumed + cleared by CanvasAdLibraryView on mount. null = no pre-filter. */
  adLibraryInitialStyles: string[] | null;
  /** When opening the Ad Library from a specific Discover card, the id of the
      ad to pin to the top of the (filtered) results. Consumed + cleared on
      mount alongside adLibraryInitialStyles. null = no pinned ad. */
  adLibraryFocusId: number | null;
  // When the chat detects a brand-onboarding intent ("here is my brand
  // <URL>") it asks the client to switch to the Brands view and pop the
  // analyzer modal with this URL pre-filled. canvas-brands-view reads this
  // and clears it once it has consumed the request.
  pendingBrandModalUrl: string | null;
  typingByProject: Record<string | number, boolean>;
  undoStack: UndoAction[];
  redoStack: UndoAction[];
  lightboxImageId: string | null;
  internalClipboard: CanvasImage | null;
  canvasSettings: CanvasSettings;
  activeBrandId: number | null;
  activeAdAccountId: number | null;
  taskSteps: TaskStep[];
  processingImageIds: Set<string>;
  attachedFile: { url: string; name: string } | null;
  // v3.86 — chips are either plain strings (legacy: click sends the text as a
  // chat message) or action objects: "chat" sends `value`, "view" switches the
  // canvas sidebar to that view (brands / ad-library / analytics …).
  followUpChips: Array<string | { label: string; action: "chat" | "view"; value: string }>;
  // Multi-select stage picker shown when angles-to-campaign needs stages from the user.
  // null when no picker is pending; payload contains per-stage angle counts.
  anglePickerStages: Record<string, number> | null;
  pendingVariantsConfirm: null | {
    correlationId: string;
    sourceCount: number;
    formats: string[];
    total: number;
    estimatedCredits: number;
  };
  /**
   * When set, the Canvas toolbar should open the Create Variations panel for
   * the given imageId and the panel should immediately fire generation with
   * the given count. Set by the Ad Library "Clone" action and the canvas
   * toolbar Clone button.
   *
   * `mode`:
   *   - 'clone'     → reproduce the source identically (composition, copy,
   *                   product all preserved). No creativity slider applies.
   *   - 'variation' → reinterpret using the panel's current creativity
   *                   setting (default — same as a manual Generate click).
   */
  pendingAutoVariations: null | { imageId: string; count: number; mode?: 'clone' | 'variation'; productId?: number; productName?: string };
  sseConnected: boolean;
  sseError: boolean;
  isSavingProject: boolean;
  folderProjectId: number | null;
  openFolderIds: number[];
  activeTaskId: number | null;
  previousView: CanvasView | null;
  taskCanvasState: Record<number, { isCanvasOpen: boolean; activeView: 'chat-only' | 'chat-canvas' }>;
  highlightedImageId: string | null;
  isApplying: boolean;
  applyProgress: { done: number; total: number };
  /**
   * When true, the canvas toolbar's Comment tool is active. A normal click
   * on any canvas image opens the comment popover at the image's center
   * (instead of the default select-only behavior). The user can then type
   * an edit comment ("make the headline blue", "swap the apple for an
   * orange"), save, and repeat across multiple images. Once at least one
   * comment exists, the CanvasApplyBar surfaces an Apply button that fires
   * the existing inpaint pipeline for every commented image in parallel.
   */
  commentMode: boolean;
  researchTasksByConversation: Record<string | number, ActiveResearchTask & { dismissed: boolean }>;
  taskPanelsByProject?: Record<number, TaskPanels>;
  showDocumentOverlay: boolean;
  documentOverlayContent: string | null;
  documentOverlayTitle: string | null;
  chatAttachments: Array<{ url: string; name: string }>;
  brushMode: boolean;
  adMap: AdMapRecord | null;
  adMapId: number | null;
  adMapStatus: AdMapRecord['status'] | null;
  isGeneratingAdMap: boolean;
  brandSelectionNeeded: {
    target: string;
    options: Array<{ type: 'new_brand' | 'competitor'; brandId?: number; label: string }>;
    projectId?: number;
    assistantMsgId: string;
  } | null;
  adDataCacheByProject: Record<string | number, Record<string, AdViewData>>;
  showSidebarPanel: boolean;
  sidebarPanelContent: import('./SidebarPanel').SidebarPanelContent | null;
  lastBriefContent: { title: string; content: string } | null;
  selectedAspectRatio: string;
  /** v4.62 — multi-ratio runs: every selected ratio; selectedAspectRatio is always the first entry. */
  selectedAspectRatios: string[];
  /** v6.22 — composer Image/Video tab (new-task screen). The GLOBAL value is
   * only the new-task screen's pending choice; once a task exists the medium
   * comes from generationMediumByProject. 'video' hard-disables image
   * playbooks server-side. */
  generationMedium: 'image' | 'video';
  /** v6.22 — the medium each task was created with (per Dan: the tab
   * hard-codes what THIS task runs). Tasks without an entry are image —
   * every pre-existing task keeps exact image behavior. In-memory v1: a
   * reload falls back to image until the medium is persisted server-side. */
  generationMediumByProject: Record<number, 'image' | 'video'>;
  /** v7.16 — composer image-model dropdown (NanoBanana 2 | GPT Image 2). The
   * GLOBAL value is the new-task screen's pending choice; once a task exists
   * the model comes from imageModelByProject (falling back to the global so
   * pre-existing tasks can still be switched mid-chat). Applies to image
   * playbooks AND the video pipeline's image steps (frames/casting cards). */
  inspire: boolean;
  inspireByProject: Record<number, boolean>;
  inspireSaveErrors: Record<number,string>;
  setInspire: (enabled:boolean,projectId?:number|null)=>void;
  imageModel: 'nano-banana-2' | 'gpt-image-2' | 'gpt-image-2.5-sunburst';
  imageModelByProject: Record<number, 'nano-banana-2' | 'gpt-image-2' | 'gpt-image-2.5-sunburst'>;
  /** v7.25 — angle sets whose build is currently running (setId → epoch-ms
   * expiry). The Angles doc panel disables its Build button while set, so an
   * auto-built set can't collect a duplicate click mid-run. */
  angleSetBuildingUntil: Record<number, number>;
  /** v7.20 — the model's second dial (Dan's spec): gpt = quality
   *  (low/medium/high), nano = resolution (1k/2k/4k). null = family default
   *  (gpt high, nano 1k). Reset whenever the model family changes. */
  imageResolution: string;
  imageResolutionByProject: Record<number, string>;
  setImageResolution: (resolution: string, projectId?: number | null) => void;
  imageTier: string | null;
  imageTierByProject: Record<number, string | null>;
  /** v7.37 — composer "Advanced" angle/style filter (image tab). null axis =
   *  All (today's behavior). Per-task stamp wins for display; picking writes
   *  both, same convention as imageTier. */
  adFilter: { styles: string[] | null; angles: string[] | null };
  adFilterByProject: Record<number, { styles: string[] | null; angles: string[] | null }>;
  /** v7.35 — VIDEO composer dials (Dan's spec): model (Seedance 2.5 for now),
   *  Standard/Turbo, resolution, duration notch, audio, and a video-specific
   *  aspect ratio (video's API set has 21:9 and no 4:5). GLOBAL values are the
   *  new-task screen's pending choice; per-task values come from
   *  videoDialsByProject once stamped at creation. Server re-validates
   *  everything (turbo caps at 1080p etc.). */
  videoModel: 'seedance-2.5' | 'minimax-h3';
  videoVariant: 'standard' | 'turbo';
  videoResolution: string;
  videoDuration: number;
  videoAudio: boolean;
  videoAspectRatio: string;
  videoDialsByProject: Record<number, { model: 'seedance-2.5' | 'minimax-h3'; variant: 'standard' | 'turbo'; resolution: string; duration: number; audio: boolean; aspectRatio: string }>;
  /** v7.35 — composer "Video Ref" attachments for the NEXT message (cleared on
   *  send): Bunny URLs + probed durations, ride the agent POST as
   *  referenceVideos → Seedance reference_videos. */
  videoRefs: Array<{ url: string; durationSec?: number; name?: string }>;
  showCopySidebar: boolean;
  /** v4.16 — Facebook-native Preview slide-out (toolbar Eye button). */
  showPreviewSidebar: boolean;
  adCopyGeneratingImageIds: Set<string>;
}

export interface AdCopyVariation {
  id: number;
  imageClientId: string;
  variationNumber: number;
  headline: string | null;
  primaryText: string | null;
  hook: string | null;
  cta: string | null;
  angle: string | null;
  status: string;
  projectId: number;
  userId: number;
  createdAt?: string;
  updatedAt?: string;
}

interface StoreActions {
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;
  setActiveTool: (tool: CanvasTool) => void;
  setSelectedImageIds: (ids: string[]) => void;
  updateImagePositions: (updates: { id: string; x: number; y: number }[]) => void;
  commitMove: (changes: { id: string; fromX: number; fromY: number; toX: number; toY: number }[]) => void;
  commitResize: (id: string, fromW: number, fromH: number, toW: number, toH: number, fromX?: number, fromY?: number, toX?: number, toY?: number) => void;
  toggleFullscreen: () => void;
  toggleSidebar: () => void;
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTourRequest: (r: 'auto' | 'forced' | null) => void;
  setTourActive: (active: boolean) => void;
  setChatWidth: (w: number) => void;
  setContextMenu: (m: ContextMenuState | null) => void;
  setCommentPopover: (p: CommentPopoverState | null) => void;
  setMarkPopover: (p: MarkPopoverState | null) => void;
  addComment: (imageId: string, pinX: number, pinY: number, text: string) => void;
  removeComment: (imageId: string, commentId: string) => void;
  clearPendingComments: () => void;
  getPendingEdits: () => PendingEdit[];
  setIsApplying: (isApplying: boolean) => void;
  setApplyProgress: (progress: { done: number; total: number }) => void;
  setCommentMode: (on: boolean) => void;
  discardPendingComments: () => void;
  applyPendingComments: (ids?: string[]) => void;
  addMark: (data: { imageId: string; regionX: number; regionY: number; regionWidth: number; regionHeight: number; instruction: string }) => void;
  discardPendingMarks: () => void;
  applyPendingMarks: (ids?: string[]) => void;
  deleteMark: (markId: string) => void;
  setActiveProject: (id: string) => void;
  /**
   * Sets the normalized active project ID — drives state.projects[id]
   * resolution. Synchronous pointer change; no side effects.
   * Pass null to clear (e.g. on logout or new-project pre-creation).
   */
  setActiveProjectId: (id: number | null) => void;
  setActiveTask: (id: number | null) => void;
  setFolderProjectId: (id: number | null) => void;
  toggleOpenFolder: (id: number) => void;
  addOpenFolder: (id: number) => void;
  setActiveView: (view: CanvasView) => void;
  // v3.91 — one-shot deep-link target for the Brands view's inner tab. Set by
  // the welcome-screen "Ad Angles" shortcut before setActiveView('brands');
  // BrandDetailView consumes+clears it so the next brand opens on that tab.
  pendingBrandsTab: 'overview' | 'angles' | 'competitors' | 'intelligence' | 'identity' | 'products' | null;
  setPendingBrandsTab: (tab: 'overview' | 'angles' | 'competitors' | 'intelligence' | 'identity' | 'products' | null) => void;
  /** v6.13 — preflight element connectors: react-flow edges (frame tile →
   *  element tiles), per project. In-memory only; the runner rebuilds them
   *  from draft.frameElements on load. */
  /** Open the standalone Ad Library view, optionally pre-filtered to these
      style tags (empty array = unfiltered / all templates). */
  openAdLibrary: (styles: string[], focusId?: number) => void;
  setCanvasOpen: (open: boolean) => void;
  sendMessage: (content: string, attachedFileUrl?: string) => void;
  pushUndoAction: (action: UndoAction) => void;
  undo: () => void;
  redo: () => void;
  deleteImages: (ids: string[]) => void;
  deleteImage: (id: string) => void;
  duplicateImage: (id: string) => void;
  openLightbox: (id: string) => void;
  closeLightbox: () => void;
  copyToClipboard: (id: string) => void;
  pasteFromClipboard: () => void;
  updateCanvasSettings: (updates: Partial<CanvasSettings>) => void;
  setActiveBrandId: (id: number | null) => void;
  setActiveAdAccountId: (id: number | null) => void;
  setChatAttachments: (attachments: Array<{ url: string; name: string }>) => void;
  addChatAttachment: (attachment: { url: string; name: string }) => void;
  removeChatAttachment: (url: string) => void;
  setBrushMode: (on: boolean) => void;
  setTaskSteps: (steps: TaskStep[]) => void;
  updateTaskStep: (label: string, status: TaskStep['status']) => void;
  addGenerationRow: (row: CanvasGenerationRow) => void;
  updateGenerationRow: (id: string, updates: Partial<CanvasGenerationRow>) => void;
  panToImage: (imageId: string) => void;
  // projectIdOverride routes the write to the originating chat instead of
  // get().activeProjectId — required from any async/SSE path where the user
  // may have navigated away. Without it, the placeholder gets created in
  // the destination chat and the originating chat keeps showing the old
  // unresolved one, producing a "5 empty boxes + N actual ads" stack.
  addCanvasPlaceholder: (taskId: string, x: number, y: number, width: number, height: number, prompt?: string, genWidth?: number, genHeight?: number, filename?: string, projectIdOverride?: number) => void;
  resolveCanvasPlaceholder: (taskId: string, projectIdOverride?: number) => void;
  failCanvasPlaceholder: (taskId: string, errorMsg: string, projectIdOverride?: number) => void;
  /** v6.53 — caption preset picker target (video tile). Rendered at
   *  workspace level (React Flow nodes are transformed — fixed positioning
   *  inside them breaks). */
  captionPicker: { imageId: string; draftId: number; videoUrl: string } | null;
  setCaptionPicker: (v: { imageId: string; draftId: number; videoUrl: string } | null) => void;
  /** v6.54 — imageId → preset label while a caption burn runs on that tile
   *  (in-place overlay; the tile itself shows "Generating captions"). */
  captionBurning: Record<string, string | undefined>;
  setCaptionBurning: (imageId: string, label: string | null) => void;
  retryPlaceholder: (taskId: string) => void;
  setImageProcessing: (id: string, processing: boolean) => void;
  updateImage: (id: string, updates: Partial<CanvasImage>) => void;
  toggleFavorite: (id: string) => void;
  editComment: (commentId: string, newText: string) => void;
  markCommentsFailed: (imageId: string) => void;
  markCommentsFailedByIds: (commentIds: string[]) => void;
  resetFailedComments: (imageId: string) => void;
  addEditedImage: (img: CanvasImage) => void;
  regenerateImage: (imageId: string) => void;
  addEditedChatMessage: (msg: CanvasChatMessage) => void;
  addNotificationMessage: (content: string) => void;
  // projectIdOverride lets SSE event handlers route image writes to the
  // ORIGINATING project even after the user has navigated away.
  // get().activeProjectId is unsafe inside async event streams.
  addImageToCanvas: (src: string, width: number, height: number, filename: string, originalWidth?: number, originalHeight?: number, prompt?: string, fixedX?: number, fixedY?: number, adMeta?: { adStyle?: string; adAngle?: string; wavespeedPrompt?: string; conceptName?: string; headline?: string; bodyCopy?: string; ctaText?: string; workflowRunId?: number; videoUrl?: string }, projectIdOverride?: number) => Promise<void>;
  addImageNextTo: (src: string, width: number, height: number, filename: string, sourceImageId: string, originalWidth?: number, originalHeight?: number, prompt?: string, projectIdOverride?: number, placement?: 'right' | 'below', adMeta?: { adStyle?: string; adAngle?: string; wavespeedPrompt?: string; conceptName?: string; headline?: string; bodyCopy?: string; ctaText?: string }) => void;
  setAttachedFile: (file: { url: string; name: string } | null) => void;
  loadOrCreateProject: () => Promise<void>;
  // projectId is REQUIRED. Auto-saves happen async/debounced and the user
  // may have switched projects by the time they fire — capturing projectId
  // at trigger time is the only way to save the right chat's messages to
  // the right DB row. Pass null/undefined active means "skip the save".
  reconcileImagesFromServer: (projectId: number | null) => Promise<void>;
  /** v4.50 — while the project's newest run is still running (e.g. after a
   *  reload severed the SSE mid-run), poll the DB images until it completes. */
  watchRunningRun: (projectId: number) => Promise<void>;
  /** v7.43 — a hydrated conversation entry says a concepts run is still
   *  `generating` (the tab was closed / server restarted mid-run). Poll the
   *  angle set + the task's conversation until the server resolves the entry,
   *  or synthesize the result client-side when the set finished but the
   *  server died before writing it. One loop per message id; 12-min cap. */
  watchAngleSetRun: (projectId: number, msgId: string, setId: number | null) => Promise<void>;
  /** Re-grid images + active placeholders into a clean grid IF anything
   *  overlaps. No-op (returns false) when already clean. Returns true if it
   *  moved anything. The live-generation source of truth for layout. */
  relayoutCanvasContent: (projectId: number | null) => boolean;
  /** Debounced wrapper around relayoutCanvasContent — call on every image/
   *  placeholder SSE event so the canvas self-corrects during generation. */
  scheduleRelayout: (projectId: number | null) => void;
  /** Force-pack ALL real images into a contiguous, gap-free 5-col grid (unlike
   *  relayoutCanvasContent, which only acts on overlaps). Run once at batch
   *  completion to remove holes left by dropped tiles / cleared loading boxes.
   *  Returns true if it moved anything. */
  compactImagesToGrid: (projectId: number | null) => boolean;
  /** v3.23 — pack ONE active generation batch (its finished images first, then
   *  its pending placeholders) into a contiguous gap-free grid anchored at the
   *  batch's top-left. Scoped to the batch, so it never moves other batches (no
   *  jump). Run on every image event so mid-run holes never persist. */
  packActiveBatch: (projectId: number | null, batchId: string | null) => void;
  saveImagesSnapshot: (projectId: number) => Promise<void>;
  saveConversationHistory: (projectId: number) => Promise<void>;
  switchProject: (projectId: number, meta?: { brandId?: number | null; adAccountId?: number | null; parentId?: number | null }) => Promise<void>;
  /** Forces a refetch of images + conversation for a project, bypassing the
   * loadedAt cache. Use when the user explicitly wants to pull server-side
   * changes (the "reload" canvas button). */
  reloadProject: (projectId: number) => Promise<void>;
  /** Non-destructive tile refresh: re-fetches ONLY the image rows and swaps
   * them in atomically — no loading flag, no loadedAt reset, no conversation
   * reload. Use for live-build polling; reloadProject blanks the whole
   * project while it refetches (visible for seconds on slow links). */
  refreshProjectImages: (projectId: number) => Promise<void>;
  createNewProject: (name: string, color?: string, parentId?: number | null) => Promise<void>;
  setActiveResearchTask: (conversationKey: string | number, task: (ActiveResearchTask & { dismissed: boolean }) | null) => void;
  dismissResearchTask: (conversationKey: string | number) => void;
  updateResearchTaskStep: (stepId: string, updates: Partial<ResearchTaskStep>, conversationKey?: string | number) => void;
  openDocumentOverlay: (title: string, content: string, ownerProjectId?: number | null) => void;
  closeDocumentOverlay: () => void;
  sendResearchMessage: (content: string) => void;
  setAdMap: (adMap: AdMapRecord | null) => void;
  updateAdMapData: (updates: Partial<AdMapRecord>) => void;
  clearAdMap: () => void;
  approveAdMap: () => Promise<void>;
  loadAdMap: () => Promise<void>;
  sendAdMapMessage: (content: string) => void;
  refineAdMapMessage: (content: string) => void;
  clearBrandSelection: () => void;
  openSidebarPanel: (content: import('./SidebarPanel').SidebarPanelContent, ownerProjectId?: number | null) => void;
  closeSidebarPanel: () => void;
  sendRoutedResearchMessage: (params: { target: string; type: 'new_brand' | 'competitor'; brandId?: number; projectId: number }) => Promise<void>;
  sendAgentMessage: (content: string, preMsgId?: string, extraContext?: { sourceSuggestion?: {messageId:string;value:string}; creativeMode?: "brand-led" | "inspire"; displayContent?: string; followUpContext?: CanvasFollowUpOrigin; productId?: number; composerReferences?: ComposerReferenceSnapshot; targetFormats?: string[]; silent?: boolean; forcedTool?: { name: string; arguments: Record<string, unknown> } }) => Promise<void>;
  dismissVariantsConfirm: () => void;
  confirmVariantsAndSend: () => void;
  setSelectedAspectRatio: (ratio: string) => void;
  toggleSelectedAspectRatio: (ratio: string) => void;
  setGenerationMedium: (medium: 'image' | 'video') => void;
  setProjectGenerationMedium: (projectId: number, medium: 'image' | 'video') => void;
  setImageModel: (model: 'nano-banana-2' | 'gpt-image-2' | 'gpt-image-2.5-sunburst') => void;
  setProjectImageModel: (projectId: number, model: 'nano-banana-2' | 'gpt-image-2' | 'gpt-image-2.5-sunburst') => void;
  setImageTier: (tier: string | null) => void;
  setVideoModel: (m: 'seedance-2.5' | 'minimax-h3') => void;
  setVideoVariant: (v: 'standard' | 'turbo') => void;
  setVideoResolution: (r: string) => void;
  setVideoDuration: (d: number) => void;
  setVideoAudio: (a: boolean) => void;
  setVideoAspectRatio: (r: string) => void;
  setProjectVideoDials: (projectId: number, dials: { model: 'seedance-2.5' | 'minimax-h3'; variant: 'standard' | 'turbo'; resolution: string; duration: number; audio: boolean; aspectRatio: string }) => void;
  addVideoRef: (ref: { url: string; durationSec?: number; name?: string }) => void;
  removeVideoRef: (url: string) => void;
  clearVideoRefs: () => void;
  setProjectImageTier: (projectId: number, tier: string | null) => void;
  setAdFilter: (f: { styles: string[] | null; angles: string[] | null }) => void;
  setProjectAdFilter: (projectId: number, f: { styles: string[] | null; angles: string[] | null }) => void;
  clearPendingPanTarget: () => void;
  clearPendingFitToView: () => void;
  clearPendingFitToRow: () => void;
  setCanvasAdCopyVariations: (imageClientId: string, variations: AdCopyVariation[]) => void;
  appendCanvasAdCopyVariation: (imageClientId: string, variation: AdCopyVariation) => void;
  setCanvasAdCopyActiveIndex: (imageClientId: string, index: number) => void;
  clearCanvasAdCopy: () => void;
  updateCanvasAdCopyVariation: (imageClientId: string, variationId: number, updates: Partial<AdCopyVariation>) => void;
  setShowCopySidebar: (show: boolean) => void;
  setShowPreviewSidebar: (show: boolean) => void;
  setAdCopyGenerating: (imageClientIds: string[], generating: boolean) => void;

  clearStaleTypingForProject: (projectKey: string | number) => void;
  _ensureProject: (projectId: number) => void;
  /**
   * Apply a pure updater to the state of a single project. The updater MUST
   * return a NEW ProjectState object (do not mutate `p` in place — Zustand
   * relies on reference equality for change detection).
   */
  _updateProject: (projectId: number, updater: (p: ProjectState) => ProjectState) => void;
  /** Dual-write: updates flat `messages` AND `projects[projectId].messages` when projectId is non-null. */
  _writeMessages: (projectId: number | null, updater: (msgs: CanvasChatMessage[]) => CanvasChatMessage[]) => void;
  /** Dual-write: updates flat `conversationHistory` AND `projects[projectId].conversationHistory` when projectId is non-null. */
  _writeConversationHistory: (projectId: number | null, history: Array<{ role: 'user' | 'assistant'; content: string }>) => void;
  /** Dual-write: updates `projects[projectId].agentThinking` when projectId is non-null. Flat field has no equivalent; message-level field carries the data for old consumers. */
  _writeAgentThinking: (projectId: number | null, text: string | null) => void;
  /** Dual-write: updates flat `images` + `hasPersistedImages` AND `projects[projectId].images` when projectId is non-null. */
  _writeImages: (projectId: number | null, updater: (imgs: CanvasImage[]) => CanvasImage[]) => void;
  /** Dual-write: updates flat `imageQaBadges` AND `projects[projectId].imageQaBadges` when projectId is non-null. */
  _writeQaBadges: (projectId: number | null, updater: (badges: Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }>) => Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }>) => void;
  /** Dual-write: updates flat `imageRequestIdToUrl` AND `projects[projectId].imageRequestIdToUrl` when projectId is non-null. */
  _writeImageRequestIdToUrl: (projectId: number | null, updater: (map: Record<string, string>) => Record<string, string>) => void;
  /** Dual-write: updates flat `canvasAdCopy` AND `projects[projectId].canvasAdCopy` when projectId is non-null. */
  _writeCanvasAdCopy: (projectId: number | null, updater: (m: Record<string, { variations: AdCopyVariation[]; activeVariationIndex: number }>) => Record<string, { variations: AdCopyVariation[]; activeVariationIndex: number }>) => void;
  /** Dual-write: updates flat `canvasPlaceholders` AND `projects[projectId].canvasPlaceholders` when projectId is non-null. */
  _writeCanvasPlaceholders: (projectId: number | null, updater: (m: Record<string, CanvasPlaceholder>) => Record<string, CanvasPlaceholder>) => void;
  /** Dual-write: updates flat `generationRows` AND `projects[projectId].generationRows` when projectId is non-null. */
  _writeGenerationRows: (projectId: number | null, updater: (rows: CanvasGenerationRow[]) => CanvasGenerationRow[]) => void;
  _writePlaybookProgress: (projectId: number | null, progress: PlaybookProgressState | null) => void;
  _writeWorkflowState: (projectId: number | null, wfState: WorkflowState | null) => void;
  _writeActiveWorkflowRunId: (projectId: number | null, runId: number | null) => void;
  _writeProjectView: (projectId: number | null, patch: Partial<Pick<ProjectState, 'isCanvasOpen' | 'activeView' | 'pendingPanTarget' | 'pendingFitToView' | 'pendingFitToRow'>>) => void;
  _writeProjectMetadata: (projectId: number, patch: Partial<Pick<ProjectState, 'brandId' | 'adAccountId' | 'parentId' | 'loadedAt' | 'loading'>>) => void;
}

export type CanvasStore = StoreState & StoreActions;

// Module-level dedup set: tracks requestIds already claimed by one resolution
// path (either the canvas_image_pending polling loop OR the canvas_image_ready
// SSE handler). Because both run at the same 3-second polling cadence they can
// both detect WaveSpeed completion in the same event-loop turn.  The first path
// to insert its requestId wins; the second path bails without calling
// addImageToCanvas, preventing duplicate DB rows and canvas images.
const handledImageRequests = new Set<string>();
// Batch-grouping for the completion compaction (session-only, by image src).
// When two "make N ads" runs overlap (user fires them back-to-back), their
// tiles interleave by position. These maps let compactImagesToGrid lay each
// batch out as its OWN ordered block (e.g. the 20, then the 10 Santa) instead
// of packing everything by raw y/x. _batchFirstSeen preserves run order.
const _imageBatchId = new Map<string, string>();      // image src -> batchId
const _batchFirstSeen = new Map<string, number>();    // batchId -> order seen
let _batchSeenCounter = 0;
// v4.64 — image src -> its fixed batch slot (server conceptIndex), recorded at
// canvas_image_ready from the resolved placeholder. Lets packActiveBatch keep
// every tile in ITS slot instead of reshuffling by arrival order.
const _slotBySrc = new Map<string, number>();
/** v4.65 — the stage-frame builder claims a run's un-filled loading boxes by
 *  BATCH IDENTITY (mixed-ratio grids have 100-171px visual gaps, so no fixed
 *  proximity margin can separate own-batch boxes from a neighboring run's). */
export const getImageBatchId = (src: string): string | undefined => _imageBatchId.get(src);
// Shared debounced safety-net timer — resets each time a new image arrives.
// Fires once, 1500 ms after the LAST canvas_image_ready in a batch.
// Prevents N×2 redundant DB fetches + fitView calls when N images complete simultaneously.
let _safetyNetTimer: ReturnType<typeof setTimeout> | null = null;
let _safetyNetProjectId: number | null = null;

// Shared debounce for live de-stacking DURING generation. Every image/
// placeholder SSE event schedules this; it fires ~180ms after the last one and
// re-grids the canvas if anything overlaps. Short enough that the user never
// stares at a stacked mess; debounced so a burst of simultaneous completions
// triggers exactly one relayout.
let _relayoutTimer: ReturnType<typeof setTimeout> | null = null;
let _relayoutProjectId: number | null = null;

// Set of user-message IDs that were pre-inserted during queuing.
// sendAgentMessage checks this to skip re-inserting duplicate user turns.
const _preInsertedMsgIds = new Set<string>();
// Per-project active request tracker: maps projectKey → AbortController for any
// in-flight sendAgentMessage call (both playbook and normal chat turns).
// Set when typing starts, deleted in all terminal paths (normal, abort, error).
const _activeRequestByProject = new Map<string | number, AbortController>();

// edit-fixes: ids dispatched per imageClientId for the in-flight Apply run —
// edit_fix_result must resolve EXACTLY what was sent (retry sends only failed
// comments; fresh applies exclude failed ones), never everything pending.
const _editFixDispatched = new Map<string, { commentIds: string[]; markIds: string[] }>();
export function registerEditFixDispatch(items: Array<{ imageClientId: string; commentIds: string[]; markIds: string[] }>): void {
  for (const it of items) _editFixDispatched.set(it.imageClientId, { commentIds: it.commentIds, markIds: it.markIds });
}

// Returns true if a message text is a stop/cancel intent.
function isStopIntent(text: string): boolean {
  return /\b(stop|cancel|halt|abort|nevermind|never mind|pause it|stop generating|cancel that)\b/i.test(text.trim());
}


export const useCanvasStore = create<CanvasStore>()(withTaskPanels((set, get) => ({
  zoom: 1,
  panX: 0,
  panY: 0,
  activeTool: 'select',
  selectedImageIds: [],
  marks: [],
  pendingMarkCount: 0,
  comments: [],
  commentsByImageId: {},
  pendingCommentCount: 0,
  isFullscreen: false,
  isSidebarOpen: true,
  isSidebarCollapsed: (() => {
    try { return localStorage.getItem('canvas-sidebar-collapsed') === '1'; } catch { return false; }
  })(),
  tourRequest: null,
  tourActive: false,
  chatWidth: 450,
  contextMenu: null,
  commentPopover: null,
  markPopover: null,
  sidebarProjects: SEED_PROJECTS,
  sidebarActiveProjectId: 'p1',
  projects: {},
  activeProjectId: null,
  standaloneActiveView: 'chat-only',
  adLibraryInitialStyles: null,
  adLibraryFocusId: null,
  pendingBrandModalUrl: null,
  tasks: SEED_TASKS,
  typingByProject: {},
  undoStack: [],
  redoStack: [],
  lightboxImageId: null,
  internalClipboard: null,
  canvasSettings: { showGrid: true, snapToGrid: false, showGuides: true },
  activeBrandId: null,
  activeAdAccountId: null,
  taskSteps: [],
  processingImageIds: new Set(),
  attachedFile: null,
  // v3.86 — no default chips: they were another context-blind static list
  // ('Remove backgrounds' didn't route anywhere). Chips now only appear when
  // the agent emits context-aware ones after a response.
  followUpChips: [],
  anglePickerStages: null,
  pendingVariantsConfirm: null,
  pendingAutoVariations: null,
  sseConnected: true,
  sseError: false,
  isSavingProject: false,
  folderProjectId: null,
  openFolderIds: [],
  activeTaskId: null,
  previousView: null,
  pendingBrandsTab: null,
  taskCanvasState: {},
  highlightedImageId: null,
  captionPicker: null,
  captionBurning: {},
  isApplying: false,
  applyProgress: { done: 0, total: 0 },
  commentMode: false,
  researchTasksByConversation: {},
  showDocumentOverlay: false,
  documentOverlayContent: null,
  documentOverlayTitle: null,
  chatAttachments: [],
  brushMode: false,
  adMap: null,
  adMapId: null,
  adMapStatus: null,
  isGeneratingAdMap: false,
  brandSelectionNeeded: null,
  adDataCacheByProject: {},
  showSidebarPanel: false,
  sidebarPanelContent: null,
  lastBriefContent: null,
  selectedAspectRatio: '1:1',
  selectedAspectRatios: ['1:1'],
  generationMedium: 'image',
  generationMediumByProject: {},
  // v7.30 — Dan's default: New Task loads with GPT Image 2 at Medium quality
  // (was NanoBanana 2; user switches freely, per-task stamps unchanged).
  inspire: false,
  inspireByProject: {},
  inspireSaveErrors: {},
  setInspire: (enabled,projectId)=>{
    set(s=>projectId != null ? {inspireByProject:{...s.inspireByProject,[projectId]:enabled},inspireSaveErrors:{...s.inspireSaveErrors,[projectId]:''}} : {inspire:enabled});
    if(projectId!=null)void persistTaskInspire(projectId,enabled).then(()=>set(s=>({inspireSaveErrors:{...s.inspireSaveErrors,[projectId]:''}}))).catch(error=>set(s=>({inspireSaveErrors:{...s.inspireSaveErrors,[projectId]:error.message}})));
  },
  imageModel: 'gpt-image-2.5-sunburst',
  imageModelByProject: {},
  angleSetBuildingUntil: {},
  imageResolution: '1k',
  imageResolutionByProject: {},
  setImageResolution: (resolution, projectId) => set(s => ({imageResolution:resolution,...(projectId != null ? {imageResolutionByProject:{...s.imageResolutionByProject,[projectId]:resolution}} : {})})),
  imageTier: 'medium',
  imageTierByProject: {},
  adFilter: { styles: null, angles: null },
  adFilterByProject: {},
  videoModel: 'seedance-2.5',
  videoVariant: 'standard',
  videoResolution: '720p',
  videoDuration: 5,
  videoAudio: true,
  videoAspectRatio: '9:16',
  videoDialsByProject: {},
  videoRefs: [],
  showCopySidebar: false,
  showPreviewSidebar: false,
  adCopyGeneratingImageIds: new Set<string>(),

  setAdCopyGenerating: (imageClientIds, generating) => set((s) => {
    const next = new Set(s.adCopyGeneratingImageIds);
    for (const id of imageClientIds) {
      if (generating) next.add(id);
      else next.delete(id);
    }
    return { adCopyGeneratingImageIds: next };
  }),

  setCanvasAdCopyVariations: (imageClientId, variations) => {
    const projectKey = get().activeProjectId;
    get()._writeCanvasAdCopy(projectKey, (m) => ({
      ...m,
      [imageClientId]: { variations, activeVariationIndex: 0 },
    }));
  },
  appendCanvasAdCopyVariation: (imageClientId, variation) => {
    const projectKey = get().activeProjectId;
    get()._writeCanvasAdCopy(projectKey, (m) => {
      const existing = m[imageClientId] || { variations: [], activeVariationIndex: 0 };
      return {
        ...m,
        [imageClientId]: {
          ...existing,
          variations: [...existing.variations, variation],
        },
      };
    });
  },
  setCanvasAdCopyActiveIndex: (imageClientId, index) => {
    const projectKey = get().activeProjectId;
    get()._writeCanvasAdCopy(projectKey, (m) => {
      const existing = m[imageClientId];
      if (!existing) return m;
      return {
        ...m,
        [imageClientId]: { ...existing, activeVariationIndex: index },
      };
    });
  },
  clearCanvasAdCopy: () => {
    const projectKey = get().activeProjectId;
    get()._writeCanvasAdCopy(projectKey, () => ({}));
  },
  updateCanvasAdCopyVariation: (imageClientId, variationId, updates) => {
    const projectKey = get().activeProjectId;
    get()._writeCanvasAdCopy(projectKey, (m) => {
      const existing = m[imageClientId];
      if (!existing) return m;
      return {
        ...m,
        [imageClientId]: {
          ...existing,
          variations: existing.variations.map((v) =>
            v.id === variationId ? { ...v, ...updates } : v
          ),
        },
      };
    });
  },
  setShowCopySidebar: (show) => set({ showCopySidebar: show }),
  setShowPreviewSidebar: (show) => set({ showPreviewSidebar: show }),

  setSelectedAspectRatio: (ratio: string) => set({ selectedAspectRatio: ratio, selectedAspectRatios: [ratio] }),
  setGenerationMedium: (medium: 'image' | 'video') => set({ generationMedium: medium }),
  setProjectGenerationMedium: (projectId: number, medium: 'image' | 'video') =>
    set((s) => ({ generationMediumByProject: { ...s.generationMediumByProject, [projectId]: medium } })),
  // Switching families resets the tier dial — a gpt quality is meaningless on
  // nano and vice versa (the server would ignore it; the UI must not show it).
  // v7.30 — family-aware tier reset: switching (back) to GPT lands on Medium
  // (Dan's default), nano keeps null (= its 1K default). Without this, a
  // nano→gpt round-trip silently jumped to High — 4x Medium's real cost.
  setImageModel: (model: 'nano-banana-2' | 'gpt-image-2' | 'gpt-image-2.5-sunburst') => set({ imageModel: model, imageTier: model === 'nano-banana-2' ? null : 'medium' }),
  setImageTier: (tier: string | null) => set({ imageTier: tier }),
  // v7.35 — video dials. Switching to Turbo clamps resolution to its 720p/
  // 1080p range (4k/480p are standard-only) so the UI never shows a value the
  // server would silently rewrite.
  // v7.36 — switching video family resets the dial set to that family's
  // defaults (Seedance 720p / MiniMax 480p; ratio kept when the new family
  // supports it — 9:21 is MiniMax-only). Same law as setImageModel's
  // family-aware tier reset.
  setVideoModel: (m: 'seedance-2.5' | 'minimax-h3') => set((s) => ({
    videoModel: m,
    videoVariant: 'standard',
    videoResolution: m === 'minimax-h3' ? '480p' : '720p',
    videoDuration: 5,
    videoAudio: true,
    videoAspectRatio: m === 'seedance-2.5' && s.videoAspectRatio === '9:21' ? '9:16' : s.videoAspectRatio,
  })),
  setVideoVariant: (v: 'standard' | 'turbo') => set((s) => ({
    videoVariant: v,
    videoResolution: v === 'turbo' && !['720p', '1080p'].includes(s.videoResolution) ? '720p' : s.videoResolution,
  })),
  setVideoResolution: (r: string) => set({ videoResolution: r }),
  setVideoDuration: (d: number) => set({ videoDuration: d }),
  setVideoAudio: (a: boolean) => set({ videoAudio: a }),
  setVideoAspectRatio: (r: string) => set({ videoAspectRatio: r }),
  setProjectVideoDials: (projectId, dials) =>
    set((s) => ({ videoDialsByProject: { ...s.videoDialsByProject, [projectId]: dials } })),
  addVideoRef: (ref) => set((s) => ({
    videoRefs: s.videoRefs.some((r) => r.url === ref.url) ? s.videoRefs : [...s.videoRefs, ref].slice(0, 10),
  })),
  removeVideoRef: (url) => set((s) => ({ videoRefs: s.videoRefs.filter((r) => r.url !== url) })),
  clearVideoRefs: () => set({ videoRefs: [] }),
  setProjectImageTier: (projectId: number, tier: string | null) =>
    set((s) => ({ imageTierByProject: { ...s.imageTierByProject, [projectId]: tier } })),
  setAdFilter: (f) => set({ adFilter: f }),
  setProjectAdFilter: (projectId, f) =>
    set((s) => ({ adFilterByProject: { ...s.adFilterByProject, [projectId]: f } })),
  setProjectImageModel: (projectId: number, model: 'nano-banana-2' | 'gpt-image-2' | 'gpt-image-2.5-sunburst') =>
    set((s) => ({ imageModelByProject: { ...s.imageModelByProject, [projectId]: model } })),
  // v4.62 — multi-ratio picker. Toggles membership, keeps at least one ratio
  // selected, and keeps the list in the canonical picker order so the primary
  // (selectedAspectRatio = first entry) is stable regardless of click order.
  toggleSelectedAspectRatio: (ratio: string) => set((state) => {
    const selectedModel = state.activeProjectId != null ? state.imageModelByProject[state.activeProjectId] ?? state.imageModel : state.imageModel;
    const RATIO_ORDER: readonly string[] = selectedModel === 'gpt-image-2.5-sunburst' || selectedModel === 'gpt-image-2' ? SUNBURST_RATIOS : ['1:1', '9:16', '16:9', '4:5', '3:4'];
    if (!RATIO_ORDER.includes(ratio)) return {};
    const current = state.selectedAspectRatios.length > 0 ? state.selectedAspectRatios : [state.selectedAspectRatio || '1:1'];
    const has = current.includes(ratio);
    if (has && current.length === 1) return {};
    const next = has
      ? current.filter((r) => r !== ratio)
      : RATIO_ORDER.filter((r) => r === ratio || current.includes(r));
    return { selectedAspectRatios: next, selectedAspectRatio: next[0] ?? '1:1' };
  }),

  dismissVariantsConfirm: () => set({ pendingVariantsConfirm: null }),
  confirmVariantsAndSend: () => {
    const state = get();
    const pending = state.pendingVariantsConfirm;
    if (!pending) return;
    const confirmMsg = `confirm-variants:${pending.correlationId}`;
    set({ pendingVariantsConfirm: null });
    // Fire confirm pseudo-message carrying targetFormats so the orchestrator
    // short-circuit can dispatch directly to the create-variants playbook.
    void state.sendAgentMessage(confirmMsg, undefined, { targetFormats: pending.formats });
  },

  clearPendingPanTarget: () => {
    const projectId = get().activeProjectId;
    get()._writeProjectView(projectId, { pendingPanTarget: null });
  },
  clearPendingFitToView: () => {
    const projectId = get().activeProjectId;
    get()._writeProjectView(projectId, { pendingFitToView: false });
  },
  clearPendingFitToRow: () => {
    const projectId = get().activeProjectId;
    get()._writeProjectView(projectId, { pendingFitToRow: null });
  },
  clearBrandSelection: () => set({ brandSelectionNeeded: null }),
  openSidebarPanel: (content, ownerProjectId = get().activeProjectId) => set((s) => taskPanelPatch(s, ownerProjectId, {
    showSidebarPanel: true,
    sidebarPanelContent: { ...content, ownerProjectId },
    ...(content.type === 'report' && content.markdown
      ? { lastBriefContent: { title: content.title, content: content.markdown } }
      : {}),
  })),
  closeSidebarPanel: () => set({ showSidebarPanel: false, sidebarPanelContent: null }),

  setActiveResearchTask: (conversationKey, task) => set((s) => {
    if (task === null) {
      const updated = { ...s.researchTasksByConversation };
      delete updated[conversationKey];
      return { researchTasksByConversation: updated };
    }
    return { researchTasksByConversation: { ...s.researchTasksByConversation, [conversationKey]: task } };
  }),
  dismissResearchTask: (conversationKey) => set((s) => {
    const existing = s.researchTasksByConversation[conversationKey];
    if (!existing) return {};
    return {
      researchTasksByConversation: {
        ...s.researchTasksByConversation,
        [conversationKey]: { ...existing, dismissed: true },
      },
    };
  }),
  updateResearchTaskStep: (stepId, updates, conversationKey) => set((s) => {
    const convKey: string | number = conversationKey ?? s.activeProjectId ?? 'new';
    const existing = s.researchTasksByConversation[convKey];
    if (!existing) return {};
    const newSteps = existing.plan.steps.map((step) =>
      step.id === stepId ? { ...step, ...updates } : step
    );
    return {
      researchTasksByConversation: {
        ...s.researchTasksByConversation,
        [convKey]: {
          ...existing,
          plan: { ...existing.plan, steps: newSteps },
        },
      },
    };
  }),
  openDocumentOverlay: (title, content, ownerProjectId = get().activeProjectId) => set(s => taskPanelPatch(s, ownerProjectId, { showDocumentOverlay: true, documentOverlayTitle: title, documentOverlayContent: content })),
  closeDocumentOverlay: () => set({ showDocumentOverlay: false }),

  setZoom: (zoom) => set({ zoom }),
  setPan: (panX, panY) => set({ panX, panY }),
  setActiveTool: (activeTool) => set({ activeTool }),
  setSelectedImageIds: (selectedImageIds) => set({ selectedImageIds }),
  setActiveBrandId: (activeBrandId) => {
    set({ activeBrandId });
    const persistenceProjectId = get().activeProjectId;
    if (persistenceProjectId) {
      get()._writeProjectMetadata(persistenceProjectId, { brandId: activeBrandId });
      fetch(`/api/canvas/projects/${persistenceProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandId: activeBrandId }),
      }).catch(() => {});
    }
  },
  setActiveAdAccountId: (activeAdAccountId) => {
    set({ activeAdAccountId });
    const persistenceProjectId = get().activeProjectId;
    if (persistenceProjectId) {
      get()._writeProjectMetadata(persistenceProjectId, { adAccountId: activeAdAccountId });
      fetch(`/api/canvas/projects/${persistenceProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adAccountId: activeAdAccountId }),
      }).catch(() => {});
    }
  },
  setAttachedFile: (attachedFile) => set({ attachedFile }),
  setChatAttachments: (chatAttachments) => set({ chatAttachments }),
  addChatAttachment: (attachment) => set((s) => ({ chatAttachments: [...s.chatAttachments, attachment] })),
  removeChatAttachment: (url) => set((s) => ({ chatAttachments: s.chatAttachments.filter((a) => a.url !== url) })),
  setBrushMode: (brushMode) => set({ brushMode }),

  updateImagePositions: (updates) => {
    const projectKey = get().activeProjectId;
    get()._writeImages(projectKey, (imgs) => imgs.map((img) => {
      const u = updates.find((u) => u.id === img.id);
      return u ? { ...img, x: u.x, y: u.y } : img;
    }));
  },

  commitMove: (changes) => {
    const action: UndoAction = { type: 'MOVE', updates: changes };
    const moveProjectKey = get().activeProjectId;
    get()._writeImages(moveProjectKey, (imgs) => imgs.map((img) => {
      const c = changes.find((c) => c.id === img.id);
      return c ? { ...img, x: c.toX, y: c.toY } : img;
    }));
    set((s) => ({
      undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
      redoStack: [],
    }));
    // Debounced persistence: patch each moved image, cancels prior pending patch for same image
    const state = get();
    if (state.activeProjectId) {
      for (const c of changes) {
        const projectId = state.activeProjectId;
        debouncedPatchImage(c.id, () => {
          fetch(`/api/canvas/projects/${projectId}/images/${c.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ x: c.toX, y: c.toY }),
          }).catch(() => {});
        });
      }
    }
  },

  commitResize: (id, fromW, fromH, toW, toH, fromX, fromY, toX, toY) => {
    const action: UndoAction = { type: 'RESIZE', id, fromW, fromH, toW, toH, fromX, fromY, toX, toY };
    const resizeProjectKey = get().activeProjectId;
    get()._writeImages(resizeProjectKey, (imgs) => imgs.map((img) => {
      if (img.id !== id) return img;
      const updated: typeof img = { ...img, displayWidth: toW, displayHeight: toH };
      if (toX !== undefined) updated.x = toX;
      if (toY !== undefined) updated.y = toY;
      return updated;
    }));
    set((s) => ({
      undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
      redoStack: [],
    }));
    // Debounced persistence: patch resized image, cancels prior pending patch for same image
    const projectId = get().activeProjectId;
    if (projectId) {
      debouncedPatchImage(id, () => {
        fetch(`/api/canvas/projects/${projectId}/images/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ displayWidth: toW, displayHeight: toH, x: toX, y: toY }),
        }).catch(() => {});
      });
    }
  },

  toggleFullscreen: () => set((s) => ({ isFullscreen: !s.isFullscreen })),
  toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),
  toggleSidebarCollapsed: () => set((s) => {
    const next = !s.isSidebarCollapsed;
    try { localStorage.setItem('canvas-sidebar-collapsed', next ? '1' : '0'); } catch {}
    return { isSidebarCollapsed: next };
  }),
  setSidebarCollapsed: (collapsed) => set(() => {
    try { localStorage.setItem('canvas-sidebar-collapsed', collapsed ? '1' : '0'); } catch {}
    return { isSidebarCollapsed: collapsed };
  }),
  setTourRequest: (tourRequest) => set({ tourRequest }),
  setTourActive: (tourActive) => set({ tourActive }),
  setChatWidth: (chatWidth) => set({ chatWidth: Math.max(300, Math.min(600, chatWidth)) }),
  setContextMenu: (contextMenu) => set({ contextMenu }),
  setCommentPopover: (commentPopover) => set({ commentPopover }),
  setMarkPopover: (markPopover) => set({ markPopover }),

  addComment: (imageId, pinX, pinY, text) => {
    const comment: CanvasComment = { id: `c-${Date.now()}`, imageId, pinX, pinY, text, isPending: true, author: 'You' };
    const action: UndoAction = { type: 'ADD_COMMENT', comment };
    set((s) => {
      const nextComments = [...s.comments, comment];
      return {
        comments: nextComments,
        commentsByImageId: buildCommentsByImageId(nextComments),
        pendingCommentCount: s.pendingCommentCount + 1,
        commentPopover: null,
        undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
        redoStack: [],
      };
    });
  },

  removeComment: (imageId, commentId) => {
    set((s) => {
      const comment = s.comments.find((c) => c.id === commentId && c.imageId === imageId);
      const action: UndoAction | null = comment ? { type: 'DELETE_COMMENT', comment } : null;
      const next = s.comments.filter((c) => c.id !== commentId);
      const pendingCount = next.filter((c) => c.isPending).length;
      return {
        comments: next,
        commentsByImageId: buildCommentsByImageId(next),
        pendingCommentCount: pendingCount,
        ...(action ? { undoStack: [...s.undoStack, action].slice(-MAX_UNDO), redoStack: [] } : {}),
      };
    });
  },

  clearPendingComments: () => {
    set((s) => {
      const next = s.comments.filter((c) => !c.isPending);
      return { comments: next, commentsByImageId: buildCommentsByImageId(next), pendingCommentCount: 0 };
    });
  },

  getPendingEdits: (): PendingEdit[] => {
    const { comments } = get();
    const images = activeProj(get()).images;
    const pendingComments = comments.filter((c) => c.isPending);
    const imageIds = Array.from(new Set(pendingComments.map((c) => c.imageId)));
    return imageIds.map((imageId) => {
      const img = images.find((i) => i.id === imageId);
      return {
        imageId,
        imageSrc: img?.src ?? '',
        comments: pendingComments.filter((c) => c.imageId === imageId),
        prompt: img?.prompt,
      };
    });
  },

  setIsApplying: (isApplying) => set({ isApplying }),
  setApplyProgress: (applyProgress) => set({ applyProgress }),
  setCommentMode: (commentMode) => set((s) => {
    // When ENTERING comment mode, clear any pre-existing canvas selection.
    // Without this the previously-selected image (from before commentMode
    // toggled on) flows through as a chat "reference image" and confuses the
    // intended behavior of comments-as-edits. Comment mode treats canvas
    // images as comment targets, not chat references.
    if (commentMode && !s.commentMode) {
      return { commentMode, selectedImageIds: [] };
    }
    return { commentMode };
  }),

  discardPendingComments: () => {
    set((s) => {
      const next = s.comments.filter((c) => !c.isPending);
      return { comments: next, commentsByImageId: buildCommentsByImageId(next), pendingCommentCount: 0 };
    });
  },

  applyPendingComments: (ids?) => {
    set((s) => {
      const toApply = ids ? new Set(ids) : null;
      const pendingIds = s.comments.filter((c) => c.isPending && (!toApply || toApply.has(c.id))).map((c) => c.id);
      const action: UndoAction = { type: 'APPLY_COMMENTS', commentIds: pendingIds };
      const applied = new Set(pendingIds);
      const next = s.comments.map((c) => applied.has(c.id) ? { ...c, isPending: false } : c);
      return {
        comments: next,
        commentsByImageId: buildCommentsByImageId(next),
        pendingCommentCount: s.comments.filter((c) => c.isPending && !applied.has(c.id)).length,
        undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
        redoStack: [],
      };
    });
  },

  addMark: ({ imageId, regionX, regionY, regionWidth, regionHeight, instruction }) => {
    const mark: CanvasMark = {
      id: `mark-${Date.now()}`,
      imageId, regionX, regionY, regionWidth, regionHeight,
      instruction,
      isPending: true,
    };
    set((s) => ({ marks: [...s.marks, mark], pendingMarkCount: s.pendingMarkCount + 1, markPopover: null }));
  },

  discardPendingMarks: () => {
    set((s) => ({ marks: s.marks.filter((m) => !m.isPending), pendingMarkCount: 0 }));
  },

  applyPendingMarks: (ids?) => {
    set((s) => {
      const toApply = ids ? new Set(ids) : null;
      const applied = new Set(s.marks.filter((m) => m.isPending && (!toApply || toApply.has(m.id))).map((m) => m.id));
      return {
        marks: s.marks.map((m) => applied.has(m.id) ? { ...m, isPending: false } : m),
        pendingMarkCount: s.marks.filter((m) => m.isPending && !applied.has(m.id)).length,
      };
    });
  },

  deleteMark: (markId) => {
    set((s) => {
      const mark = s.marks.find((m) => m.id === markId);
      return {
        marks: s.marks.filter((m) => m.id !== markId),
        pendingMarkCount: mark?.isPending ? Math.max(0, s.pendingMarkCount - 1) : s.pendingMarkCount,
      };
    });
  },

  setActiveProject: (sidebarActiveProjectId) => set({ sidebarActiveProjectId }),
  setActiveProjectId: (id: number | null) => set({ activeProjectId: id }),
  setActiveTask: (activeTaskId) => set({ activeTaskId }),
  setFolderProjectId: (folderProjectId) => {
    // When leaving a project (folderProjectId → null), clear brand/account context
    // so loose chats are always brand-free
    if (folderProjectId === null) {
      set({ folderProjectId, activeBrandId: null, activeAdAccountId: null });
    } else {
      set({ folderProjectId });
    }
  },
  toggleOpenFolder: (id) => set((s) => ({
    openFolderIds: s.openFolderIds.includes(id)
      ? s.openFolderIds.filter((fid) => fid !== id)
      : [...s.openFolderIds, id],
  })),
  addOpenFolder: (id) => set((s) => ({
    openFolderIds: s.openFolderIds.includes(id) ? s.openFolderIds : [...s.openFolderIds, id],
  })),
  setPendingBrandsTab: (tab) => set({ pendingBrandsTab: tab }),
  setCaptionPicker: (v) => set({ captionPicker: v }),
  setCaptionBurning: (imageId, label) =>
    set((st) => {
      const next = { ...st.captionBurning };
      if (label == null) delete next[imageId];
      else next[imageId] = label;
      return { captionBurning: next };
    }),
  setActiveView: (activeView) => {
    const projectKey = get().activeProjectId;
    set({ previousView: activeProj(get()).activeView });
    if (projectKey != null) {
      // Project-scoped: write to per-project view state.
      get()._writeProjectView(projectKey, { activeView });
    } else {
      // No project active (sidebar menu items: Heartbeat, Brands, Analytics,
      // Ads Manager, Ad Library) — write to the standalone slot so the
      // selector picks it up. Without this, the menu item click silently
      // dropped on the floor and the view never changed.
      set({ standaloneActiveView: activeView });
    }
  },
  openAdLibrary: (styles, focusId) => {
    // Clear any project/task scope (mirrors the sidebar's Ad Library nav) so
    // setActiveView routes to the standalone slot, and stash the pre-filter +
    // optional pinned ad for CanvasAdLibraryView to pick up on mount.
    set({ activeProjectId: null, activeTaskId: null, adLibraryInitialStyles: styles, adLibraryFocusId: focusId ?? null });
    get().setActiveView('ad-library');
  },
  setCanvasOpen: (isCanvasOpen) => {
    // When opening: switch to 'chat-canvas'. When closing: stay in 'chat-canvas' so the
    // "Show canvas" pill remains visible — only `isCanvasOpen` controls visibility.
    const { taskCanvasState } = get();
    const persistenceProjectId = get().activeProjectId;
    const newView: 'chat-only' | 'chat-canvas' = isCanvasOpen ? 'chat-canvas' : 'chat-canvas';
    get()._writeProjectView(persistenceProjectId, { isCanvasOpen, activeView: newView });
    if (persistenceProjectId) {
      set({
        taskCanvasState: {
          ...taskCanvasState,
          [persistenceProjectId]: { isCanvasOpen, activeView: newView },
        },
        ...(isCanvasOpen ? { showSidebarPanel: false, showDocumentOverlay: false } : {}),
      });
    } else if (isCanvasOpen) {
      set({ showSidebarPanel: false, showDocumentOverlay: false });
    }
  },

  setTaskSteps: (taskSteps) => set({ taskSteps }),

  addGenerationRow: (row) => {
    const projectKey = get().activeProjectId;
    get()._writeGenerationRows(projectKey, (rows) => [...rows, row]);
  },

  updateGenerationRow: (id, updates) => {
    const projectKey = get().activeProjectId;
    get()._writeGenerationRows(projectKey, (rows) =>
      rows.map((r) => r.id === id ? { ...r, ...updates } : r)
    );
  },

  panToImage: (imageId) => {
    const state = get();
    const persistenceProjectId = state.activeProjectId;
    const proj = persistenceProjectId != null ? state.projects[persistenceProjectId] : null;
    const stateImages = proj?.images ?? [];
    const img = stateImages.find((i) => i.id === imageId);
    if (!img) return;

    // If canvas is not open, open it first
    const { taskCanvasState } = state;
    const projActiveView = proj?.activeView ?? 'chat-only';
    const projIsCanvasOpen = proj?.isCanvasOpen ?? false;
    if (!projIsCanvasOpen || projActiveView === 'chat-only') {
      get()._writeProjectView(persistenceProjectId, { isCanvasOpen: true, activeView: 'chat-canvas' as const });
      if (persistenceProjectId) {
        set({
          taskCanvasState: {
            ...taskCanvasState,
            [persistenceProjectId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const },
          },
        });
      }
    }

    // Center the canvas on the image using React Flow's setCenter via pendingPanTarget
    const centerX = img.x + img.displayWidth / 2;
    const centerY = img.y + img.displayHeight / 2;

    // Write pan target to per-project slice. zoom/imageId extensions live in
    // a separate state path now that the flat field is gone.
    const panProjKey = get().activeProjectId;
    get()._writeProjectView(panProjKey, { pendingPanTarget: { x: centerX, y: centerY } });
    set({ selectedImageIds: [imageId], highlightedImageId: imageId });

    // Clear highlight after 1.5s
    setTimeout(() => {
      set((s) => (s.highlightedImageId === imageId ? { highlightedImageId: null } : {}));
    }, 1500);
  },

  updateTaskStep: (label, status) => {
    set((s) => {
      const existing = s.taskSteps.find((t) => t.label === label);
      if (existing) {
        return { taskSteps: s.taskSteps.map((t) => t.label === label ? { ...t, status } : t) };
      }
      const newStep: TaskStep = { id: `step-${Date.now()}`, label, status };
      return { taskSteps: [...s.taskSteps, newStep] };
    });
  },

  addCanvasPlaceholder: (taskId, x, y, width, height, prompt?, genWidth?, genHeight?, filename?, projectIdOverride?) => {
    const projectKey = projectIdOverride ?? get().activeProjectId;
    get()._writeCanvasPlaceholders(projectKey, (m) => ({
      ...m,
      [taskId]: { taskId, x, y, width, height, prompt, genWidth, genHeight, filename },
    }));
  },

  resolveCanvasPlaceholder: (taskId, projectIdOverride?) => {
    // Capture target NOW so the deferred delete writes to the same project,
    // even if user switched in the 400ms window.
    const projectKey = projectIdOverride ?? get().activeProjectId;
    get()._writeCanvasPlaceholders(projectKey, (m) => ({
      ...m,
      [taskId]: { ...m[taskId], resolving: true },
    }));
    setTimeout(() => {
      get()._writeCanvasPlaceholders(projectKey, (m) => {
        const next = { ...m };
        delete next[taskId];
        return next;
      });
    }, 400);
  },

  failCanvasPlaceholder: (taskId, errorMsg, projectIdOverride?) => {
    const projectKey = projectIdOverride ?? get().activeProjectId;
    get()._writeCanvasPlaceholders(projectKey, (m) => ({
      ...m,
      [taskId]: { ...m[taskId], failed: true, errorMsg },
    }));
  },

  retryPlaceholder: (taskId) => {
    const ph = activeProj(get()).canvasPlaceholders[taskId];
    if (!ph || !ph.prompt) return;
    const { prompt, genWidth = 1080, genHeight = 1080, filename } = ph;
    // Pin the originating project NOW. The retry poll runs up to 3 minutes;
    // user could navigate away, so we route the result back to this chat.
    const retryOriginProjectId = get().activeProjectId;
    const projectKey = retryOriginProjectId;
    get()._writeCanvasPlaceholders(projectKey, (m) => ({
      ...m,
      [taskId]: { ...m[taskId], failed: false, errorMsg: undefined },
    }));
    fetch('/api/canvas/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, width: genWidth, height: genHeight }),
    })
      .then((r) => r.json())
      .then(async (d) => {
        if (!d.taskId) {
          get().failCanvasPlaceholder(taskId, 'Retry failed: no task ID');
          return;
        }
        for (let attempt = 0; attempt < 60; attempt++) {
          await new Promise((r) => setTimeout(r, 3000));
          const statusRes = await fetch(`/api/canvas/generate/status/${d.taskId}`);
          const statusData = await statusRes.json();
          if (statusData.status === 'completed' && statusData.outputUrl) {
            get().resolveCanvasPlaceholder(taskId);
            get().addImageToCanvas(statusData.outputUrl, genWidth, genHeight, filename || `canvas_gen_retry_${Date.now()}.jpg`, genWidth, genHeight, undefined, undefined, undefined, undefined, retryOriginProjectId ?? undefined);
            return;
          } else if (statusData.status === 'failed') {
            get().failCanvasPlaceholder(taskId, statusData.error || 'Retry generation failed');
            return;
          }
        }
        get().failCanvasPlaceholder(taskId, 'Retry timed out');
      })
      .catch((err) => get().failCanvasPlaceholder(taskId, err.message || 'Retry error'));
  },

  setImageProcessing: (id, processing) => {
    set((s) => {
      const next = new Set(s.processingImageIds);
      if (processing) next.add(id);
      else next.delete(id);
      return { processingImageIds: next };
    });
  },

  updateImage: (id, updates) => {
    const projectKey = get().activeProjectId;
    get()._writeImages(projectKey, (imgs) => imgs.map((img) => img.id === id ? { ...img, ...updates } : img));
  },

  toggleFavorite: (id) => {
    const state = get();
    const img = activeProj(state).images.find((i) => i.id === id);
    if (!img) return;
    const newFavorite = !img.isFavorite;
    const favProjectKey = get().activeProjectId;
    get()._writeImages(favProjectKey, (imgs) => imgs.map((i) => i.id === id ? { ...i, isFavorite: newFavorite } : i));
    const projectId = state.activeProjectId;
    if (projectId) {
      fetch(`/api/canvas/projects/${projectId}/images/${id}/favorite`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isFavorite: newFavorite }),
      }).catch(() => {});
    }
  },

  editComment: (commentId, newText) => {
    set((s) => {
      const next = s.comments.map((c) => c.id === commentId ? { ...c, text: newText, isFailed: false } : c);
      return { comments: next, commentsByImageId: buildCommentsByImageId(next) };
    });
  },

  markCommentsFailed: (imageId) => {
    set((s) => {
      const next = s.comments.map((c) =>
        c.imageId === imageId && c.isPending ? { ...c, isFailed: true } : c
      );
      return { comments: next, commentsByImageId: buildCommentsByImageId(next) };
    });
  },

  // v3.87 — failure re-open by EXACT dispatched ids. Apply now optimistically
  // resolves sent comments at send-time (so the next Apply can't re-send
  // them); when the server later reports 'failed' for an image, this re-opens
  // precisely the comments that were dispatched so Retry still works.
  markCommentsFailedByIds: (commentIds) => {
    const idSet = new Set(commentIds);
    set((s) => {
      const next = s.comments.map((c) =>
        idSet.has(c.id) ? { ...c, isFailed: true, isPending: true } : c
      );
      return {
        comments: next,
        commentsByImageId: buildCommentsByImageId(next),
        pendingCommentCount: next.filter((c) => c.isPending).length,
      };
    });
  },

  resetFailedComments: (imageId) => {
    set((s) => {
      const next = s.comments.map((c) =>
        c.imageId === imageId && c.isFailed ? { ...c, isFailed: false } : c
      );
      return { comments: next, commentsByImageId: buildCommentsByImageId(next) };
    });
  },

  addEditedImage: (img) => {
    const projectKey = get().activeProjectId;
    get()._writeImages(projectKey, (imgs) => [...imgs, img]);
  },

  addEditedChatMessage: (msg) => {
    const projectKey = get().activeProjectId;
    get()._writeMessages(projectKey, (msgs) => [...msgs, msg]);
  },

  addNotificationMessage: (content) => {
    const msg: CanvasChatMessage = {
      id: `notify-${Date.now()}`,
      role: 'assistant',
      content,
    };
    const projectKey = get().activeProjectId;
    get()._writeMessages(projectKey, (msgs) => [...msgs, msg]);
  },

  regenerateImage: (imageId) => {
    const state = get();
    const img = activeProj(state).images.find((i) => i.id === imageId);
    if (!img || !img.prompt) return;

    const { prompt, originalWidth = 1080, originalHeight = 1080 } = img;
    const displayScale = Math.min(280 / originalWidth, 280 / originalHeight, 1);
    const displayWidth = Math.round(originalWidth * displayScale);
    const displayHeight = Math.round(originalHeight * displayScale);
    const GAP = 24;
    const placeholderX = img.x + img.displayWidth + GAP;
    const placeholderY = img.y;
    const taskId = `regen-${Date.now()}`;
    const regenPrompt = prompt.trimEnd().replace(/,\s*alternative composition and angle\s*$/i, '') + ', alternative composition and angle';

    // Pin originating project — regeneration polls for up to 3 minutes; the
    // user could navigate away, so the result must route back here.
    const regenOriginProjectId = state.activeProjectId;

    get().addCanvasPlaceholder(taskId, placeholderX, placeholderY, displayWidth, displayHeight, regenPrompt, originalWidth, originalHeight, img.name.replace(/(\.[^.]+)$/, '-regen$1'));

    fetch('/api/canvas/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: regenPrompt,
        width: originalWidth,
        height: originalHeight,
        ...(state.activeBrandId ? { brandId: state.activeBrandId } : {}),
        ...(state.activeAdAccountId ? { adAccountId: state.activeAdAccountId } : {}),
      }),
    })
      .then((r) => r.json())
      .then(async (d) => {
        if (!d.taskId) { get().failCanvasPlaceholder(taskId, 'No task ID returned'); return; }
        for (let attempt = 0; attempt < 60; attempt++) {
          await new Promise((r) => setTimeout(r, 3000));
          const statusRes = await fetch(`/api/canvas/generate/status/${d.taskId}`);
          const statusData = await statusRes.json();
          if (statusData.status === 'completed' && statusData.outputUrl) {
            get().resolveCanvasPlaceholder(taskId);
            const regenFilename = img.name.replace(/(\.[^.]+)$/, '-regen$1') || `regen_${Date.now()}.jpg`;
            get().addImageNextTo(statusData.outputUrl, originalWidth, originalHeight, regenFilename, img.id, originalWidth, originalHeight, regenPrompt, regenOriginProjectId ?? undefined);
            const projectId = get().activeProjectId;
            if (projectId && state.activeAdAccountId) {
              fetch('/api/canvas/heartbeat-event', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  eventType: 'canvas_generation',
                  importance: 3,
                  content: `Canvas Regenerate: ${regenPrompt}`,
                  projectId,
                  adAccountId: state.activeAdAccountId,
                }),
              }).catch(() => {});
            }
            return;
          } else if (statusData.status === 'failed') {
            get().failCanvasPlaceholder(taskId, statusData.error || 'Generation failed');
            return;
          }
        }
        get().failCanvasPlaceholder(taskId, 'Generation timed out');
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Error';
        get().failCanvasPlaceholder(taskId, msg);
      });
  },

  addImageToCanvas: async (src, width, height, filename, originalWidth?, originalHeight?, prompt?, fixedX?: number, fixedY?: number, adMeta?: { adStyle?: string; adAngle?: string; wavespeedPrompt?: string; conceptName?: string; headline?: string; bodyCopy?: string; ctaText?: string; workflowRunId?: number; videoUrl?: string }, projectIdOverride?: number) => {
    console.log('[addImageToCanvas] called, src=', src?.slice(0, 60), 'override=', projectIdOverride);
    // Pin the target project up-front. SSE handlers pass projectIdOverride so
    // the image lands in the originating chat even if the user clicks away.
    // Without this, get().activeProjectId returns the chat the user is now
    // viewing, and the image leaks into the wrong chat's canvas + DB row.
    const targetProjectId: number | null = projectIdOverride ?? get().activeProjectId;
    const projectState = targetProjectId != null
      ? (get().projects[targetProjectId] ?? null)
      : null;

    // Snapshot images and rows from the TARGET project (not the active one)
    const displayScale = Math.min(DEFAULT_WIDTH / width, DEFAULT_HEIGHT / height, 1);
    const displayWidth = Math.round(width * displayScale);
    const displayHeight = Math.round(height * displayScale);
    // Use fixed position when provided (from placeholder), otherwise calculate from rows
    let posX: number;
    let posY: number;
    if (fixedX !== undefined && fixedY !== undefined) {
      posX = fixedX;
      posY = fixedY;
    } else {
      const currentImages = projectState?.images ?? [];
      const currentRows = projectState?.generationRows ?? [];
      const currentPlaceholders = Object.values(projectState?.canvasPlaceholders ?? {});
      let rowY = getNextRowY(currentRows, currentImages);
      // Find images already placed at this rowY so we don't stack on top of them
      const slotXAt = (y: number) => {
        const imagesAtRow = currentImages.filter(
          (img) => !isVirtualImage(img) && img.y === y
        );
        return START_X + imagesAtRow.length * (displayWidth + IMAGE_GAP);
      };
      posX = slotXAt(rowY);
      // v6.67 — collision guard. getNextRowY trusts the LAST TRACKED ROW, but
      // content added after that row (angle variations, clone drops, manually
      // moved tiles, in-flight placeholders) can sit below it — an Ad-library
      // clone dropped into a lived-in canvas landed on top of existing ads.
      // If the proposed slot rect intersects anything real, restart the row
      // below ALL content instead. Same-row siblings (same y, slotted to the
      // right) don't intersect, so sequential same-row adds keep working.
      const realRects = [
        ...currentImages.filter((im) => !isVirtualImage(im)).map((im) => ({ x: im.x, y: im.y, w: im.displayWidth, h: im.displayHeight })),
        ...currentPlaceholders.map((ph) => ({ x: ph.x, y: ph.y, w: ph.width, h: ph.height })),
      ];
      const collides = (x: number, y: number) =>
        realRects.some((r) => x < r.x + r.w && x + displayWidth > r.x && y < r.y + r.h && y + displayHeight > r.y);
      if (collides(posX, rowY)) {
        const maxBottom = realRects.reduce((max, r) => Math.max(max, r.y + r.h), rowY);
        rowY = maxBottom + ROW_GAP;
        posX = slotXAt(rowY);
      }
      posY = rowY;
    }
    // v2.05 — FINAL same-src choke point. Multiple paths can try to add the
    // same generated image (SSE canvas_image_ready, the canvas_image_pending
    // polling loop, image_complete, safety-net replays). Per-path requestId
    // guards have repeatedly missed an edge ("still got tons of duplicates"
    // on v2.04) — so enforce it where every generated-image add converges:
    // if a node with this src already exists in the target project, skip.
    // Manual Duplicate doesn't pass through here (duplicateImage writes the
    // copy directly), so user-made copies are unaffected.
    // NOTE: exact string compare on purpose — the video-clone runner relies on
    // query-param-distinct srcs (`?tile=clip-N` / `?tile=final`) to place tiles
    // that share a poster image; stripping query params in this comparison
    // would silently vanish its clip/final tiles.
    {
      const existingBySrc = (projectState?.images ?? []).find(
        (im) => !isVirtualImage(im) && im.src === src,
      );
      if (existingBySrc) {
        console.log('[addImageToCanvas] same-src node already on canvas — skipping duplicate add:', src.slice(0, 60));
        return;
      }
    }
    const currentImagesForColor = projectState?.images ?? [];
    const newImage: CanvasImage = {
      id: `img-gen-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: filename,
      src,
      x: posX,
      y: posY,
      displayWidth,
      displayHeight,
      originalWidth: originalWidth ?? width,
      originalHeight: originalHeight ?? height,
      colorIndex: currentImagesForColor.length,
      prompt,
      ...(adMeta ?? {}),
    };

    // Pinned target — same id used for the DB POST and the in-memory write.
    const projectIdAtStart = targetProjectId;

    // Persist to DB FIRST so that any concurrent switchProject will find the image
    // in the DB when it reloads, eliminating the blank-canvas race condition.
    if (projectIdAtStart) {
      try {
        await fetch(`/api/canvas/projects/${projectIdAtStart}/images`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientId: newImage.id,
            src: newImage.src,
            name: newImage.name,
            x: newImage.x,
            y: newImage.y,
            displayWidth: newImage.displayWidth,
            displayHeight: newImage.displayHeight,
            originalWidth: newImage.originalWidth,
            originalHeight: newImage.originalHeight,
            colorIndex: newImage.colorIndex,
            prompt: newImage.prompt,
            adStyle: newImage.adStyle,
            adAngle: newImage.adAngle,
            wavespeedPrompt: newImage.wavespeedPrompt,
            conceptName: newImage.conceptName,
            headline: newImage.headline,
            bodyCopy: newImage.bodyCopy,
            ctaText: newImage.ctaText,
            workflowRunId: newImage.workflowRunId,
            videoUrl: newImage.videoUrl,
          }),
        });
      } catch {
        // Ignore save failures — image still appears on canvas in-memory
      }
    }

    // No project switch guard needed — projectIdAtStart is pinned to the
    // ORIGINATING chat (via override or initial active id). All reads/writes
    // below target that pinned project, regardless of where the user is now.
    if (projectIdAtStart == null) {
      // No project to write to (truly orphan add). Bail.
      return;
    }

    // Compute bounding box + dedupe check using the pinned project's state.
    const action: UndoAction = { type: 'ADD', images: [newImage] };
    let rowBbox: { minX: number; minY: number; maxX: number; maxY: number };
    let alreadyPresent: boolean;
    {
      const proj = get().projects[projectIdAtStart] ?? defaultProjectState(projectIdAtStart);
      const rowY = newImage.y;
      const rowMembers: Array<{ x: number; y: number; w: number; h: number }> = [
        { x: newImage.x, y: rowY, w: newImage.displayWidth, h: newImage.displayHeight },
      ];
      for (const img of proj.images) {
        if (!isVirtualImage(img) && img.y === rowY && img.id !== newImage.id) {
          rowMembers.push({ x: img.x, y: rowY, w: img.displayWidth, h: img.displayHeight });
        }
      }
      for (const ph of Object.values(proj.canvasPlaceholders)) {
        if (ph.y === rowY) {
          rowMembers.push({ x: ph.x, y: rowY, w: ph.width, h: ph.height });
        }
      }
      rowBbox = {
        minX: Math.min(...rowMembers.map((m) => m.x)),
        minY: rowY,
        maxX: Math.max(...rowMembers.map((m) => m.x + m.w)),
        maxY: Math.max(...rowMembers.map((m) => m.y + m.h)),
      };
      alreadyPresent = proj.images.some((i) => i.id === newImage.id);
    }
    get()._writeImages(projectIdAtStart, (imgs) => {
      const realImages = imgs.filter((i) => !isVirtualImage(i));
      if (alreadyPresent) return realImages;
      return [...realImages, newImage];
    });
    // Only nudge the view (open canvas, set fit-to-row) if the user is
    // currently looking at this project — otherwise we'd briefly disrupt
    // their view of a different chat.
    if (get().activeProjectId === projectIdAtStart) {
      get()._writeProjectView(projectIdAtStart, { pendingFitToRow: rowBbox, isCanvasOpen: true, activeView: 'chat-canvas' as const });
    } else {
      get()._writeProjectView(projectIdAtStart, { pendingFitToRow: rowBbox, isCanvasOpen: true, activeView: 'chat-canvas' as const });
    }
    set((s) => ({
      ...(alreadyPresent ? {} : {
        undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
        redoStack: [],
      }),
      showDocumentOverlay: false,
      // The VideoScriptDoc is the CONTROL SURFACE of the generation that adds
      // these very tiles — closing it on each frame/clip arrival dead-ends the
      // flow (Generate unreachable). v6.77: the Angles doc likewise stays open
      // while "Make these ads" generates. Every other panel type keeps the
      // old close-on-add behavior. (Caught in Task 12 spec review.)
      showSidebarPanel: (s.sidebarPanelContent?.type === 'video_script' || s.sidebarPanelContent?.type === 'angle_set') ? s.showSidebarPanel : false,
    }));
    const afterSet = get();
    const afterProj = activeProj(afterSet);
    console.log('[addImageToCanvas] set() done. images.length=', afterProj.images.length, 'isCanvasOpen=', afterProj.isCanvasOpen, 'activeView=', afterProj.activeView);
    // 0ms retry guard: if a concurrent set() wiped the update, force-reapply immediately.
    const retryImageId = newImage.id;
    setTimeout(() => {
      // Retry guard reads from the PINNED project, not the active one.
      const retryProj = projectIdAtStart != null
        ? (get().projects[projectIdAtStart] ?? null)
        : null;
      if (retryProj && !retryProj.images.some((i) => i.id === retryImageId)) {
        console.warn('[addImageToCanvas] 0ms retry: image not found in store — force re-applying');
        const retryAction: UndoAction = { type: 'ADD', images: [newImage] };
        const retryRowY = newImage.y;
        const retryMembers: Array<{ x: number; w: number; h: number }> = [
          { x: newImage.x, w: newImage.displayWidth, h: newImage.displayHeight },
        ];
        for (const img of retryProj.images) {
          if (!isVirtualImage(img) && img.y === retryRowY && img.id !== newImage.id) {
            retryMembers.push({ x: img.x, w: img.displayWidth, h: img.displayHeight });
          }
        }
        for (const ph of Object.values(retryProj.canvasPlaceholders)) {
          if (ph.y === retryRowY) {
            retryMembers.push({ x: ph.x, w: ph.width, h: ph.height });
          }
        }
        const retryRowBbox = {
          minX: Math.min(...retryMembers.map((m) => m.x)),
          minY: retryRowY,
          maxX: Math.max(...retryMembers.map((m) => m.x + m.w)),
          maxY: Math.max(...retryMembers.map((m) => retryRowY + m.h)),
        };
        get()._writeImages(projectIdAtStart, (imgs) => {
          const realImages = imgs.filter((i) => !isVirtualImage(i));
          return [...realImages, newImage];
        });
        get()._writeProjectView(projectIdAtStart, { pendingFitToRow: retryRowBbox, isCanvasOpen: true, activeView: 'chat-canvas' as const });
        set((retryS) => ({
          undoStack: [...retryS.undoStack, retryAction].slice(-MAX_UNDO),
          redoStack: [],
          showDocumentOverlay: false,
          // Same video_script/angle_set exemption as the primary close above —
          // the retry path must not close the panel the primary path preserved.
          showSidebarPanel: (retryS.sidebarPanelContent?.type === 'video_script' || retryS.sidebarPanelContent?.type === 'angle_set') ? retryS.showSidebarPanel : false,
        }));
      } else {
        console.log('[addImageToCanvas] 0ms check: image confirmed in store ✓ id=', retryImageId.slice(0, 24));
      }
    }, 0);
  },

  addImageNextTo: (src, width, height, filename, sourceImageId, originalWidth?, originalHeight?, prompt?, projectIdOverride?, placement: 'right' | 'below' = 'right', adMeta?: { adStyle?: string; adAngle?: string; wavespeedPrompt?: string; conceptName?: string; headline?: string; bodyCopy?: string; ctaText?: string }) => {
    // Pin to the originating project — SSE handlers pass projectIdOverride
    // so an image generated for chat A doesn't land in chat B if the user
    // has navigated away mid-stream.
    const projectKey: number | null = projectIdOverride ?? get().activeProjectId;
    const proj = projectKey != null
      ? (get().projects[projectKey] ?? defaultProjectState(projectKey))
      : defaultProjectState(-1);
    const source = proj.images.find((i) => i.id === sourceImageId);
    const displayScale = Math.min(320 / width, 320 / height, 1);
    const displayWidth = Math.round(width * displayScale);
    const displayHeight = Math.round(height * displayScale);
    // Two placement modes:
    //  - 'right' (default, for regen/edit) — place to the right of source,
    //    fanning right when multiple parallel adds happen.
    //  - 'below' (for variations) — place on a NEW ROW below source,
    //    fanning right within that row. Variations belong as a sibling
    //    group, not adjacent to existing peer ads.
    let x: number;
    let y: number;
    if (source) {
      if (placement === 'below') {
        // Place variations on a brand-new row beneath ALL existing canvas
        // content — not just below the source. This avoids the "variations
        // get interleaved with the row below source" problem when that
        // row already has unrelated ads on it.
        const existingMaxBottom = proj.images.reduce(
          (max, im) => Math.max(max, im.y + im.displayHeight),
          source.y + source.displayHeight
        );
        const targetY = existingMaxBottom + GEN_GAP;
        const variationRowImages = proj.images.filter((im) =>
          Math.abs(im.y - targetY) < 4
        );
        const rightmostInRow = variationRowImages.reduce(
          (max, im) => Math.max(max, im.x + im.displayWidth),
          source.x - GEN_GAP // so the first var aligns with source.x
        );
        x = rightmostInRow + GEN_GAP;
        y = targetY;
      } else {
        const sourceRowImages = proj.images.filter((im) =>
          im.id !== source.id && im.y === source.y && im.x > source.x
        );
        const rightEdgeOfSource = source.x + source.displayWidth;
        const rightmostSiblingEnd = sourceRowImages.reduce(
          (max, im) => Math.max(max, im.x + im.displayWidth),
          rightEdgeOfSource
        );
        x = rightmostSiblingEnd + GEN_GAP;
        y = source.y;
      }
    } else {
      x = GEN_START_X;
      y = GEN_START_Y;
    }
    const newImage: CanvasImage = {
      id: `img-gen-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: filename,
      src,
      x,
      y,
      displayWidth,
      displayHeight,
      originalWidth: originalWidth ?? width,
      originalHeight: originalHeight ?? height,
      colorIndex: proj.images.length,
      prompt,
      // Optional structured metadata so the Info badge + StyleAngle label
      // render on variations (campaign-v2 also passes these via adMeta).
      ...(adMeta?.adStyle ? { adStyle: adMeta.adStyle } : {}),
      ...(adMeta?.adAngle ? { adAngle: adMeta.adAngle } : {}),
      ...(adMeta?.wavespeedPrompt ? { wavespeedPrompt: adMeta.wavespeedPrompt } : {}),
      ...(adMeta?.conceptName ? { conceptName: adMeta.conceptName } : {}),
      ...(adMeta?.headline ? { headline: adMeta.headline } : {}),
      ...(adMeta?.bodyCopy ? { bodyCopy: adMeta.bodyCopy } : {}),
      ...(adMeta?.ctaText ? { ctaText: adMeta.ctaText } : {}),
    };
    const action: UndoAction = { type: 'ADD', images: [newImage] };
    const targetPanX = -(x - 80) * get().zoom;
    const targetPanY = -(y - 80) * get().zoom;
    get()._writeImages(projectKey, (imgs) => [...imgs, newImage]);
    set((curS) => ({
      panX: targetPanX,
      panY: targetPanY,
      undoStack: [...curS.undoStack, action].slice(-MAX_UNDO),
      redoStack: [],
    }));
  },

  sendMessage: async (content, attachedFileUrl?) => {
    const state = get();
    const stateProj = activeProj(state);

    // If the most-recent completed assistant message has active suggestion pills
    // (variation type question), route through the full agent path which has proper
    // SSE handling for playbook events (canvas_image_pending, canvas_image_ready, etc.)
    // and correctly injects reference images. Clear the pills immediately so future
    // messages are not accidentally re-routed after the pill question is answered.
    const lastAssistantMsg = stateProj.messages
      .slice()
      .reverse()
      .find((m) => m.role === 'assistant' && !m.isStreaming);
    if (lastAssistantMsg?.suggestionPills && lastAssistantMsg.suggestionPills.pills.length > 0) {
      get()._writeMessages(state.activeProjectId, (msgs) =>
        msgs.map((m) => m.id === lastAssistantMsg.id ? { ...m, suggestionPills: undefined } : m)
      );
      return get().sendAgentMessage(content);
    }

    // Loose chats (no folderProjectId) always have null brand/account context —
    // never inherit stale state from a previously visited project.
    let effectiveBrandId: number | null = state.folderProjectId ? state.activeBrandId : null;
    let effectiveAdAccountId: number | null = state.folderProjectId ? state.activeAdAccountId : null;

    // If inside a folder project, always resolve brand + ad accounts from the project.
    // This overrides any previously set activeAdAccountId to ensure we never use a stale
    // or manually-selected account from a prior session/project.
    if (state.folderProjectId) {
      try {
        const projResp = await fetch(`/api/canvas/projects/${state.folderProjectId}`);
        if (projResp.ok) {
          const proj = await projResp.json();
          if (proj.brandId) {
            effectiveBrandId = proj.brandId;
            set({ activeBrandId: proj.brandId });
            if (state.activeProjectId) {
              get()._writeProjectMetadata(state.activeProjectId, { brandId: proj.brandId });
            }
            // Always re-resolve ad accounts from this brand (never reuse potentially stale state)
            try {
              const brandAccountsResp = await fetch(`/api/brands/${proj.brandId}/ad-accounts`, { credentials: 'include' });
              if (brandAccountsResp.ok) {
                const brandAccounts = await brandAccountsResp.json();
                if (Array.isArray(brandAccounts) && brandAccounts.length > 0) {
                  effectiveAdAccountId = brandAccounts[0].id;
                  set({ activeAdAccountId: brandAccounts[0].id });
                  if (state.activeProjectId) {
                    get()._writeProjectMetadata(state.activeProjectId, { adAccountId: brandAccounts[0].id });
                  }
                } else {
                  // Brand has no linked ad accounts — clear stale account state
                  effectiveAdAccountId = null;
                  set({ activeAdAccountId: null });
                  if (state.activeProjectId) {
                    get()._writeProjectMetadata(state.activeProjectId, { adAccountId: null });
                  }
                }
              }
            } catch { /* non-blocking — proceed without ad account context */ }
          }
        }
      } catch { /* non-blocking */ }
    }

    const userContent = attachedFileUrl ? `${content}\n\n[Attached image: ${attachedFileUrl}]` : content;
    const userMsg: CanvasChatMessage = {
      id: `m-${Date.now()}`,
      role: 'user',
      content: userContent,
      thumbnails: attachedFileUrl ? [attachedFileUrl] : undefined,
    };

    const isCloneIntent = !!attachedFileUrl && (
      !content.trim() ||
      /\b(clone|recreate|remake|replicate|copy the style|make one like)\b/i.test(content)
    );
    const intent = isCloneIntent ? 'creation' : detectMessageIntent(content);
    const isResearch = intent === 'research' || intent === 'analysis';
    const isAdMap = intent === 'admap';

    const isApproveIntent = state.adMap && (state.adMap.status === 'in_review' || state.adMap.status === 'draft') &&
      /\b(approve|looks good|let['']s go|lgtm|ship it|go for it|good to go)\b/i.test(content);

    const isAdMapRefineIntent = state.adMap && !isApproveIntent && (state.adMap.status === 'in_review' || state.adMap.status === 'draft') &&
      !isAdMap &&
      /\b(change|update|edit|modify|add|remove|rename|replace|adjust|set|make|use|switch|more|less|different|another)\b/i.test(content);

    const initSteps: TaskStep[] = isCloneIntent ? [
      { id: 's1', label: 'Analyzing source ad', status: 'running' },
      { id: 's2', label: 'Extracting layout & style', status: 'pending' },
      { id: 's3', label: 'Generating clone', status: 'pending' },
      { id: 's4', label: 'Clone complete', status: 'pending' },
    ] : [];

    const isFirstMessage = stateProj.messages.length === 0;
    // Capture the project key for scoped isTyping — use 'new' for tasks without a DB id yet
    const projectKey: string | number = state.activeProjectId ?? 'new';
    // Capture projectId at stream start. ALL SSE event handlers in this stream
    // MUST route mutations through this captured value, NOT via
    // get().activeProjectId (which can change if user clicks another chat).
    const streamProjectId: number | null = state.activeProjectId;

    // Persist user message immediately before any async work so it survives
    // view transitions and store resets. Awaited to guarantee commit before attemptConnect().
    // Use the full messages list (not sliced conversationHistory) so switching back restores all messages.
    if (state.activeProjectId) {
      // v7.51 — the SAME entry shape saveConversationHistory posts. This used
      // to strip every entry to {role, content}: with no ids in the payload
      // the server's guard re-appended the resolved concepts entry at the
      // bottom (duplicate summary + a stranded "Make these ads" pill after
      // the newer message on reload) and every earlier ad/task card lost its
      // UI payload.
      const existingHistory = stateProj.messages
        .filter((m) => !m.isStreaming || m.content || m.agentTaskGroups?.length || m.workflowTaskCards?.length || m.adCards?.length || m.conceptsRun)
        .map(({ isStreaming, ...rest }) => rest);
      const newHistory = [
        ...existingHistory,
        { role: 'user' as const, content: userContent },
      ];
      try {
        const persistResp = await fetch(`/api/canvas/projects/${state.activeProjectId}/conversation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ history: newHistory }),
        });
        if (!persistResp.ok) {
          // Persistence failed — log but still allow the user's message to remain
          // visible in the local UI and proceed with generation.
          console.warn('[CanvasStore] Failed to persist user message before generation (HTTP', persistResp.status, ')');
        }
      } catch (persistErr) {
        console.warn('[CanvasStore] Network error persisting user message:', persistErr);
      }
    }

    get()._writeMessages(state.activeProjectId, (msgs) => [...msgs, userMsg]);
    set((s) => ({
      typingByProject: { ...s.typingByProject, [projectKey]: true },
      taskSteps: initSteps,
      followUpChips: [],
      anglePickerStages: null,
      attachedFile: null,
      sseError: false,
      sseConnected: true,
      researchTasksByConversation: isResearch
        ? (() => {
            const convKey: string | number = s.activeProjectId ?? 'new';
            const updated = { ...s.researchTasksByConversation };
            delete updated[convKey];
            return updated;
          })()
        : s.researchTasksByConversation,
    }));

    if (isApproveIntent) {
      get().approveAdMap();
      return;
    }

    if (isAdMapRefineIntent) {
      set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
      get()._writeMessages(state.activeProjectId, (msgs) => msgs.filter((m) => m.content !== ''));
      get().refineAdMapMessage(content);
      return;
    }

    if (isResearch) {
      get().sendResearchMessage(content);
      return;
    }

    if (isAdMap) {
      set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
      get()._writeMessages(state.activeProjectId, (msgs) => msgs.filter((m) => m.content !== ''));
      get().sendAdMapMessage(content);
      return;
    }

    // Smart title generation: fires only for the first message in a task
    // Uses persistenceProjectId (not activeTaskId) since it's set in all task flows
    if (isFirstMessage && state.activeProjectId) {
      const taskId = state.activeProjectId;
      const fallbackName = content.length > 40 ? content.slice(0, 40) + '…' : content;
      // Immediately set sidebar name to the 40-char fallback
      const invalidateTaskQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
        queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', taskId] });
        const currentState = get();
        if (currentState.folderProjectId) {
          queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', currentState.folderProjectId, 'tasks'] });
        }
      };
      fetch(`/api/canvas/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: fallbackName }),
      }).then(invalidateTaskQueries).catch(() => {});
      // Fire-and-forget: generate AI title and update if successful
      (async () => {
        try {
          const resp = await fetch('/api/canvas/generate-title', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: content }),
          });
          if (!resp.ok) return;
          const { title } = await resp.json();
          if (!title) return;
          const patchResp = await fetch(`/api/canvas/tasks/${taskId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: title }),
          });
          if (!patchResp.ok) return;
          invalidateTaskQueries();
        } catch { /* non-blocking — fallback already applied */ }
      })();
    }

    // Save the user message IMMEDIATELY (no debounce) for the project we're
    // posting to. This guarantees the message is in DB even if the user clicks
    // away within the 200ms debounce window. Claude-style: messages save at
    // all costs.
    if (streamProjectId != null) {
      get().saveConversationHistory(streamProjectId).catch(() => {});
    }

    const assistantMsgId = `m-${Date.now() + 1}`;
    const assistantMsg: CanvasChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      creativeMode: userMsg.creativeMode,
      content: '',
      isStreaming: true,
      imageSlots: [],
      taskSteps: initSteps,
      timestamp: Date.now() + 1,
      intent,
      aspectRatio: state.selectedAspectRatio,
    };
    get()._writeMessages(state.activeProjectId, (msgs) => [...msgs, assistantMsg]);

    const history = stateProj.conversationHistory.slice();

    const selectedImageUrls = state.selectedImageIds
      .map((id) => stateProj.images.find((img) => img.id === id)?.src)
      .filter((url): url is string => !!url);

    let retries = 0;
    const maxRetries = 3;
    let batchRowForThisMessage = -1; // will be set when the first image_generating event fires
    let generationTimeout: ReturnType<typeof setTimeout>;
    let sseAbortController: AbortController | null = null;

    const attemptConnect = async () => {
      sseAbortController = new AbortController();
      const { signal } = sseAbortController;

      // Inactivity timer: resets to 60s on every incoming SSE chunk.
      // If no progress is received within 60s the request is aborted and an
      // error message is appended to the chat.
      const fireInactivityTimeout = () => {
        sseAbortController?.abort();
        const timeoutErrorMsg: CanvasChatMessage = {
          id: `m-timeout-${Date.now()}`,
          role: 'assistant',
          content: 'Generation timed out — try again.',
          isStreaming: false,
        };
        get()._writeMessages(state.activeProjectId, (msgs) => [
          ...msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false } : m),
          timeoutErrorMsg,
        ]);
        set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
      };
      const resetInactivityTimer = () => {
        clearTimeout(generationTimeout);
        generationTimeout = setTimeout(fireInactivityTimeout, 60000);
      };
      // Start the initial inactivity timer
      resetInactivityTimer();

      try {
        const pendingWorkflowMsg = activeProj(get()).messages
          .slice()
          .reverse()
          .find((m) => m.workflowState?.status === 'paused' && m.workflowState.checkpointId);
        const pendingWorkflowRunId = pendingWorkflowMsg?.workflowState?.workflowRunId;
        const pendingCheckpointId = pendingWorkflowMsg?.workflowState?.checkpointId;

        const response = await fetch('/api/canvas/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal,
          body: JSON.stringify({
            message: content,
            creativeMode: (state.activeProjectId != null ? state.inspireByProject[state.activeProjectId] ?? false : state.inspire) ? "inspire" : "brand-led",

            brandId: effectiveBrandId,
            adAccountId: effectiveAdAccountId,
            selectedImageIds: state.selectedImageIds,
            selectedImageUrls,
            selectedImageId: state.selectedImageIds.length === 1 ? state.selectedImageIds[0] : undefined,
            conversationHistory: history,
            attachedImageUrl: attachedFileUrl,
            projectId: state.activeProjectId,
            selectedAspectRatio: state.selectedAspectRatio,
            selectedAspectRatios: state.selectedAspectRatios,
            ...(pendingWorkflowRunId && pendingCheckpointId
              ? { workflowRunId: pendingWorkflowRunId, checkpointId: pendingCheckpointId }
              : {}),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        set({ sseConnected: true, sseError: false });

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let fullText = '';
        let imagesGenerated = 0;
        let sseBuffer = '';
        const taskToSlot = new Map<string, number>();
        let nextSlotIndex = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Reset inactivity timer on each received chunk
          resetInactivityTimer();
          sseBuffer += decoder.decode(value, { stream: true });

          const blocks = sseBuffer.split('\n\n');
          sseBuffer = blocks.pop() ?? '';

          for (const block of blocks) {
            if (!block.trim()) continue;
            const event = parseSSEBlock(block);
            if (!event) continue;

            switch (event.type) {
              case 'web_research_start': {
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, webResearchStatus: 'searching' as const, thinkingText: 'Searching the web\u2026' }
                      : m
                  )
                );
                break;
              }
              case 'web_research_sources': {
                const rawSources: any[] = event.data.sources || [];
                const sources: WebResearchSource[] = rawSources.map((s: any) => ({
                  url: s.url || '',
                  title: s.title || s.domain || s.url || '',
                  snippet: s.snippet || '',
                  domain: s.domain || '',
                  favicon: s.favicon || '',
                  status: (['pending', 'success', 'failed', 'skipped'].includes(s.status) ? s.status : 'pending') as WebResearchSource['status'],
                }));
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, webResearchSources: sources, webResearchStatus: 'searching' as const }
                      : m
                  )
                );
                break;
              }
              case 'web_research_findings': {
                const count = event.data.count || 0;
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, webResearchStatus: 'analyzing' as const, thinkingText: `Analyzing ${count} source${count !== 1 ? 's' : ''}\u2026` }
                      : m
                  )
                );
                break;
              }
              case 'web_research_complete': {
                const completeSources: any[] = event.data.sources || [];
                const updatedSources: WebResearchSource[] = completeSources.map((s: any) => ({
                  url: s.url || '',
                  title: s.title || s.domain || s.url || '',
                  snippet: s.snippet || '',
                  domain: s.domain || '',
                  favicon: s.favicon || '',
                  status: (['pending', 'success', 'failed', 'skipped'].includes(s.status) ? s.status : 'success') as WebResearchSource['status'],
                }));
                const reportSummary = event.data.summary || '';
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) =>
                    m.id === assistantMsgId
                      ? {
                          ...m,
                          webResearchSources: updatedSources,
                          webResearchStatus: 'complete' as const,
                          webResearchReport: reportSummary || m.webResearchReport,
                        }
                      : m
                  )
                );
                break;
              }
              case 'token': {
                fullText += event.data.text || '';
                const strippedText = stripCommandTags(fullText);
                const displayText = suppressIncompleteArtifact(strippedText);
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) => m.id === assistantMsgId ? { ...m, content: displayText } : m)
                );
                // Throttle render so tokens appear at a natural reading pace (~20ms per chunk)
                await new Promise<void>((r) => setTimeout(r, 20));
                break;
              }
              case 'task_step': {
                const { step, status } = event.data;
                get().updateTaskStep(step, status);
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) => {
                    if (m.id !== assistantMsgId) return m;
                    // Only show task steps for image-generating intents
                    if (m.intent !== 'creation' && m.intent !== 'edit') return m;
                    const existing = m.taskSteps?.find((t) => t.label === step);
                    const taskSteps = existing
                      ? (m.taskSteps || []).map((t) => t.label === step ? { ...t, status } : t)
                      : [...(m.taskSteps || []), { id: `s-${Date.now()}`, label: step, status }];
                    // Update thinking text — always show a friendly "Thinking…" not the internal step label
                    const thinkingText = status === 'running' ? 'Thinking…' : (m.thinkingText ?? 'Thinking…');
                    return { ...m, taskSteps, thinkingText };
                  })
                );
                break;
              }
              case 'image_generating': {
                const { taskId: imgTaskId, index, total, prompt: genPrompt, width: genW, height: genH, filename: genFilename } = event.data;
                // Auto-open canvas panel when images start generating
                const curView = streamProjectId == null ? 'chat-only' : get().projects[streamProjectId]?.activeView;
                if (curView === 'chat-only' || curView === 'new-task') {
                  get()._writeProjectView(streamProjectId, { activeView: 'chat-canvas' as const, isCanvasOpen: true });
                  const newCanvasState = streamProjectId
                    ? { ...get().taskCanvasState, [streamProjectId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const } }
                    : get().taskCanvasState;
                  set(s => ({ taskCanvasState: newCanvasState, ...taskPanelPatch(s, streamProjectId, { showSidebarPanel: false, showDocumentOverlay: false }) }));
                } else if (curView === 'chat-canvas') {
                  get()._writeProjectView(streamProjectId, { isCanvasOpen: true });
                  const newCanvasState = streamProjectId
                    ? { ...get().taskCanvasState, [streamProjectId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const } }
                    : get().taskCanvasState;
                  set(s => ({ taskCanvasState: newCanvasState, ...taskPanelPatch(s, streamProjectId, { showSidebarPanel: false, showDocumentOverlay: false }) }));
                }
                const taskId = imgTaskId;
                const { displayWidth: placeholderW, displayHeight: placeholderH } = computeDisplaySize(genW || 1080, genH || 1080);

                // Row-per-batch: first image in batch establishes the row
                if (index === 0) {
                  const currentImages = activeProj(get()).images;
                  const existingRows = activeProj(get()).generationRows;
                  batchRowForThisMessage = existingRows.length > 0
                    ? existingRows[existingRows.length - 1].batchIndex + 1
                    : 0;
                  const rowY = getNextRowY(existingRows, currentImages);
                  const { displayHeight: estimatedHeight } = computeDisplaySize(genW || 1080, genH || 1080);
                  get().addGenerationRow({
                    id: `row-${assistantMsgId}`,
                    batchIndex: batchRowForThisMessage,
                    prompt: genPrompt || '',
                    brandId: state.activeBrandId ?? undefined,
                    imageIds: [],
                    rowY,
                    rowHeight: estimatedHeight,
                  });
                }

                // Look up the row we just registered (or the one assigned in index===0)
                const thisRow = activeProj(get()).generationRows.find(r => r.id === `row-${assistantMsgId}`);
                const rowY = thisRow?.rowY ?? START_Y;
                const positions = calculateBatchPositions(total, rowY, placeholderW);
                const { x, y } = positions[index] ?? { x: START_X, y: rowY };

                get().addCanvasPlaceholder(taskId, x, y, placeholderW, placeholderH, genPrompt, genW, genH, genFilename);

                // After placing ALL placeholders (last index), set pendingFitToRow to auto-pan
                if (index === total - 1) {
                  const lastPos = positions[total - 1];
                  get()._writeProjectView(streamProjectId, {
                    pendingFitToRow: {
                      minX: START_X,
                      minY: rowY,
                      maxX: lastPos.x + placeholderW,
                      maxY: rowY + placeholderH,
                    },
                  });
                }

                const currentSlot = nextSlotIndex++;
                taskToSlot.set(taskId, currentSlot);
                get()._writeMessages(streamProjectId, (msgs) =>
                  msgs.map((m) => {
                    if (m.id !== assistantMsgId) return m;
                    const slots = [...(m.imageSlots || []), { index: currentSlot, total }];
                    return { ...m, imageSlots: slots };
                  })
                );
                get().updateTaskStep('Generating images', 'running');
                break;
              }
              case 'image_complete': {
                const { taskId, imageUrl, width, height, filename, prompt: imgPrompt } = event.data;
                const slotIdx = taskToSlot.get(taskId) ?? -1;
                // Capture placeholder position from the ORIGINATING project's
                // bucket (streamProjectId), not whichever chat the user is on
                // right now. Same for the resolve.
                const projForPh = streamProjectId != null ? get().projects[streamProjectId] : null;
                const placeholder = projForPh?.canvasPlaceholders[taskId];
                const placeholderX = placeholder?.x;
                const placeholderY = placeholder?.y;
                get().resolveCanvasPlaceholder(taskId, streamProjectId ?? undefined);

                // Place image at the batch-row position (placeholder's coords) instead of getNextImagePosition
                const imgW = width || 1080;
                const imgH = height || 1080;
                const imgFilename = filename || `canvas_gen_${Date.now()}.jpg`;
                if (placeholderX !== undefined && placeholderY !== undefined) {
                  const { displayWidth, displayHeight } = computeDisplaySize(imgW, imgH);
                  const batchColorIndex = activeProj(get()).images.length;
                  const batchPlacedImage: CanvasImage = {
                    id: `img-gen-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    name: imgFilename,
                    src: imageUrl,
                    x: placeholderX,
                    y: placeholderY,
                    displayWidth,
                    displayHeight,
                    originalWidth: imgW,
                    originalHeight: imgH,
                    colorIndex: batchColorIndex,
                    prompt: imgPrompt,
                  };
                  const action: UndoAction = { type: 'ADD', images: [batchPlacedImage] };
                  get()._writeImages(streamProjectId, (imgs) => {
                    const realImages = imgs.filter((i) => !isVirtualImage(i));
                    // v2.05 — same-src guard (mirrors addImageToCanvas choke point)
                    if (realImages.some((i) => i.src === batchPlacedImage.src)) {
                      console.log('[image_complete] same-src node already on canvas — skipping duplicate add');
                      return realImages;
                    }
                    return [...realImages, batchPlacedImage];
                  });
                  set((s) => ({
                    undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
                    redoStack: [],
                  }));
                  // Persist
                  if (streamProjectId && batchPlacedImage!) {
                    fetch(`/api/canvas/projects/${streamProjectId}/images`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        clientId: batchPlacedImage!.id,
                        src: batchPlacedImage!.src,
                        name: batchPlacedImage!.name,
                        x: batchPlacedImage!.x,
                        y: batchPlacedImage!.y,
                        displayWidth: batchPlacedImage!.displayWidth,
                        displayHeight: batchPlacedImage!.displayHeight,
                        originalWidth: batchPlacedImage!.originalWidth,
                        originalHeight: batchPlacedImage!.originalHeight,
                        colorIndex: batchPlacedImage!.colorIndex,
                        prompt: batchPlacedImage!.prompt,
                      }),
                    }).catch(() => {});
                  }
                } else {
                  // Pinned to streamProjectId — image goes to the originating chat
                  // even if the user has navigated to a different one.
                  get().addImageToCanvas(imageUrl, imgW, imgH, imgFilename, imgW, imgH, imgPrompt, undefined, undefined, undefined, streamProjectId ?? undefined);
                }
                imagesGenerated++;

                // Track image ID in generation row and update rowHeight if actual image is taller
                const addedImg = activeProj(get()).images.find((i) => i.src === imageUrl);
                if (addedImg) {
                  const rowId = `row-${assistantMsgId}`;
                  const existingRow = activeProj(get()).generationRows.find(r => r.id === rowId);
                  const actualDisplayHeight = addedImg.displayHeight;
                  get().updateGenerationRow(rowId, {
                    imageIds: [...(existingRow?.imageIds || []), addedImg.id],
                    rowHeight: Math.max(existingRow?.rowHeight ?? 0, actualDisplayHeight),
                  });
                }

                if (slotIdx >= 0) {
                  get()._writeMessages(streamProjectId, (msgs) =>
                    msgs.map((m) => {
                      if (m.id !== assistantMsgId) return m;
                      const slots = (m.imageSlots || []).map((slot) =>
                        slot.index === slotIdx ? { ...slot, resolved: imageUrl } : slot
                      );
                      return { ...m, imageSlots: slots };
                    })
                  );
                }
                break;
              }
              case 'image_error': {
                const { taskId, error } = event.data;
                const slotIdx = taskToSlot.get(taskId) ?? -1;
                get().failCanvasPlaceholder(taskId, error || 'Generation failed');
                if (slotIdx >= 0) {
                  get()._writeMessages(streamProjectId, (msgs) =>
                    msgs.map((m) => {
                      if (m.id !== assistantMsgId) return m;
                      const slots = (m.imageSlots || []).map((slot) =>
                        slot.index === slotIdx ? { ...slot, failed: true, errorMsg: error } : slot
                      );
                      return { ...m, imageSlots: slots };
                    })
                  );
                }
                break;
              }
              case 'edit_command': {
                const { imageUrl } = event.data;
                if (!imageUrl) break;
                // Auto-open canvas panel on edit commands
                const editView = activeProj(get()).activeView;
                if (editView === 'chat-only' || editView === 'new-task') {
                  get()._writeProjectView(streamProjectId, { activeView: 'chat-canvas' as const, isCanvasOpen: true });
                  const editCanvasState = streamProjectId
                    ? { ...get().taskCanvasState, [streamProjectId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const } }
                    : get().taskCanvasState;
                  set({ taskCanvasState: editCanvasState });
                } else if (editView === 'chat-canvas') {
                  get()._writeProjectView(streamProjectId, { isCanvasOpen: true });
                  const editCanvasState = streamProjectId
                    ? { ...get().taskCanvasState, [streamProjectId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const } }
                    : get().taskCanvasState;
                  set({ taskCanvasState: editCanvasState });
                }
                const editTarget = activeProj(get()).images.find((i) => i.src === imageUrl);
                if (editTarget) {
                  get().setImageProcessing(editTarget.id, true);
                }
                break;
              }
              case 'edit_complete': {
                const { action: editAction, imageUrl: editImgUrl, outputUrl: editOutputUrl } = event.data;
                if (!editOutputUrl || !editImgUrl) break;
                const targetImg = activeProj(get()).images.find((i) => i.src === editImgUrl);
                if (editAction === 'upscale' && targetImg) {
                  get().updateImage(targetImg.id, {
                    src: editOutputUrl,
                    displayWidth: targetImg.displayWidth * 2,
                    displayHeight: targetImg.displayHeight * 2,
                  });
                  get().setImageProcessing(targetImg.id, false);
                } else if (editAction === 'remove_background' && targetImg) {
                  get().addImageNextTo(
                    editOutputUrl,
                    targetImg.originalWidth,
                    targetImg.originalHeight,
                    `nobg_${targetImg.name}`,
                    targetImg.id,
                    targetImg.originalWidth,
                    targetImg.originalHeight,
                    undefined,
                    streamProjectId ?? undefined,
                  );
                  get().setImageProcessing(targetImg.id, false);
                } else if (editAction === 'inpaint' && targetImg) {
                  get().updateImage(targetImg.id, { src: editOutputUrl });
                  get().setImageProcessing(targetImg.id, false);
                }
                break;
              }
              case 'edit_error': {
                const { imageUrl: errImgUrl } = event.data;
                if (errImgUrl) {
                  const errImg = activeProj(get()).images.find((i) => i.src === errImgUrl);
                  if (errImg) get().setImageProcessing(errImg.id, false);
                }
                break;
              }
              case 'done': {
                clearTimeout(generationTimeout);
                const strippedForContext = stripArtifactTags(stripCommandTags(fullText));
                const chips = generateFollowUpChips(strippedForContext, imagesGenerated);
                const newHistory = [
                  ...history,
                  { role: 'user' as const, content },
                  { role: 'assistant' as const, content: strippedForContext },
                ];
                get()._writeConversationHistory(state.activeProjectId, newHistory.slice(-20));
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) => {
                    if (m.id !== assistantMsgId) return m;
                    return {
                      ...m,
                      isStreaming: false,
                      content: stripCommandTags(fullText),
                      taskSteps: (m.taskSteps || []).map((t) =>
                        t.status === 'running' ? { ...t, status: 'completed' as const } : t
                      ),
                    };
                  })
                );
                set((s) => ({
                  typingByProject: { ...s.typingByProject, [projectKey]: false },
                  followUpChips: chips,
                  taskSteps: s.taskSteps.map((t) =>
                    t.status === 'running' ? { ...t, status: 'completed' as const } : t
                  ),
                }));
                // After all images in the batch complete, re-fit the viewport to the row
                // so it shows the final image dimensions (may differ from placeholder estimates).
                if (imagesGenerated > 0) {
                  const lastRow = activeProj(get()).generationRows.find(r => r.id === `row-${assistantMsgId}`);
                  const lastRowProjKey = get().activeProjectId;
                  if (lastRow) {
                    const rowImages = activeProj(get()).images.filter(img => lastRow.imageIds.includes(img.id));
                    if (rowImages.length > 0) {
                      get()._writeProjectView(lastRowProjKey, {
                        pendingFitToRow: {
                          minX: Math.min(...rowImages.map(i => i.x)),
                          minY: Math.min(...rowImages.map(i => i.y)),
                          maxX: Math.max(...rowImages.map(i => i.x + i.displayWidth)),
                          maxY: Math.max(...rowImages.map(i => i.y + i.displayHeight)),
                        },
                      });
                    } else {
                      get()._writeProjectView(lastRowProjKey, { pendingFitToView: true });
                    }
                  } else {
                    get()._writeProjectView(lastRowProjKey, { pendingFitToView: true });
                  }
                }
                // Debounced conversation history persistence (2s after done) — scoped to streamProjectId
                debouncedSaveConversation(() => {
                  if (streamProjectId != null) {
                    get().saveConversationHistory(streamProjectId).catch(() => {});
                  }
                });
                break;
              }
            }
          }
        }

        clearTimeout(generationTimeout);
        get()._writeMessages(state.activeProjectId, (msgs) =>
          msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false } : m)
        );
        set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));

      } catch (err) {
        clearTimeout(generationTimeout);
        // If the request was aborted by the timeout handler, don't retry —
        // the timeout already appended a system error message and cleared isTyping.
        if (err instanceof Error && err.name === 'AbortError') return;
        retries++;
        if (retries <= maxRetries) {
          set({ sseConnected: false });
          await new Promise((r) => setTimeout(r, 3000));
          return attemptConnect();
        }
        get()._writeMessages(state.activeProjectId, (msgs) =>
          msgs.map((m) => {
            if (m.id !== assistantMsgId) return m;
            return {
              ...m,
              isStreaming: false,
              content: m.content || "Sorry, I couldn't connect. Please try again.",
              taskSteps: (m.taskSteps || []).map((t) =>
                t.status === 'running' ? { ...t, status: 'failed' as const } : t
              ),
            };
          })
        );
        set((s) => ({
          typingByProject: { ...s.typingByProject, [projectKey]: false },
          sseError: true,
          sseConnected: false,
          taskSteps: s.taskSteps.map((t) =>
            t.status === 'running' ? { ...t, status: 'failed' as const } : t
          ),
        }));
      }
    };

    attemptConnect();
  },

  sendResearchMessage: async (content) => {
    const state = get();
    const researchProjectKey: string | number = state.activeProjectId ?? 'new';
    const assistantMsgId = `m-research-${Date.now()}`;
    const assistantMsg: CanvasChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      taskSteps: [],
      timestamp: Date.now(),
    };
    get()._writeMessages(state.activeProjectId, (msgs) => [...msgs, assistantMsg]);

    try {
      const response = await fetch('/api/canvas/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,

          projectId: state.activeProjectId,
          adAccountId: state.activeAdAccountId,
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const blocks = sseBuffer.split('\n\n');
        sseBuffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;
          const event = parseSSEBlock(block);
          if (!event) continue;

          switch (event.type) {
            case 'brand_selection_needed': {
              const { target, options, projectId: selectionProjectId } = event.data;
              get()._writeMessages(state.activeProjectId, (msgs) =>
                msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false, content: '' } : m)
              );
              set((s) => ({
                brandSelectionNeeded: {
                  target,
                  options,
                  projectId: selectionProjectId,
                  assistantMsgId,
                },
                typingByProject: { ...s.typingByProject, [researchProjectKey]: false },
              }));
              break;
            }
            case 'task_plan': {
              const { taskId, plan } = event.data;
              const researchTask: ActiveResearchTask & { dismissed: boolean } = {
                taskId,
                plan: {
                  ...plan,
                  steps: (plan.steps || []).map((s: any) => ({ ...s, status: 'pending' as const, substeps: [] })),
                },
                status: 'running',
                completedSteps: 0,
                totalSteps: (plan.steps || []).length,
                documentReady: false,
                followUps: [],
                startedAt: Date.now(),
                dismissed: false,
              };
              set((s) => ({
                researchTasksByConversation: { ...s.researchTasksByConversation, [researchProjectKey]: researchTask },
              }));
              get()._writeMessages(state.activeProjectId, (msgs) =>
                msgs.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, content: `Starting research: **${plan.title}**\n\nPlanning ${(plan.steps || []).length} research steps...` }
                    : m
                )
              );
              break;
            }
            case 'step_start': {
              const { stepId } = event.data;
              get().updateResearchTaskStep(stepId, { status: 'running', substeps: [] }, researchProjectKey);
              break;
            }
            case 'step_progress': {
              const { stepId, text } = event.data;
              set((s) => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return {};
                const newSteps = existing.plan.steps.map((step) =>
                  step.id === stepId
                    ? { ...step, substeps: [...(step.substeps || []), text] }
                    : step
                );
                return {
                  researchTasksByConversation: {
                    ...s.researchTasksByConversation,
                    [researchProjectKey]: {
                      ...existing,
                      plan: { ...existing.plan, steps: newSteps },
                    },
                  },
                };
              });
              break;
            }
            case 'step_complete': {
              const { stepId, summary, durationMs } = event.data;
              get().updateResearchTaskStep(stepId, { status: 'completed', summary, durationMs }, researchProjectKey);
              set((s) => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return {};
                return {
                  researchTasksByConversation: {
                    ...s.researchTasksByConversation,
                    [researchProjectKey]: {
                      ...existing,
                      completedSteps: existing.completedSteps + 1,
                    },
                  },
                };
              });
              break;
            }
            case 'step_error': {
              const { stepId, error } = event.data;
              get().updateResearchTaskStep(stepId, { status: 'failed', summary: error }, researchProjectKey);
              break;
            }
            case 'summary_chunk': {
              const { text } = event.data;
              if (text) {
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, researchSummary: (m.researchSummary || '') + text }
                      : m
                  )
                );
              }
              break;
            }
            case 'document': {
              const { taskId, title, content } = event.data;
              const wordCount = content ? content.split(/\s+/).filter(Boolean).length : 0;
              const preview = content ? content.replace(/^#+\s*/gm, '').replace(/\*\*/g, '').replace(/\*/g, '').slice(0, 300) : '';
              set((s): Partial<CanvasStore> => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                const ownedDocument = taskPanelPatch(s, state.activeProjectId, { documentOverlayTitle: title || null, documentOverlayContent: content || null });
                if (!existing) return ownedDocument;
                const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, documentReady: true, status: 'completed', documentPreview: preview, documentWordCount: wordCount };
                const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
                return { researchTasksByConversation: tasks, ...ownedDocument };
              });
              break;
            }
            case 'task_complete': {
              const { title, completedSteps, totalSteps, taskId: completedTaskId } = event.data;
              get()._writeMessages(state.activeProjectId, (msgs) =>
                msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false } : m)
              );
              set((s): Partial<CanvasStore> => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return { typingByProject: { ...s.typingByProject, [researchProjectKey]: false } };
                const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, status: 'completed', completedSteps, documentReady: true };
                const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
                return { typingByProject: { ...s.typingByProject, [researchProjectKey]: false }, researchTasksByConversation: tasks };
              });
              break;
            }
            case 'follow_ups': {
              const { suggestions } = event.data;
              set((s): Partial<CanvasStore> => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return { followUpChips: suggestions || [] };
                const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, followUps: suggestions || [] };
                const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
                return { followUpChips: suggestions || [], researchTasksByConversation: tasks };
              });
              break;
            }
            case 'task_error': {
              const { error } = event.data;
              get()._writeMessages(state.activeProjectId, (msgs) =>
                msgs.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, isStreaming: false, content: `Research failed: ${error}` }
                    : m
                )
              );
              set((s): Partial<CanvasStore> => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return { typingByProject: { ...s.typingByProject, [researchProjectKey]: false } };
                const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, status: 'failed', errorMessage: error };
                const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
                return { typingByProject: { ...s.typingByProject, [researchProjectKey]: false }, researchTasksByConversation: tasks };
              });
              break;
            }
          }
        }
      }

      get()._writeMessages(state.activeProjectId, (msgs) =>
        msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false } : m)
      );
      set((s) => ({ typingByProject: { ...s.typingByProject, [researchProjectKey]: false } }));

    } catch (err: any) {
      get()._writeMessages(state.activeProjectId, (msgs) =>
        msgs.map((m) =>
          m.id === assistantMsgId
            ? { ...m, isStreaming: false, content: `Research failed: ${err.message}` }
            : m
        )
      );
      set((s) => ({
        typingByProject: { ...s.typingByProject, [researchProjectKey]: false },
        researchTasksByConversation: (() => {
          const existing = s.researchTasksByConversation[researchProjectKey];
          if (!existing) return s.researchTasksByConversation;
          const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, status: 'failed', errorMessage: err.message };
          const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
          return tasks;
        })(),
      }));
    }
  },

  sendRoutedResearchMessage: async (params: { target: string; type: 'new_brand' | 'competitor'; brandId?: number; projectId: number }) => {
    const state = get();
    const researchProjectKey: string | number = state.activeProjectId ?? 'new';
    const assistantMsgId = `m-research-${Date.now()}`;
    const assistantMsg: CanvasChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      taskSteps: [],
      timestamp: Date.now(),
    };
    get()._writeMessages(state.activeProjectId, (msgs) => [...msgs, assistantMsg]);
    set((s) => ({ typingByProject: { ...s.typingByProject, [researchProjectKey]: true } }));

    try {
      const response = await fetch(`/api/canvas/tasks/${params.projectId}/route-research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: params.type,
          brandId: params.brandId,
          target: params.target,
          conversationId: state.activeProjectId,
          adAccountId: state.activeAdAccountId,
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const blocks = sseBuffer.split('\n\n');
        sseBuffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;
          const event = parseSSEBlock(block);
          if (!event) continue;

          switch (event.type) {
            case 'task_plan': {
              const { taskId, plan } = event.data;
              const researchTask: ActiveResearchTask & { dismissed: boolean } = {
                taskId,
                plan: {
                  ...plan,
                  steps: (plan.steps || []).map((s: any) => ({ ...s, status: 'pending' as const, substeps: [] })),
                },
                status: 'running',
                completedSteps: 0,
                totalSteps: (plan.steps || []).length,
                documentReady: false,
                followUps: [],
                startedAt: Date.now(),
                dismissed: false,
              };
              set((s) => ({
                researchTasksByConversation: { ...s.researchTasksByConversation, [researchProjectKey]: researchTask },
              }));
              get()._writeMessages(state.activeProjectId, (msgs) =>
                msgs.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, content: `Starting competitor research: **${plan.title}**\n\nPlanning ${(plan.steps || []).length} research steps...` }
                    : m
                )
              );
              break;
            }
            case 'step_start': {
              const { stepId } = event.data;
              get().updateResearchTaskStep(stepId, { status: 'running', substeps: [] }, researchProjectKey);
              break;
            }
            case 'step_progress': {
              const { stepId, text } = event.data;
              set((s) => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return {};
                const newSteps = existing.plan.steps.map((step) =>
                  step.id === stepId
                    ? { ...step, substeps: [...(step.substeps || []), text] }
                    : step
                );
                return {
                  researchTasksByConversation: {
                    ...s.researchTasksByConversation,
                    [researchProjectKey]: { ...existing, plan: { ...existing.plan, steps: newSteps } },
                  },
                };
              });
              break;
            }
            case 'step_complete': {
              const { stepId, summary, durationMs } = event.data;
              get().updateResearchTaskStep(stepId, { status: 'completed', summary, durationMs }, researchProjectKey);
              set((s) => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return {};
                return {
                  researchTasksByConversation: {
                    ...s.researchTasksByConversation,
                    [researchProjectKey]: { ...existing, completedSteps: existing.completedSteps + 1 },
                  },
                };
              });
              break;
            }
            case 'step_error': {
              const { stepId, error } = event.data;
              get().updateResearchTaskStep(stepId, { status: 'failed', summary: error }, researchProjectKey);
              break;
            }
            case 'summary_chunk': {
              const { text } = event.data;
              if (text) {
                get()._writeMessages(state.activeProjectId, (msgs) =>
                  msgs.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, researchSummary: (m.researchSummary || '') + text }
                      : m
                  )
                );
              }
              break;
            }
            case 'document': {
              const { title, content } = event.data;
              const wordCount = content ? content.split(/\s+/).filter(Boolean).length : 0;
              const preview = content ? content.replace(/^#+\s*/gm, '').replace(/\*\*/g, '').replace(/\*/g, '').slice(0, 300) : '';
              set((s): Partial<CanvasStore> => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                const ownedDocument = taskPanelPatch(s, state.activeProjectId, { documentOverlayTitle: title || null, documentOverlayContent: content || null });
                if (!existing) return ownedDocument;
                const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, documentReady: true, status: 'completed', documentPreview: preview, documentWordCount: wordCount };
                const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
                return { researchTasksByConversation: tasks, ...ownedDocument };
              });
              break;
            }
            case 'task_complete': {
              const { completedSteps, totalSteps } = event.data;
              get()._writeMessages(state.activeProjectId, (msgs) =>
                msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false } : m)
              );
              set((s): Partial<CanvasStore> => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return { typingByProject: { ...s.typingByProject, [researchProjectKey]: false } };
                const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, status: 'completed', completedSteps, documentReady: true };
                const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
                return { typingByProject: { ...s.typingByProject, [researchProjectKey]: false }, researchTasksByConversation: tasks };
              });
              break;
            }
            case 'follow_ups': {
              const { suggestions } = event.data;
              set((s): Partial<CanvasStore> => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return { followUpChips: suggestions || [] };
                const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, followUps: suggestions || [] };
                const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
                return { followUpChips: suggestions || [], researchTasksByConversation: tasks };
              });
              break;
            }
            case 'task_error': {
              const { error } = event.data;
              get()._writeMessages(state.activeProjectId, (msgs) =>
                msgs.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, isStreaming: false, content: `Research failed: ${error}` }
                    : m
                )
              );
              set((s): Partial<CanvasStore> => {
                const existing = s.researchTasksByConversation[researchProjectKey];
                if (!existing) return { typingByProject: { ...s.typingByProject, [researchProjectKey]: false } };
                const updated: ActiveResearchTask & { dismissed: boolean } = { ...existing, status: 'failed', errorMessage: error };
                const tasks: Record<string | number, ActiveResearchTask & { dismissed: boolean }> = { ...s.researchTasksByConversation, [researchProjectKey]: updated };
                return { typingByProject: { ...s.typingByProject, [researchProjectKey]: false }, researchTasksByConversation: tasks };
              });
              break;
            }
          }
        }
      }

      get()._writeMessages(state.activeProjectId, (msgs) =>
        msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false } : m)
      );
      set((s) => ({ typingByProject: { ...s.typingByProject, [researchProjectKey]: false } }));

    } catch (err: any) {
      get()._writeMessages(state.activeProjectId, (msgs) =>
        msgs.map((m) =>
          m.id === assistantMsgId
            ? { ...m, isStreaming: false, content: `Research failed: ${err.message}` }
            : m
        )
      );
      set((s) => ({ typingByProject: { ...s.typingByProject, [researchProjectKey]: false } }));
    }
  },

  setAdMap: (adMap) => set({
    adMap,
    adMapId: adMap?.id ?? null,
    adMapStatus: adMap?.status ?? null,
  }),

  updateAdMapData: (updates) => set((s) => ({
    adMap: s.adMap ? { ...s.adMap, ...updates } : null,
    adMapStatus: updates.status ?? s.adMapStatus,
  })),

  clearAdMap: () => set({ adMap: null, adMapId: null, adMapStatus: null }),

  loadAdMap: async () => {
    const persistenceProjectId = get().activeProjectId;
    if (!persistenceProjectId) return;
    try {
      const res = await fetch(`/api/canvas/projects/${persistenceProjectId}/admap`);
      if (!res.ok) return;
      const adMap: AdMapRecord | null = await res.json();
      if (adMap) {
        set({ adMap, adMapId: adMap.id, adMapStatus: adMap.status });
      }
    } catch { /* non-blocking */ }
  },

  approveAdMap: async () => {
    const persistenceProjectId = get().activeProjectId;
    const { adMapId } = get();
    if (!persistenceProjectId || !adMapId) return;
    try {
      const res = await fetch(`/api/canvas/projects/${persistenceProjectId}/admap/${adMapId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const { adMap: approved, totalCreatives, message: approveMsg } = await res.json();
      set({ adMap: approved, adMapId: approved.id, adMapStatus: approved.status });
      const notifyMsg: CanvasChatMessage = {
        id: `notify-approve-${Date.now()}`,
        role: 'assistant',
        content: approveMsg || `AdMap approved! ${totalCreatives} creatives locked in. Say 'build it' when you're ready to generate.`,
      };
      get()._writeMessages(persistenceProjectId, (msgs) => [...msgs, notifyMsg]);
    } catch { /* non-blocking */ }
  },

  sendAdMapMessage: async (content) => {
    const state = get();
    const projectId = state.activeProjectId;
    if (!projectId) return;

    const projectKey: string | number = projectId ?? 'new';
    const adMapMsgId = `m-admap-${Date.now()}`;

    const assistantMsg: CanvasChatMessage = {
      id: adMapMsgId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      intent: 'admap',
      timestamp: Date.now(),
    };

    get()._writeMessages(projectId, (msgs) => [...msgs, assistantMsg]);
    set((s) => ({
      typingByProject: { ...s.typingByProject, [projectKey]: true },
      followUpChips: [],
      anglePickerStages: null,
      isGeneratingAdMap: true,
    }));

    try {
      const response = await fetch(`/api/canvas/projects/${projectId}/admap/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,

          brandId: state.activeBrandId,
          adAccountId: state.activeAdAccountId,
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const blocks = sseBuffer.split('\n\n');
        sseBuffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;
          const event = parseSSEBlock(block);
          if (!event) continue;

          switch (event.type) {
            case 'admap_thinking': {
              get()._writeMessages(projectId, (msgs) =>
                msgs.map((m) => m.id === adMapMsgId ? { ...m, content: event.data.message || 'Building your AdMap...' } : m)
              );
              break;
            }
            case 'admap_created': {
              const { adMap, totalCreatives, message: adMapMsg } = event.data;
              get()._writeMessages(projectId, (msgs) =>
                msgs.map((m) =>
                  m.id === adMapMsgId
                    ? { ...m, content: adMapMsg || `AdMap created with ${totalCreatives} creative angles.`, isStreaming: false }
                    : m
                )
              );
              set({ adMap, adMapId: adMap.id, adMapStatus: adMap.status });
              const curView = activeProj(get()).activeView;
              const curProjId = get().activeProjectId;
              if (curView === 'chat-only' || curView === 'new-task') {
                const newCanvasState = curProjId
                  ? { ...get().taskCanvasState, [curProjId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const } }
                  : get().taskCanvasState;
                set({ taskCanvasState: newCanvasState });
                get()._writeProjectView(curProjId, { activeView: 'chat-canvas', isCanvasOpen: true });
              }
              break;
            }
            case 'error': {
              get()._writeMessages(projectId, (msgs) =>
                msgs.map((m) =>
                  m.id === adMapMsgId
                    ? { ...m, content: `Failed to build AdMap: ${event.data.message}`, isStreaming: false }
                    : m
                )
              );
              break;
            }
            case 'done': {
              get()._writeMessages(projectId, (msgs) =>
                msgs.map((m) => m.id === adMapMsgId ? { ...m, isStreaming: false } : m)
              );
              set((s) => ({
                typingByProject: { ...s.typingByProject, [projectKey]: false },
                isGeneratingAdMap: false,
                followUpChips: ['Approve this AdMap', 'Add another audience segment', 'Change the campaign objective'],
              }));
              break;
            }
          }
        }
      }
    } catch (err: any) {
      get()._writeMessages(projectId, (msgs) =>
        msgs.map((m) =>
          m.id === adMapMsgId
            ? { ...m, isStreaming: false, content: `AdMap generation failed: ${err.message}` }
            : m
        )
      );
      set((s) => ({
        typingByProject: { ...s.typingByProject, [projectKey]: false },
        isGeneratingAdMap: false,
      }));
    }
  },

  refineAdMapMessage: async (content) => {
    const state = get();
    const projectId = state.activeProjectId;
    const adMapId = state.adMapId;
    if (!projectId || !adMapId) return;

    const projectKey: string | number = projectId ?? 'new';
    const refineMsgId = `m-admap-refine-${Date.now()}`;

    const assistantMsg: CanvasChatMessage = {
      id: refineMsgId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      intent: 'admap',
      timestamp: Date.now(),
    };

    get()._writeMessages(projectId, (msgs) => [...msgs, assistantMsg]);
    set((s) => ({
      typingByProject: { ...s.typingByProject, [projectKey]: true },
      followUpChips: [],
      anglePickerStages: null,
      isGeneratingAdMap: true,
    }));

    try {
      const response = await fetch(`/api/canvas/projects/${projectId}/admap/${adMapId}/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: content }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const blocks = sseBuffer.split('\n\n');
        sseBuffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;
          const event = parseSSEBlock(block);
          if (!event) continue;

          switch (event.type) {
            case 'admap_thinking': {
              get()._writeMessages(projectId, (msgs) =>
                msgs.map((m) => m.id === refineMsgId ? { ...m, content: event.data.message || 'Refining your AdMap...' } : m)
              );
              break;
            }
            case 'admap_updated': {
              const { adMap, totalCreatives, message: refineMsg } = event.data;
              get()._writeMessages(projectId, (msgs) =>
                msgs.map((m) =>
                  m.id === refineMsgId
                    ? { ...m, content: refineMsg || `Strategy updated! ${totalCreatives} creative angles.`, isStreaming: false }
                    : m
                )
              );
              set({ adMap, adMapId: adMap.id, adMapStatus: adMap.status });
              break;
            }
            case 'error': {
              get()._writeMessages(projectId, (msgs) =>
                msgs.map((m) =>
                  m.id === refineMsgId
                    ? { ...m, content: `Failed to refine AdMap: ${event.data.message}`, isStreaming: false }
                    : m
                )
              );
              break;
            }
            case 'done': {
              get()._writeMessages(projectId, (msgs) =>
                msgs.map((m) => m.id === refineMsgId ? { ...m, isStreaming: false } : m)
              );
              set((s) => ({
                typingByProject: { ...s.typingByProject, [projectKey]: false },
                isGeneratingAdMap: false,
                followUpChips: ['Approve this AdMap', 'Add another audience segment', 'Change the campaign objective'],
              }));
              break;
            }
          }
        }
      }
    } catch (err: any) {
      get()._writeMessages(projectId, (msgs) =>
        msgs.map((m) =>
          m.id === refineMsgId
            ? { ...m, isStreaming: false, content: `AdMap refinement failed: ${err.message}` }
            : m
        )
      );
      set((s) => ({
        typingByProject: { ...s.typingByProject, [projectKey]: false },
        isGeneratingAdMap: false,
      }));
    }
  },

  pushUndoAction: (action) => {
    set((s) => ({
      undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
      redoStack: [],
    }));
  },

  undo: () => {
    const state = get();
    if (state.undoStack.length === 0) return;
    const newUndoStack = [...state.undoStack];
    const action = newUndoStack.pop()!;
    const result = applyUndoActionBackward(action, activeProj(state).images, state.comments);
    const nextComments = result.comments ?? state.comments;
    const undoProjectKey = get().activeProjectId;
    if (result.images !== undefined) {
      const undoImgs = result.images;
      get()._writeImages(undoProjectKey, () => undoImgs);
    }
    set({
      ...(result.comments !== undefined ? {
        comments: nextComments,
        commentsByImageId: buildCommentsByImageId(nextComments),
      } : {}),
      pendingCommentCount: nextComments.filter((c) => c.isPending).length,
      undoStack: newUndoStack,
      redoStack: [...state.redoStack, action].slice(-MAX_UNDO),
    });
  },

  redo: () => {
    const state = get();
    if (state.redoStack.length === 0) return;
    const newRedoStack = [...state.redoStack];
    const action = newRedoStack.pop()!;
    const result = applyUndoActionForward(action, activeProj(state).images, state.comments);
    const nextComments = result.comments ?? state.comments;
    const redoProjectKey = get().activeProjectId;
    if (result.images !== undefined) {
      const redoImgs = result.images;
      get()._writeImages(redoProjectKey, () => redoImgs);
    }
    set({
      ...(result.comments !== undefined ? {
        comments: nextComments,
        commentsByImageId: buildCommentsByImageId(nextComments),
      } : {}),
      pendingCommentCount: nextComments.filter((c) => c.isPending).length,
      redoStack: newRedoStack,
      undoStack: [...state.undoStack, action].slice(-MAX_UNDO),
    });
  },

  deleteImages: (ids) => {
    const state = get();
    const toDelete = activeProj(state).images.filter((img) => ids.includes(img.id));
    if (toDelete.length === 0) return;
    const action: UndoAction = { type: 'DELETE', images: toDelete };
    const deleteProjectKey = get().activeProjectId;
    get()._writeImages(deleteProjectKey, (imgs) => imgs.filter((img) => !ids.includes(img.id)));
    set((s) => {
      const nextMarks = s.marks.filter((m) => !ids.includes(m.imageId));
      const nextComments = s.comments.filter((c) => !ids.includes(c.imageId));
      return {
        selectedImageIds: s.selectedImageIds.filter((sid) => !ids.includes(sid)),
        marks: nextMarks,
        comments: nextComments,
        commentsByImageId: buildCommentsByImageId(nextComments),
        pendingMarkCount: nextMarks.filter((m) => m.isPending).length,
        pendingCommentCount: nextComments.filter((c) => c.isPending).length,
        undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
        redoStack: [],
      };
    });
    const projectId = state.activeProjectId;
    if (projectId) {
      ids.forEach((id) => {
        fetch(`/api/canvas/projects/${projectId}/images/${id}`, { method: 'DELETE' }).catch(() => {});
      });
    }
  },

  deleteImage: (id) => {
    get().deleteImages([id]);
  },

  duplicateImage: (id) => {
    const state = get();
    const orig = activeProj(state).images.find((img) => img.id === id);
    if (!orig) return;
    // v5.77 — place the copy at the END of the original's row (same y band),
    // never overlapping. When the original is the rightmost tile this is
    // simply "beside it".
    const rowMates = activeProj(state).images.filter(
      (i) => Math.abs(i.y - orig.y) < Math.max(40, orig.displayHeight / 2),
    );
    const rowRight = Math.max(...rowMates.map((i) => i.x + i.displayWidth), orig.x + orig.displayWidth);
    const dup: CanvasImage = { ...orig, id: `img-dup-${Date.now()}`, x: rowRight + 24, y: orig.y };
    const action: UndoAction = { type: 'DUPLICATE', images: [dup] };
    const projectKey = get().activeProjectId;
    get()._writeImages(projectKey, (imgs) => [...imgs, dup]);
    set((s) => ({
      selectedImageIds: [dup.id],
      undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
      redoStack: [],
    }));
  },

  openLightbox: (id) => set({ lightboxImageId: id }),
  closeLightbox: () => set({ lightboxImageId: null }),

  copyToClipboard: (id) => {
    const img = activeProj(get()).images.find((i) => i.id === id);
    if (!img) return;
    set({ internalClipboard: img });
    try {
      navigator.clipboard?.writeText(img.src).catch(() => {});
    } catch {}
    const el = document.createElement('div');
    el.textContent = `Copied "${img.name}"`;
    el.style.cssText = 'position:fixed;bottom:88px;left:50%;transform:translateX(-50%);background:#374151;color:#fff;padding:6px 16px;border-radius:9999px;font-size:12px;font-weight:500;z-index:9999;pointer-events:none;transition:opacity 0.3s ease;box-shadow:0 2px 8px rgba(0,0,0,0.3)';
    document.body.appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 350); }, 1800);
  },

  pasteFromClipboard: () => {
    const state = get();
    const orig = state.internalClipboard;
    if (!orig) return;
    const pasted: CanvasImage = { ...orig, id: `img-paste-${Date.now()}`, x: orig.x + 30, y: orig.y + 30 };
    const action: UndoAction = { type: 'ADD', images: [pasted] };
    const projectKey = get().activeProjectId;
    get()._writeImages(projectKey, (imgs) => [...imgs, pasted]);
    set((s) => ({
      selectedImageIds: [pasted.id],
      undoStack: [...s.undoStack, action].slice(-MAX_UNDO),
      redoStack: [],
    }));
    const el = document.createElement('div');
    el.textContent = `Pasted "${pasted.name}"`;
    el.style.cssText = 'position:fixed;bottom:88px;left:50%;transform:translateX(-50%);background:#374151;color:#fff;padding:6px 16px;border-radius:9999px;font-size:12px;font-weight:500;z-index:9999;pointer-events:none;transition:opacity 0.3s ease;box-shadow:0 2px 8px rgba(0,0,0,0.3)';
    document.body.appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 350); }, 1800);
  },

  updateCanvasSettings: (updates) => {
    set((s) => ({ canvasSettings: { ...s.canvasSettings, ...updates } }));
  },

  // ─── Persistence ───────────────────────────────────────────────────────────

  loadOrCreateProject: async () => {
    // If switchProject already loaded a project, just return.
    // loadOrCreateProject fetches the most-recently-updated project, which may
    // differ from the one switchProject chose — overwriting it would be wrong.
    if (get().activeProjectId !== null) {
      return;
    }
    try {
      const resp = await fetch('/api/canvas/projects');
      if (!resp.ok) return;
      const projects: Array<{ id: number; name: string; color?: string; brandId?: number; adAccountId?: number; parentId?: number | null; is_folder?: boolean; isFolder?: boolean }> = await resp.json();

      // Filter to the first non-folder project so a recently-updated folder
      // at index 0 doesn't cause an early return and skip loading images.
      const folderProject = projects.find((p) => p.is_folder === true || p.isFolder === true);
      const project = projects.find((p) => p.is_folder !== true && p.isFolder !== true);
      if (!project) {
        // No non-folder project found; store the folder id if one exists
        if (folderProject) set({ folderProjectId: folderProject.id });
        return;
      }

      set({ activeProjectId: project.id, folderProjectId: project.parentId ?? folderProject?.id ?? null });
      if (project.brandId) set({ activeBrandId: project.brandId });
      if (project.adAccountId) set({ activeAdAccountId: project.adAccountId });
      get()._writeProjectMetadata(project.id, {
        brandId: project.brandId ?? null,
        adAccountId: project.adAccountId ?? null,
        parentId: project.parentId ?? folderProject?.id ?? null,
      });

      // Load saved images
      const imgResp = await fetch(`/api/canvas/projects/${project.id}/images`);
      if (imgResp.ok) {
        type SavedImageRow = { id?: number; clientId?: string; client_id?: string; src?: string; image_url?: string; name: string; x: number; y: number; displayWidth?: number; display_width?: number; displayHeight?: number; display_height?: number; originalWidth?: number; original_width?: number; originalHeight?: number; original_height?: number; colorIndex?: number; prompt?: string; adStyle?: string; adAngle?: string; wavespeedPrompt?: string; conceptName?: string; headline?: string; bodyCopy?: string; ctaText?: string; qaPassed?: boolean | null; qaReason?: string | null; variantOfImageId?: number | null; variant_of_image_id?: number | null; adAngleId?: number | null; workflowRunId?: number | null; videoUrl?: string | null; video_url?: string | null };
        const savedImages: SavedImageRow[] = await imgResp.json();
        console.log('[CanvasStore] loadOrCreateProject fetched images count=', savedImages.length, savedImages.map((si) => ({ clientId: si.clientId, displayWidth: si.displayWidth, displayHeight: si.displayHeight, src: si.src?.slice(0, 60) })));
        if (savedImages.length > 0) {
          // v4.17 — resolve the DB variant link (variant_of_image_id, a numeric
          // canvas_images.id) to the source image's CLIENT id so edited/variant
          // tiles keep their lineage after reload (Preview copy inheritance,
          // AdInfoBadge, etc. read img.sourceImageId).
          const dbIdToClientId = new Map<number, string>();
          for (const si of savedImages) {
            const cid = si.clientId || si.client_id || '';
            if (typeof si.id === 'number' && cid) dbIdToClientId.set(si.id, cid);
          }
          const restored: CanvasImage[] = savedImages.map((si) => {
            const src = si.src || si.image_url || '';
            if (!src) console.warn('[CanvasStore] restored image has no src, clientId=', si.clientId ?? si.client_id);
            const variantOf = si.variantOfImageId ?? si.variant_of_image_id;
            return {
              id: persistedCanvasImageId(si),
              sourceImageId: variantOf != null ? dbIdToClientId.get(variantOf) : undefined,
              name: si.name,
              src,
              x: si.x,
              y: si.y,
              displayWidth: si.displayWidth ?? si.display_width ?? 0,
              displayHeight: si.displayHeight ?? si.display_height ?? 0,
              originalWidth: si.originalWidth ?? si.original_width ?? si.displayWidth ?? si.display_width ?? 0,
              originalHeight: si.originalHeight ?? si.original_height ?? si.displayHeight ?? si.display_height ?? 0,
              colorIndex: si.colorIndex || 0,
              prompt: si.prompt,
              adStyle: si.adStyle,
              adAngle: si.adAngle,
              wavespeedPrompt: si.wavespeedPrompt,
              conceptName: si.conceptName,
              headline: si.headline,
              bodyCopy: si.bodyCopy,
              ctaText: si.ctaText,
              adAngleId: si.adAngleId ?? null,
              workflowRunId: si.workflowRunId ?? null,
              videoUrl: si.videoUrl ?? si.video_url ?? null,
              // Persisted QA result — surfaces the QaBadge from DB when the
              // in-memory imageQaBadges map doesn't have a live SSE-delivered
              // badge (e.g. after page reload, or for variations whose badge
              // SSE arrived before the requestId→url mapping was ready).
              qaPassed: si.qaPassed,
              qaReason: si.qaReason,
            };
          });
          // Populate imageQaBadges from persisted QA data
          const restoredQaBadges: Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }> = {};
          for (const si of savedImages) {
            if (si.qaPassed !== null && si.qaPassed !== undefined) {
              const src = si.src || si.image_url || '';
              if (src) {
                restoredQaBadges[src] = {
                  passed: si.qaPassed,
                  score: 0,
                  badgeType: si.qaPassed ? 'pass' : 'fail',
                  reason: si.qaReason || (si.qaPassed ? 'QA Passed' : 'QA Failed'),
                  attempt: 0,
                };
              }
            }
          }
          get()._writeImages(project.id, () => restored.filter(image => !!image.src));
          get()._writeQaBadges(project.id, () => restoredQaBadges);
          // Auto-open the canvas on initial load if the project has images
          // — same logic as switchProject. Without this, opening the app
          // straight to a chat with saved ads shows chat-only.
          if (restored.length > 0) {
            get()._writeProjectView(project.id, {
              isCanvasOpen: true,
              activeView: 'chat-canvas' as const,
            });
          }
        }
      }

      // Load saved conversation
      const convResp = await fetch(`/api/canvas/projects/${project.id}/conversation`);
      if (convResp.ok) {
        const conv = await convResp.json();
        if (conv?.history?.length) {
          const history: Array<Record<string, unknown>> = conv.history;
          // v7.43 — shared pure hydration (see conversation-hydrate.ts): the
          // v7.31 display rules (tool turns hidden, STOP-guard internals hidden,
          // dangling contentless assistant → crash marker, mid-history ones
          // dropped unless they carry UI) + server-owned concepts-run entries
          // (a still-`generating` run renders a live card, never the marker).
          const restoredMessages: CanvasChatMessage[] = hydrateConversation(history);
          // Build conversationHistory for LLM context (just role+content
          // for displayable messages; the server will rebuild the full
          // OpenAI-format thread including tool calls on the next turn).
          const llmHistory = toLlmHistory(history);
          const loadedProjectId = get().activeProjectId;
          // v4.47 — restore run cards lost to a mid-run disconnect (sleep/close).
          const withRunCards = loadedProjectId != null
            ? await synthesizeRunCards(loadedProjectId, restoredMessages, get().projects[loadedProjectId]?.images ?? [])
            : restoredMessages;
          get()._writeMessages(loadedProjectId, () => withRunCards);
          get()._writeConversationHistory(loadedProjectId, llmHistory);
          // v7.43 — concepts runs that were still writing when this tab last
          // saw them: poll until the server resolves the entry (or the set lands).
          if (loadedProjectId != null) {
            for (const m of withRunCards) {
              if (m.conceptsRun?.status === 'generating') {
                get().watchAngleSetRun(loadedProjectId, m.id, m.conceptsRun.setId ?? null).catch(() => {});
              }
            }
          }
        }
      }

      // Load AdMap if exists
      get().loadAdMap().catch(() => {});

      // v4.50 — if this project's newest run is still generating (reload
      // mid-run), keep pulling its ads from the DB until it completes.
      {
        const pidForWatch = get().activeProjectId;
        if (pidForWatch != null) get().watchRunningRun(pidForWatch).catch(() => {});
      }
    } catch (err) {
      console.warn('[CanvasStore] Failed to load project:', err);
    }
  },

  // Saves THIS project's images to THIS project's DB row.
  // projectId is captured at trigger time so debounced/async saves can't
  // race with project-switching.
  // After a long generation, individual canvas_image_ready SSE events can be
  // dropped — and the existing safety-net only fires when one DOES arrive. On
  // playbook_complete we pull the project's images from the DB (the source of
  // truth: every generated + QA'd image is persisted server-side) and merge in
  // any the live stream missed, so "WaveSpeed made N but only M showed" self-
  // heals without a manual reload. No-op when the canvas is already in sync.
  // v4.50 — a reload severs the SSE mid-run, so ads that land afterwards only
  // reach the DB and the reloaded canvas never shows them ("it only made 7
  // ads" — the last 3 were still in QA retry when the tab reloaded). While
  // the newest run is running, pull DB images every 10s until it completes
  // (bounded at 10 min, one loop per project).
  watchRunningRun: async (projectId: number) => {
    if (projectId == null || runCatchUpPolls.has(projectId)) return;
    runCatchUpPolls.add(projectId);
    try {
      for (let i = 0; i < 60; i++) {
        let runs: RecoverableRun[];
        try {
          const resp = await fetch(`/api/workflows?projectId=${projectId}`);
          if (!resp.ok) break;
          runs = await resp.json();
          if (!Array.isArray(runs)) break;
        } catch { break; }
        // Reconcile before resolving slots so ready images replace loaders in
        // the same poll. Check every run: a newer completed run must not hide
        // an older generation that is still active.
        await get().reconcileImagesFromServer(projectId).catch(() => {});
        const proj = get().projects[projectId];
        if (proj) {
          get()._writeMessages(projectId, current => recoverActiveRunCards(runs, current, proj.images));
        }
        if (!runs.some(isActiveRun)) break;
        await new Promise((r) => setTimeout(r, 10_000));
      }
    } finally {
      runCatchUpPolls.delete(projectId);
    }
  },

  watchAngleSetRun: async (projectId: number, msgId: string, setId: number | null) => {
    if (projectId == null || !msgId || angleSetRunWatches.has(msgId)) return;
    angleSetRunWatches.add(msgId);
    const INTERVAL_MS = 5_000;
    const CAP_MS = 12 * 60_000;
    const startedAt = Date.now();
    type SetRow = { id: number; name: string; status: string; angleCount: number };
    // The entry as the store holds it RIGHT NOW — null once it's gone or has
    // resolved through another path (live stream, a sibling watcher).
    const current = (): CanvasChatMessage | null =>
      get().projects[projectId]?.messages.find((m) => m.id === msgId) ?? null;
    // Every write re-checks that the message still lives in THIS project and
    // is still generating — the user may have switched tasks (or the live
    // stream may have finished it) while a fetch was in flight.
    const writeIfStillGenerating = (next: CanvasChatMessage) => {
      get()._writeMessages(projectId, (msgs) =>
        msgs.map((m) => {
          if (m.id !== msgId || m.conceptsRun?.status !== 'generating') return m;
          // An in-flight poll must not move a newer SSE milestone backwards.
          const currentDraft = m.conceptDraftProgress;
          const incomingDraft = next.conceptDraftProgress;
          return currentDraft && (!incomingDraft || currentDraft.updatedAt > incomingDraft.updatedAt)
            ? { ...next, conceptDraftProgress: currentDraft }
            : next;
        }),
      );
    };
    const fetchEntry = async (): Promise<Record<string, unknown> | null> => {
      try {
        const resp = await fetch(`/api/canvas/projects/${projectId}/conversation`);
        if (!resp.ok) return null;
        const conv = await resp.json();
        const history: Array<Record<string, unknown>> = Array.isArray(conv?.history) ? conv.history : [];
        return history.find((h) => h?.id === msgId) ?? null;
      } catch {
        return null;
      }
    };
    try {
      let knownSetId: number | null = setId;
      // v7.51 — the sweeper (and the run itself) flips the SET before writing
      // the entry. A poll landing in that gap used to synthesize a clean
      // "N concepts saved" card over what the server was about to write —
      // hiding an interruption's Retry pill and "N of M" count. One grace
      // tick lets the server's own (richer) entry win; a truly dead server
      // costs one extra 5s poll.
      let settledTicks = 0;
      while (Date.now() - startedAt < CAP_MS) {
        const msg = current();
        if (!msg || msg.conceptsRun?.status !== 'generating') return;

        // 1. Is the SET done? (Only when we know which set to ask about.)
        let set: SetRow | null = null;
        let setSettled = knownSetId == null;
        if (knownSetId != null) {
          try {
            const resp = await fetch(`/api/angle-sets/${knownSetId}`);
            if (resp.ok) {
              const json = (await resp.json()) as { set?: SetRow };
              set = json?.set ?? null;
              setSettled = !!set && set.status !== 'generating';
            }
          } catch {
            // transient — try again next tick
          }
        }

        // Reopened tasks no longer have the original SSE connection. Carry
        // persisted public draft milestones through the existing polling loop.
        if (!setSettled) {
          const entry = await fetchEntry();
          if (entry?.conceptsRun) {
            const [hydrated] = hydrateConversation([entry]);
            if (hydrated) writeIfStillGenerating(recordConceptPoll({ ...hydrated, id: msgId },set?.status));
          }
        }
        if (setSettled) {
          // 2. The server normally writes the resolved entry itself — prefer it.
          const entry = await fetchEntry();
          const entryRun = entry?.conceptsRun as CanvasChatMessage['conceptsRun'] | undefined;
          if (entry && entryRun && entryRun.status !== 'generating') {
            const [hydrated] = hydrateConversation([entry]);
            if (hydrated) writeIfStillGenerating(recordConceptPoll({ ...hydrated, id: msgId },set?.status));
            return;
          }
          // The entry is still `generating` while we have no set to wait on:
          // adopt the set id once the server stamps it, and carry any progress
          // the server wrote (task groups) onto the live card meanwhile.
          if (knownSetId == null) {
            if (entry && entryRun) {
              if (typeof entryRun.setId === 'number') knownSetId = entryRun.setId;
              const [hydrated] = hydrateConversation([entry]);
              if (hydrated) writeIfStillGenerating(recordConceptPoll({ ...hydrated, id: msgId },set?.status));
            }
          } else if (set && set.status === 'ready' && ++settledTicks >= 2) {
            // 3. Set finished but the server died between saving the angles and
            // writing the entry → synthesize the result on the client.
            writeIfStillGenerating({
              ...msg,
              content: `${set.angleCount} concepts saved as "${set.name}"`,
              isStreaming: false,
              agentThinking: undefined,
              conceptsRun: { ...msg.conceptsRun, status: 'ready', setId: set.id },
              agentTaskGroups: settleTaskGroups(msg.agentTaskGroups, 'completed'),
              angleSetCard: msg.angleSetCard ?? { setId: set.id, title: set.name, angleCount: set.angleCount },
            });
            return;
          } else if (set && set.status === 'failed' && ++settledTicks >= 2) {
            // Set failed and the entry never flipped — stop the spinner with a
            // retry line rather than polling for 12 minutes.
            writeIfStillGenerating({
              ...msg,
              content: msg.conceptsRun?.retryMessage
                || `Concept writing for "${set.name}" didn't finish. Resend your message to retry.`,
              isStreaming: false,
              agentThinking: undefined,
              conceptsRun: { ...msg.conceptsRun, status: 'failed', setId: set.id },
              agentTaskGroups: settleTaskGroups(msg.agentTaskGroups, 'failed'),
            });
            return;
          }
        }

        await new Promise((r) => setTimeout(r, INTERVAL_MS));
      }
      // Cap reached — stop silently; the next task open re-hydrates and re-watches.
    } finally {
      angleSetRunWatches.delete(msgId);
    }
  },

  reconcileImagesFromServer: async (projectId) => {
    if (projectId == null) return;
    try {
      const resp = await fetch(`/api/canvas/projects/${projectId}/images`);
      if (!resp.ok) return;
      const dbImages: any[] = await resp.json();
      if (!Array.isArray(dbImages)) return;
      const restored: CanvasImage[] = dbImages
        .filter((si) => !String(si.src || si.image_url || '').startsWith('blob:'))
        .map((si) => ({
          id: persistedCanvasImageId(si),
          name: si.name ?? 'generated',
          src: si.src || si.image_url || '',
          x: si.x,
          y: si.y,
          displayWidth: si.displayWidth ?? si.display_width ?? 0,
          displayHeight: si.displayHeight ?? si.display_height ?? 0,
          originalWidth: si.originalWidth ?? si.original_width ?? si.displayWidth ?? si.display_width ?? 0,
          originalHeight: si.originalHeight ?? si.original_height ?? si.displayHeight ?? si.display_height ?? 0,
          colorIndex: si.colorIndex || 0,
          prompt: si.prompt,
          adStyle: si.adStyle,
          adAngle: si.adAngle,
          wavespeedPrompt: si.wavespeedPrompt,
          conceptName: si.conceptName,
          headline: si.headline,
          bodyCopy: si.bodyCopy,
          ctaText: si.ctaText,
          adAngleId: (si as any).adAngleId ?? null,
          workflowRunId: (si as any).workflowRunId ?? null,
          videoUrl: (si as any).videoUrl ?? (si as any).video_url ?? null,
          qaPassed: si.qaPassed,
          qaReason: si.qaReason,
        }));
      let added = 0;
      let layoutChanged = false;
      get()._writeImages(projectId, (current) => {
        added = restored.filter(image => !current.some(existing => existing.id === image.id || existing.src === image.src)).length;
        const merged = reconcileImagePositions(current.filter(image => !!image.src), restored.filter(image => !!image.src), isVirtualImage);
        layoutChanged = merged !== current;
        return merged;
      });
      const runResp = await fetch(`/api/workflows?projectId=${projectId}`);
      if (runResp.ok) {
        const runs = await runResp.json();
        if (Array.isArray(runs)) {
          get()._writeCanvasPlaceholders(projectId, current => recoverRunPlaceholders(runs, restored, current));
          const project = get().projects[projectId];
          if (project && Object.keys(project.canvasPlaceholders).length > 0 && (project.activeView === 'chat-only' || project.activeView === 'chat-canvas')) {
            get()._writeProjectView(projectId, { isCanvasOpen: true, activeView: 'chat-canvas' });
          }
        }
      }
      // Save recovered IDs and the unchanged user positions together.
      if (layoutChanged) {
        void get().saveImagesSnapshot(projectId);
      }
      if (added > 0) {
        console.log(`[reconcileImagesFromServer] project ${projectId}: recovered ${added} image(s) the live stream missed`);
        const qa: Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }> = {};
        for (const si of dbImages) {
          const src = si.src || si.image_url || '';
          if (src && si.qaPassed != null) {
            qa[src] = { passed: !!si.qaPassed, score: 0, badgeType: si.qaPassed ? 'pass' : 'fail', reason: si.qaReason || (si.qaPassed ? 'QA Passed' : 'QA Failed'), attempt: 0 };
          }
        }
        get()._writeQaBadges(projectId, (cur) => ({ ...cur, ...qa }));
      }
    } catch (e) {
      console.warn('[reconcileImagesFromServer] failed', e);
    }
  },

  relayoutCanvasContent: (projectId) => {
    if (projectId == null) return false;
    const proj = get().projects[projectId];
    if (!proj) return false;
    // v4.79 — the global overlap regrid merges all sections into one 40px
    // block and moves tiles the user is watching; while ANY run is still
    // generating, leave overlaps alone (finalize cleans up at the end).
    const relayoutBusy = Object.values(proj.canvasPlaceholders).some(
      (p) => (p.taskId.startsWith('agent-img-') || p.taskId.startsWith('intent-ph-')) && !p.failed,
    );
    if (relayoutBusy) return false;
    // Combine REAL images + active placeholders into one set — a placeholder and
    // an image must never share a slot either. computeGridLayout only returns
    // moves when something actually overlaps, so a clean canvas is untouched.
    const items = [
      ...proj.images
        .filter((im) => !isVirtualImage(im))
        .map((im) => ({ key: `img:${im.id}`, x: im.x, y: im.y, w: im.displayWidth || DEFAULT_WIDTH, h: im.displayHeight || DEFAULT_HEIGHT })),
      ...Object.values(proj.canvasPlaceholders)
        .map((p) => ({ key: `ph:${p.taskId}`, x: p.x, y: p.y, w: p.width || DEFAULT_WIDTH, h: p.height || DEFAULT_HEIGHT })),
    ];
    const moves = computeGridLayout(items);
    if (moves.size === 0) return false; // already clean
    get()._writeImages(projectId, (cur) =>
      cur.map((im) => {
        const p = moves.get(`img:${im.id}`);
        return p ? { ...im, x: p.x, y: p.y } : im;
      }),
    );
    get()._writeCanvasPlaceholders(projectId, (m) => {
      let changed = false;
      const next = { ...m };
      for (const k of Object.keys(next)) {
        const p = moves.get(`ph:${next[k].taskId}`);
        if (p) { next[k] = { ...next[k], x: p.x, y: p.y }; changed = true; }
      }
      return changed ? next : m;
    });
    return true;
  },

  scheduleRelayout: (projectId) => {
    if (projectId == null) return;
    if (_relayoutTimer !== null && _relayoutProjectId === projectId) clearTimeout(_relayoutTimer);
    _relayoutProjectId = projectId;
    _relayoutTimer = setTimeout(() => {
      _relayoutTimer = null;
      get().relayoutCanvasContent(projectId);
    }, 180);
  },

  compactImagesToGrid: (projectId) => {
    if (projectId == null) return false;
    const proj = get().projects[projectId];
    if (!proj) return false;
    const real = proj.images.filter((im) => !isVirtualImage(im));
    if (real.length === 0) return false;
    // v4.79 — NEVER repack the whole canvas while any run is mid-flight:
    // run 1's finalize used to compact while run 2's loading boxes were
    // waiting, sliding tiles over them ("preview disappeared"). The LAST
    // finishing run compacts once nothing is generating.
    const anyGenerating = Object.values(proj.canvasPlaceholders).some(
      (p) => (p.taskId.startsWith('agent-img-') || p.taskId.startsWith('intent-ph-')) && !p.failed,
    );
    if (anyGenerating) return false;
    const COLS = 5;

    // Pack into a contiguous, gap-free grid — but GROUPED BY BATCH so two runs
    // that overlapped in time (e.g. "make 20" + "make 10 santa" fired back to
    // back) come out as separate ordered blocks instead of interleaved. Each
    // batch fills left-to-right, and the next batch starts on a fresh row below.
    // Batches with no known batchId (e.g. images restored from a prior session)
    // sort first by their current position, preserving their existing order.
    const batchOf = (im: { src: string }) => _imageBatchId.get(im.src) ?? '__prior__';
    const orderOf = (b: string) =>
      b === '__prior__' ? -1 : (_batchFirstSeen.has(b) ? (_batchFirstSeen.get(b) as number) : Number.MAX_SAFE_INTEGER);
    const groups = new Map<string, typeof real>();
    for (const im of real) {
      const b = batchOf(im);
      if (!groups.has(b)) groups.set(b, []);
      (groups.get(b) as typeof real).push(im);
    }
    const orderedBatches = [...groups.keys()].sort((a, b) => orderOf(a) - orderOf(b) || (a < b ? -1 : a > b ? 1 : 0));
    const pos = new Map<string, { x: number; y: number }>();
    // v4.79 — batches are separated by the 120px SECTION gap (rows inside a
    // batch reserve metadata clearance). The old row-cursor packed the next batch onto
    // the literal next row — 40px between sections, under the run frame's
    // 52px label band, so finished frames overlapped each other.
    // v4.80 — anchor at the EXISTING content's top-left, not (60,60): the
    // final settle used to yank the whole board to the canvas origin
    // ("everything randomly shifted left").
    // v5.07 — finite-only anchor, and ignore server spacer rows parked at
    // x=-999999 (campaign-v3 section spacing): one NaN tile NaN'd every
    // position, and a spacer dragged the whole board a million px left.
    const anchorable = real.filter((im) => Number.isFinite(im.x) && Number.isFinite(im.y) && im.x > -100_000);
    if (anchorable.length === 0) return false;
    const anchorX = Math.min(...anchorable.map((im) => im.x));
    let yCursor = Math.min(...anchorable.map((im) => im.y));
    for (const b of orderedBatches) {
      // Within a batch, keep current reading order (top-to-bottom, left-to-right).
      const imgs = (groups.get(b) as typeof real).slice().sort((p, q) => (p.y - q.y) || (p.x - q.x));
      const cell = canvasCellSize(imgs.map(im => ({ w: im.displayWidth, h: im.displayHeight })));
      const STEP_X = cell.width + IMAGE_GAP;
      const STEP_Y = cell.height + CANVAS_AD_ROW_GAP;
      imgs.forEach((im, i) => {
        pos.set(im.id, {
          x: anchorX + (i % COLS) * STEP_X,
          y: yCursor + Math.floor(i / COLS) * STEP_Y,
        });
      });
      const rows = Math.ceil(imgs.length / COLS);
      yCursor += rows * STEP_Y + (120 - CANVAS_AD_ROW_GAP); // Preserve 120px between sections.
    }
    let changed = false;
    get()._writeImages(projectId, (cur) =>
      cur.map((im) => {
        const p = pos.get(im.id);
        if (p && (Math.round(im.x) !== p.x || Math.round(im.y) !== p.y)) { changed = true; return { ...im, x: p.x, y: p.y }; }
        return im;
      }),
    );
    return changed;
  },
  packActiveBatch: (projectId, batchId) => {
    if (projectId == null || !batchId) return;
    const proj = get().projects[projectId];
    if (!proj) return;
    const imgs = proj.images
      .filter((im) => !isVirtualImage(im) && _imageBatchId.get(im.src) === batchId)
      .slice()
      .sort((a, b) => (a.y - b.y) || (a.x - b.x));
    const phs = Object.values(proj.canvasPlaceholders)
      .filter((p) => p.batchId === batchId)
      .slice()
      .sort((a, b) => (a.y - b.y) || (a.x - b.x));
    if (imgs.length + phs.length === 0) return;
    // A chat turn may contain several workflows whose concept indices each
    // restart at zero. Their reserved positions already form separate grids.
    // Repacking those duplicate indices on every arrival moves healthy tiles
    // repeatedly; only repair a real collision, never compact empty space.
    if (!hasOverlaps([
      ...imgs.map(im => ({ key: im.id, x: im.x, y: im.y, w: im.displayWidth || DEFAULT_WIDTH, h: im.displayHeight || DEFAULT_HEIGHT })),
      ...phs.map(ph => ({ key: ph.taskId, x: ph.x, y: ph.y, w: ph.width || DEFAULT_WIDTH, h: ph.height || DEFAULT_HEIGHT })),
    ])) return;
    const COLS = 5;
    const cell = canvasCellSize([...imgs.map(im => ({ w: im.displayWidth, h: im.displayHeight })), ...phs.map(p => ({ w: p.width, h: p.height }))]);
    const STEP_X = cell.width + IMAGE_GAP;
    const STEP_Y = cell.height + CANVAS_AD_ROW_GAP;
    // Anchor at the batch's existing top-left so the block never drifts.
    const originX = Math.min(...imgs.map((t) => t.x), ...phs.map((t) => t.x));
    const originY = Math.min(...imgs.map((t) => t.y), ...phs.map((t) => t.y));
    const imgPos = new Map<string, { x: number; y: number }>();
    const phPos = new Map<string, { x: number; y: number }>();
    // v4.64 — slot-aware packing: when tiles carry a fixed slot (intent boxes
    // + images that claimed them), every tile packs to ITS OWN slot, so an
    // image landing never moves any other tile — the layout the user watched
    // build IS the layout. The old "finished images first, boxes shoved to
    // the back" repack reshuffled the whole batch on every arrival.
    const imgSlot = (im: { src: string }) => _slotBySrc.get(im.src);
    const anySlots = imgs.some((im) => imgSlot(im) !== undefined) || phs.some((p) => typeof p.slotIndex === 'number');
    if (anySlots) {
      // v4.66 — anchor at the SLOT-GRID origin implied by the members' own
      // slots, not min(x,y): when slot 0 is vacant (tile deleted mid-run /
      // concept failed before its box existed) a raw-min anchor re-shifted
      // the whole batch one slot right on EVERY pack.
      // v5.07 — MEDIAN of the implied origins, not MIN: min() let a single
      // slot↔position-inconsistent member (e.g. a safety-net-recovered row
      // sitting at SERVER grid coords while stamped with its conceptIndex
      // slot) drag the anchor left/up, and each repack↔regrid cycle
      // compounded it — run 1020 walked its whole batch 12 column-strides to
      // x=-4020 with the viewport never following ("all my previews
      // disappeared"). The median tracks the consistent majority: still
      // idempotent with sparse slots, still follows a whole-section drag,
      // immune to a lone outlier.
      const slotted = [
        ...imgs.flatMap(im => { const slot = imgSlot(im); return slot === undefined ? [] : [{ x: im.x, y: im.y, slot }]; }),
        ...phs.flatMap(p => typeof p.slotIndex === 'number' ? [{ x: p.x, y: p.y, slot: p.slotIndex }] : []),
      ];
      const gridKey = `${projectId}:${batchId}`;
      const previousSteps = batchGridSteps.get(gridKey) ?? { x: DEFAULT_WIDTH + IMAGE_GAP, y: DEFAULT_HEIGHT + CANVAS_AD_ROW_GAP };
      const origin = canvasBatchOrigin(slotted, previousSteps.x, previousSteps.y);
      batchGridSteps.set(gridKey, { x: STEP_X, y: STEP_Y });
      let anchorX = origin.x;
      let anchorY = origin.y;
      if (!Number.isFinite(anchorX) || !Number.isFinite(anchorY)) { anchorX = originX; anchorY = originY; }
      if (!Number.isFinite(anchorX) || !Number.isFinite(anchorY)) return; // never write NaN positions
      const posFor = (slot: number) => ({ x: anchorX + (slot % COLS) * STEP_X, y: anchorY + Math.floor(slot / COLS) * STEP_Y });
      const usedSlots = new Set<number>();
      const unslottedImgs: typeof imgs = [];
      const unslottedPhs: typeof phs = [];
      for (const im of imgs) {
        const s = imgSlot(im);
        if (s !== undefined && !usedSlots.has(s)) { usedSlots.add(s); imgPos.set(im.id, posFor(s)); }
        else unslottedImgs.push(im);
      }
      for (const p of phs) {
        const s = typeof p.slotIndex === 'number' ? p.slotIndex : undefined;
        if (s !== undefined && !usedSlots.has(s)) { usedSlots.add(s); phPos.set(p.taskId, posFor(s)); }
        else unslottedPhs.push(p);
      }
      // Tiles with no slot (or a duplicated one) append into the next free
      // slots after the highest used one, in their current visual order.
      let next = usedSlots.size > 0 ? Math.max(...Array.from(usedSlots)) + 1 : 0;
      for (const im of unslottedImgs) { while (usedSlots.has(next)) next++; usedSlots.add(next); imgPos.set(im.id, posFor(next)); }
      for (const p of unslottedPhs) { while (usedSlots.has(next)) next++; usedSlots.add(next); phPos.set(p.taskId, posFor(next)); }
    } else {
      // Legacy batches (no slot info): finished images first (top-to-bottom,
      // left-to-right), pending placeholders after them — contiguous, no holes.
      let i = 0;
      for (const im of imgs) {
        imgPos.set(im.id, { x: originX + (i % COLS) * STEP_X, y: originY + Math.floor(i / COLS) * STEP_Y });
        i++;
      }
      for (const p of phs) {
        phPos.set(p.taskId, { x: originX + (i % COLS) * STEP_X, y: originY + Math.floor(i / COLS) * STEP_Y });
        i++;
      }
    }
    get()._writeImages(projectId, (cur) =>
      cur.map((im) => {
        const p = imgPos.get(im.id);
        return p && (Math.round(im.x) !== p.x || Math.round(im.y) !== p.y) ? { ...im, x: p.x, y: p.y } : im;
      }),
    );
    get()._writeCanvasPlaceholders(projectId, (m) => {
      let phChanged = false;
      const next = { ...m };
      phPos.forEach((p, tid) => {
        const cur = next[tid];
        if (cur && (Math.round(cur.x) !== p.x || Math.round(cur.y) !== p.y)) {
          next[tid] = { ...cur, x: p.x, y: p.y };
          phChanged = true;
        }
      });
      return phChanged ? next : m;
    });
  },

  saveImagesSnapshot: async (projectId: number) => {
    const state = get();
    if (projectId == null) return;
    const proj = state.projects[projectId];
    if (!proj) return;
    set({ isSavingProject: true });
    try {
      const payload = proj.images
        .filter((img) => !img.src.startsWith('data:image/svg'))
        .map((img) => {
          // Pull QA badge from the in-memory map (keyed by src) so the sync
          // preserves QA state for client-side-added images (variations,
          // regen, etc.) that didn't go through the playbook DB-insert path.
          const badge = proj.imageQaBadges?.[img.src];
          return {
            clientId: img.id,
            src: img.src,
            name: img.name,
            x: img.x,
            y: img.y,
            displayWidth: img.displayWidth,
            displayHeight: img.displayHeight,
            originalWidth: img.originalWidth,
            originalHeight: img.originalHeight,
            colorIndex: img.colorIndex,
            prompt: img.prompt,
            adStyle: img.adStyle,
            adAngle: img.adAngle,
            wavespeedPrompt: img.wavespeedPrompt,
            conceptName: img.conceptName,
            headline: img.headline,
            bodyCopy: img.bodyCopy,
            ctaText: img.ctaText,
            qaPassed: badge ? badge.passed : undefined,
            qaReason: badge ? badge.reason : undefined,
            // v4.47 — run/angle attribution must survive the destructive sync.
            // The server's preserve-lookup (metaByClientId/metaBySrc) misses
            // when ids/srcs diverge, which nulled workflow_run_id on whole
            // runs (965/966) and killed section boxes on reload. The client
            // knows these — send them; server pickField prefers payload.
            workflowRunId: img.workflowRunId ?? undefined,
            adAngleId: img.adAngleId ?? undefined,
            // videoUrl must survive the destructive sync the same way — a
            // stripped payload row would null the mp4 link on the DB row.
            videoUrl: img.videoUrl ?? undefined,
          };
        });
      // Guard: never sync an empty payload unless this project has previously had
      // confirmed real images.  Syncing empty would call the destructive DELETE+INSERT
      // endpoint and wipe any images that were individually saved (POST /images) by
      // addImageToCanvas during the generation window before the store was updated.
      if (payload.length === 0 && !proj.hasPersistedImages) {
        console.log('[saveImagesSnapshot] skipping empty sync — no confirmed images for this project yet');
        return;
      }
      await fetch(`/api/canvas/projects/${projectId}/images/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: payload }),
      });
    } catch (err) {
      console.warn('[CanvasStore] Failed to save images:', err);
    } finally {
      set({ isSavingProject: false });
    }
  },

  // Saves THIS project's messages to THIS project's DB row.
  // Claude-style: the projectId argument lets debounced saves write to the
  // ORIGINATING project even if the user has navigated away. Without this,
  // a fast click-away within the 200ms debounce window dropped the user's
  // message on the floor.
  saveConversationHistory: async (projectId: number) => {
    if (projectId == null) return;
    const proj = get().projects[projectId];
    if (!proj) return;
    try {
      // Persist the full messages list (not the sliced LLM context window)
      // so that switching back to this chat restores all visible messages.
      // Keep streaming messages that already have meaningful content so the AI
      // side of the conversation survives project switches during generation.
      const fullHistory = proj.messages
        // v7.43 — a hydrated `generating` concepts-run entry is streaming with
        // no content; the client's full-blob replace must NOT delete it (the
        // server's stale-write guard can only protect entries that arrive).
        .filter((m) => !m.isStreaming || m.content || m.agentTaskGroups?.length || m.workflowTaskCards?.length || m.adCards?.length || m.conceptsRun)
        .map(({ isStreaming, ...rest }) => rest);
      await fetch(`/api/canvas/projects/${projectId}/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ history: fullHistory }),
      });
    } catch (err) {
      console.warn('[CanvasStore] Failed to save conversation:', err);
    }
  },

  switchProject: async (projectId: number, meta?: { brandId?: number | null; adAccountId?: number | null; parentId?: number | null }) => {
    // Pointer change — synchronous from a UX perspective. SSE streams from
    // previous project keep running and writing to their own state.projects[id]
    // bucket via captured streamProjectId.
    const metadata = resolveProjectNavigation(meta, get().projects[projectId]);
    get()._writeProjectMetadata(projectId, metadata);
    set({ activeProjectId: projectId, activeBrandId: metadata.brandId, activeAdAccountId: metadata.adAccountId });
    if (metadata.brandId == null) {
      const canonical = await loadProjectNavigation(projectId, meta, get().projects[projectId], async id => {
        const response = await fetch(`/api/canvas/projects/${id}`);
        if (!response.ok) throw new Error('Could not load the task’s brand context. Please reopen the task.');
        return response.json();
      });
      get()._writeProjectMetadata(projectId, canonical);
      if (get().activeProjectId === projectId) set({ activeBrandId: canonical.brandId, activeAdAccountId: canonical.adAccountId });
    }

    // Already-loaded project: skip the fetch but still ensure the canvas
    // auto-opens if the project has images. This handles the "reopen chat
    // → see chat-only despite the chat having 5 ads" bug after a reload.
    const existingProj = get().projects[projectId];
    if (existingProj && existingProj.loadedAt != null && existingProj.images.length > 0 && !existingProj.isCanvasOpen) {
      get()._writeProjectView(projectId, {
        isCanvasOpen: true,
        activeView: 'chat-canvas' as const,
      });
    }

    // Lazy-load from DB if this project hasn't been loaded yet.
    const proj = get().projects[projectId];
    if (!proj || proj.loadedAt == null) {
      get()._writeProjectMetadata(projectId, { loading: true });
      try {
        const [imgResp, convResp] = await Promise.all([
          fetch(`/api/canvas/projects/${projectId}/images`),
          fetch(`/api/canvas/projects/${projectId}/conversation`),
        ]);

        // Track each fetch independently. The OLD code silently coerced a
        // non-ok image response into `images = []` then set `loadedAt =
        // Date.now()` — every subsequent navigation saw loadedAt set, the
        // guard at line ~3840 skipped the retry, and the user was stuck on
        // a blank canvas with no way to recover except a hard refresh. This
        // is the "going back to canvas chats shows blank" bug. The fix is
        // to only mark loadedAt on FULL success; partial/full failures
        // leave loadedAt = null so the next switchProject retries.
        const imagesOk = imgResp.ok;
        const convOk = convResp.ok;
        if (!imagesOk) {
          console.error(`[canvas-store] images fetch failed for project ${projectId}: HTTP ${imgResp.status} ${imgResp.statusText}`);
        }
        if (!convOk) {
          console.error(`[canvas-store] conversation fetch failed for project ${projectId}: HTTP ${convResp.status} ${convResp.statusText}`);
        }

        const imageRows: any[] = imagesOk ? await imgResp.json() : [];
        // v4.17 — same variant-link → client-id resolution as loadOrCreateProject,
        // so edited tiles keep img.sourceImageId across project switches.
        const dbIdToClientIdSwitch = new Map<number, string>();
        for (const row of imageRows) {
          const cid = row.client_id ?? row.clientId;
          if (typeof row.id === 'number' && cid) dbIdToClientIdSwitch.set(row.id, cid);
        }
        const images: CanvasImage[] = imageRows.filter((row: any) => !!(row.src ?? row.image_url)).map((row: any) => ({
          id: persistedCanvasImageId(row),
          sourceImageId: (row.variant_of_image_id ?? row.variantOfImageId) != null
            ? dbIdToClientIdSwitch.get(row.variant_of_image_id ?? row.variantOfImageId)
            : undefined,
          name: row.name,
          src: row.src ?? row.image_url,
          x: row.x, y: row.y,
          displayWidth: row.display_width ?? row.displayWidth,
          displayHeight: row.display_height ?? row.displayHeight,
          originalWidth: row.original_width ?? row.originalWidth,
          originalHeight: row.original_height ?? row.originalHeight,
          colorIndex: row.color_index ?? row.colorIndex ?? 0,
          prompt: row.prompt,
          adStyle: row.ad_style ?? row.adStyle,
          adAngle: row.ad_angle ?? row.adAngle,
          wavespeedPrompt: row.wavespeed_prompt ?? row.wavespeedPrompt,
          conceptName: row.concept_name ?? row.conceptName,
          headline: row.headline,
          bodyCopy: row.body_copy ?? row.bodyCopy,
          ctaText: row.cta_text ?? row.ctaText,
          adAngleId: row.ad_angle_id ?? row.adAngleId ?? null,
          workflowRunId: row.workflow_run_id ?? row.workflowRunId ?? null,
          videoUrl: row.video_url ?? row.videoUrl ?? null,
          // Persisted QA verdict — ImageNode's badge falls back to these
          // when no live SSE badge exists (full-funnel builds stream to the
          // Ad Angles panel, so tiles ONLY ever get the persisted verdict).
          qaPassed: row.qa_passed ?? row.qaPassed ?? null,
          qaReason: row.qa_reason ?? row.qaReason ?? null,
        }));
        const conv = convOk ? await convResp.json() : null;
        const history: Array<Record<string, unknown>> = Array.isArray(conv?.history) ? conv.history : [];
        // v7.43 — shared pure hydration (see conversation-hydrate.ts). This
        // path used to carry its own OLDER copy of the display filter that
        // still put the "(Previous turn didn't complete)" marker on the
        // mid-history tool_calls-only turns of SUCCESSFUL runs; both call
        // sites now follow the same v7.31 + concepts-run rules.
        const messages: CanvasChatMessage[] = hydrateConversation(history);
        const conversationHistory = toLlmHistory(history);

        // Write whichever side succeeded — partial success is still useful
        // (e.g. chat history loads, images failed → user sees the chat and
        // knows to retry). The next switchProject will refetch both because
        // loadedAt stays null.
        if (imagesOk) {
          get()._writeImages(projectId, () => images);
        }
        if (convOk) {
          // v4.47 — restore run cards lost to a mid-run disconnect (sleep/close).
          const withRunCards = await synthesizeRunCards(projectId, messages, imagesOk ? images : []);
          get()._writeMessages(projectId, () => withRunCards);
          get()._writeConversationHistory(projectId, conversationHistory);
          // v7.43 — concepts runs still `generating` in the saved blob: poll
          // until the server resolves the entry (or the set lands).
          for (const m of withRunCards) {
            if (m.conceptsRun?.status === 'generating') {
              get().watchAngleSetRun(projectId, m.id, m.conceptsRun.setId ?? null).catch(() => {});
            }
          }
        }

        const fullySucceeded = imagesOk && convOk;
        get()._writeProjectMetadata(projectId, {
          loading: false,
          // KEY FIX: only mark loaded when both halves succeeded. Partial
          // failure leaves loadedAt=null so the next nav retries.
          loadedAt: fullySucceeded ? Date.now() : null,
        });

        // v4.50 — reopened a project whose run is still generating: keep
        // pulling its ads from the DB until the run completes.
        get().watchRunningRun(projectId).catch(() => {});

        // Auto-open the canvas when reopening a chat that already has
        // generated images. Without this, a chat with 5 saved ads opens in
        // chat-only mode after a reload or fresh session, hiding the work
        // the user already produced.
        if (imagesOk && images.length > 0) {
          get()._writeProjectView(projectId, {
            isCanvasOpen: true,
            activeView: 'chat-canvas' as const,
          });
        }
      } catch (err) {
        console.warn("[canvas-store] switchProject load failed:", err);
        get()._writeProjectMetadata(projectId, { loading: false });
      }
    }
  },

  reloadProject: async (projectId: number) => {
    // Forces a fresh fetch even when loadedAt is set. Used by the canvas
    // chrome "reload" button when the user wants to pull server-side
    // changes that happened after they navigated away (e.g. images that
    // finished generating after an SSE disconnect — see v1.37 SSE ceiling
    // notes; on long runs the stream closes but the backend keeps
    // producing images that only appear on a refresh).
    get()._writeProjectMetadata(projectId, { loadedAt: null });
    await get().switchProject(projectId);
  },

  refreshProjectImages: async (projectId: number) => {
    // Live-build tile refresh. Mirrors switchProject's row→CanvasImage
    // mapping but swaps images in ONE atomic write with no loading state —
    // reloadProject's loadedAt=null + refetch blanked the canvas for the
    // whole round-trip, which on a slow link (VPN) made mid-build tiles
    // visibly "disappear" between polls.
    try {
      const resp = await fetch(`/api/canvas/projects/${projectId}/images`);
      if (!resp.ok) return;
      const images: CanvasImage[] = (await resp.json()).map((row: any) => ({
        id: persistedCanvasImageId(row),
        name: row.name,
        src: row.src ?? row.image_url,
        x: row.x, y: row.y,
        displayWidth: row.display_width ?? row.displayWidth,
        displayHeight: row.display_height ?? row.displayHeight,
        originalWidth: row.original_width ?? row.originalWidth,
        originalHeight: row.original_height ?? row.originalHeight,
        colorIndex: row.color_index ?? row.colorIndex ?? 0,
        prompt: row.prompt,
        adStyle: row.ad_style ?? row.adStyle,
        adAngle: row.ad_angle ?? row.adAngle,
        wavespeedPrompt: row.wavespeed_prompt ?? row.wavespeedPrompt,
        conceptName: row.concept_name ?? row.conceptName,
        headline: row.headline,
        bodyCopy: row.body_copy ?? row.bodyCopy,
        ctaText: row.cta_text ?? row.ctaText,
        adAngleId: row.ad_angle_id ?? row.adAngleId ?? null,
        workflowRunId: row.workflow_run_id ?? row.workflowRunId ?? null,
        videoUrl: row.video_url ?? row.videoUrl ?? null,
        qaPassed: row.qa_passed ?? row.qaPassed ?? null,
        qaReason: row.qa_reason ?? row.qaReason ?? null,
      }));
      // Reserved rows have no image yet. They belong in the workflow-aware
      // placeholder bucket, never in images (where an empty src means an
      // immortal "Generating" tile, including after the run has completed).
      get()._writeImages(projectId, () => images.filter(image => !!image.src));
      const runResp = await fetch(`/api/workflows?projectId=${projectId}`);
      if (runResp.ok) {
        const runs = await runResp.json();
        if (Array.isArray(runs)) {
          get()._writeCanvasPlaceholders(projectId, current => recoverRunPlaceholders(runs, images, current));
        }
      }
    } catch {
      // best-effort — the next poll or a full reload catches up
    }
  },

  createNewProject: async (name: string, color?: string, parentId?: number | null) => {
    // Persist current project state before creating new — scoped to its own id
    // so the save always lands in the correct DB row (not the about-to-be-replaced active one).
    const currentActiveId = get().activeProjectId;
    if (currentActiveId != null) {
      await Promise.all([
        get().saveImagesSnapshot(currentActiveId),
        get().saveConversationHistory(currentActiveId),
      ]).catch(() => {});
    }

    const resp = await fetch('/api/canvas/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, color: color || '#6366F1', parentId: parentId ?? null }),
    });
    if (!resp.ok) return;
    const project = await resp.json();
    const isFolderItem = project.is_folder === true || project.isFolder === true;
    const isTask = !isFolderItem;
    const newProjectId: number | null = isTask ? project.id : null;
    // Dual-write the empty conversation and images to the new project slot (if it's a task)
    get()._writeMessages(newProjectId, () => []);
    get()._writeConversationHistory(newProjectId, []);
    get()._writeImages(newProjectId, () => []);
    set((s) => {
      const { new: _removed, ...restTyping } = s.typingByProject as Record<string | number, boolean>;
      return {
        activeProjectId: newProjectId,
        folderProjectId: isTask ? (project.parentId ?? null) : project.id,
        activeTaskId: isTask ? project.id : null,
        activeBrandId: null,
        activeAdAccountId: null,
        typingByProject: restTyping,
      };
    });
    // Dual-write view + metadata to per-project state for new task projects
    if (isTask && newProjectId !== null) {
      get()._writeProjectView(newProjectId, { isCanvasOpen: false, activeView: 'chat-only' as const });
      get()._writeProjectMetadata(newProjectId, {
        brandId: null,
        adAccountId: null,
        parentId: project.parentId ?? null,
      });
    }
  },

  sendAgentMessage: async (content: string, preMsgId?: string, extraContext?: { sourceSuggestion?: {messageId:string;value:string}; creativeMode?: "brand-led" | "inspire"; displayContent?: string; followUpContext?: CanvasFollowUpOrigin; productId?: number; composerReferences?: ComposerReferenceSnapshot; targetFormats?: string[]; silent?: boolean; forcedTool?: { name: string; arguments: Record<string, unknown> } }) => {
    const state = get();
    if (extraContext?.followUpContext && extraContext.followUpContext.projectId !== state.activeProjectId) return;
    const projectKey: string | number = state.activeProjectId ?? 'new';
    // `silent` is set by canvas UI actions (e.g. the per-image Regenerate
    // button) that want the agent to do its work WITHOUT leaving a user-typed
    // bubble + assistant-thinking bubble in the chat transcript. SSE events
    // still flow through normally — placeholder + image land on the canvas as
    // usual; only the chat artifacts are suppressed.
    const silent = extraContext?.silent === true;
    // Capture projectId at stream start. ALL SSE event handlers in this stream
    // MUST route mutations through this captured value, NOT via
    // get().activeProjectId (which can change if user clicks another chat).
    const streamProjectId: number | null = state.activeProjectId;

    // --- Stop/cancel intent while a playbook is running ---
    if (isStopIntent(content)) {
      // 1. Signal the server to cancel the workflow run (cooperative cancellation).
      //    The playbookExecutor checks `workflowRun.status === "cancelled"` between tiers.
      const stopNumericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
      const runIdToCancel = stopNumericKey != null ? (get().projects[stopNumericKey]?.activeWorkflowRunId ?? null) : null;
      if (runIdToCancel !== null) {
        // Fire-and-forget — we abort the SSE stream immediately regardless of the HTTP response.
        fetch(`/api/workflows/${runIdToCancel}/cancel`, { method: 'POST' }).catch(() => {});
        get()._writeActiveWorkflowRunId(stopNumericKey, null);
      }

      const stopUserMsg: CanvasChatMessage = { id: `m-${Date.now()}`, role: 'user', content };
      const stopAckMsg: CanvasChatMessage = {
        id: `m-stop-${Date.now() + 1}`,
        role: 'assistant',
        content: 'Got it — generation stopped.',
        isStreaming: false,
        isAgentMessage: true,
      };
      get()._writeMessages(streamProjectId, (msgs) => [
        ...msgs.map((m) => m.isStreaming ? { ...m, isStreaming: false, agentThinking: undefined } : m),
        stopUserMsg,
        stopAckMsg,
      ]);
      set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
      return;
    }

    // If this message was pre-inserted into chat while queued, skip re-inserting it.
    const isPreInserted = preMsgId !== undefined && _preInsertedMsgIds.has(preMsgId);
    if (isPreInserted) _preInsertedMsgIds.delete(preMsgId);

    // Compute reference URLs BEFORE creating the user message so we can set thumbnails on it.
    const stateProjForRefs = activeProj(state);
    const submittedSelection = extraContext?.followUpContext?.imageIds ?? extraContext?.composerReferences?.selectedImageIds ?? state.selectedImageIds;
    const selectedReferenceUrls = extraContext?.followUpContext ? [...extraContext.followUpContext.imageUrls] : submittedSelection
      .map((id) => stateProjForRefs.images.find((img) => img.id === id)?.src)
      .filter((src): src is string => Boolean(src) && src.startsWith('http'));

    // Also pull any inline [Attached image: <url>] out of the content string
    // (these come from file-upload attachments added by canvas-chat).
    const inlineAttachUrls: string[] = [];
    const attachPattern = /\[Attached image:\s*(https?:\/\/[^\]]+)\]/gi;
    let attachMatch: RegExpExecArray | null;
    while ((attachMatch = attachPattern.exec(content)) !== null) {
      if (attachMatch[1]) inlineAttachUrls.push(attachMatch[1].trim());
    }

    const allThumbnails = [...new Set([...inlineAttachUrls, ...selectedReferenceUrls])];

    const userMsg: CanvasChatMessage = {
      id: preMsgId ?? `m-${Date.now()}`,
      role: 'user',
      content,
      thumbnails: allThumbnails.length > 0 ? allThumbnails : undefined,
      creativeMode: extraContext?.creativeMode ?? ((state.activeProjectId != null ? state.inspireByProject[state.activeProjectId] ?? false : state.inspire) ? "inspire" : "brand-led"),
      displayContent: extraContext?.displayContent,
      followUpContext: extraContext?.followUpContext,
    };

    const assistantMsgId = `m-agent-${Date.now() + 1}`;

    // Optimistically open canvas if message looks like ad creation
    const looksLikeAdCreation = /\b(make|create|generate|build|whip|give me|show me)\b/i.test(content) &&
      (/\b(ads?|creatives?|images?|stuff|content)\b/i.test(content) || /\b\d+\b/.test(content));

    // Instant optimistic reasoning placeholder — shown IMMEDIATELY on submit so the
    // user sees a typing thought process right away instead of 4-5s of dead air while
    // the classifier runs. Once the real `agent_reasoning` SSE event arrives, this
    // value is replaced with the real classifier text (and the TypewriterText in the
    // chat view seamlessly continues typing into it).
    const optimisticReasoning = looksLikeAdCreation
      ? "Let me think about what you're looking for…"
      : 'Reading your message…';

    const assistantMsg: CanvasChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      isAgentMessage: true,
      agentCards: [],
      timestamp: Date.now() + 1,
      aspectRatio: get().selectedAspectRatio,
      classifierReasoning: optimisticReasoning,
    };

    // If the user message was pre-inserted (queued), only append the assistant bubble.
    // In silent mode (e.g. Regenerate button), insert NEITHER — the action is
    // surfaced by the canvas image spinner + the resulting image landing in
    // place, not by chat noise.
    if (!silent) {
      get()._writeMessages(streamProjectId, (msgs) =>
        isPreInserted ? [...msgs, assistantMsg] : [...msgs, userMsg, assistantMsg]
      );
    }
    if (looksLikeAdCreation && get().activeBrandId) {
      get()._writeProjectView(streamProjectId, { isCanvasOpen: true, activeView: 'chat-canvas' as const });
    }
    set((s) => ({
      // Silent actions don't show a "typing…" indicator in chat — the user
      // didn't send a message, so the chat shouldn't appear to be responding.
      typingByProject: silent ? s.typingByProject : { ...s.typingByProject, [projectKey]: true },
      followUpChips: [],
      anglePickerStages: null,
      sseError: false,
      sseConnected: true,
    }));

    // Persist the user message IMMEDIATELY (no debounce) so even a 100ms
    // click-away can't lose it. Claude-style: messages save at all costs.
    // This is fire-and-forget — the SSE stream below proceeds regardless.
    // Skip in silent mode — nothing was added to the transcript.
    if (streamProjectId != null && !silent) {
      get().saveConversationHistory(streamProjectId).catch(() => {});
    }

    // Clear orphaned UI only. A live provider request can legitimately exceed
    // eight minutes; normal stream completion/error owns its terminal state.
    const staleThinkingTimer = scheduleRequestWatchdog(
      () => _activeRequestByProject.has(projectKey),
      () => get().clearStaleTypingForProject(projectKey),
    );

    const abortController = new AbortController();
    // Track this request per-project so clearStaleTypingForProject can detect live requests.
    _activeRequestByProject.set(projectKey, abortController);

    // v7.25 — drop resilience: prod SSE connections DO drop mid-run (the
    // v3.07 keepalive reduced but never eliminated it), and the concepts→
    // build flow holds one stream open for 4-6 minutes — Dan's Sep 1 runs
    // all finished server-side, yet a late drop threw and the catch REPLACED
    // the finished turn with "Something went wrong". Track how far the
    // stream really got so the catch can tell a dead request from a dropped
    // wire. Declared OUTSIDE the try so the catch can read them.
    let sawAgentDone = false;
    let sawGenerationProgress = false;

    try {

      console.log('[canvas-chat] sending reference_images:', selectedReferenceUrls);

      // Silent actions (Regenerate / per-image Apply) don't need
      // conversation history — the agent only needs the current message
      // + the reference image. Sending the full history when it has
      // accumulated many prior image_url blocks bloats the Claude
      // request and trips upstream 500s. Send an empty history in
      // silent mode; the agent sees the fresh request and tool-calls
      // cleanly.
      const historyForRequest = silent ? [] : stateProjForRefs.conversationHistory;

      const response = await fetch('/api/canvas/agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
        body: JSON.stringify({
          message: content,
          sourceSuggestion: extraContext?.sourceSuggestion,
          creativeMode: extraContext?.creativeMode ?? ((state.activeProjectId != null ? state.inspireByProject[state.activeProjectId] ?? false : state.inspire) ? "inspire" : "brand-led"),

          projectId: state.activeProjectId,
          brandId: state.activeBrandId,
          adAccountId: state.activeAdAccountId,
          conversationHistory: historyForRequest,
          // v7.43 — the id of the assistant bubble this stream fills. The
          // server keys its durable concepts-run conversation entry on it so
          // a reopened task hydrates the SAME message (progress → result)
          // instead of a blank/crashed turn.
          clientMessageId: assistantMsgId,
          displayContent: extraContext?.displayContent,
          followUpContext: extraContext?.followUpContext,
          productId: extraContext?.productId ?? extraContext?.followUpContext?.productId,
          // v7.35 — in video mode the ratio comes from the VIDEO dial (its API
          // set has 21:9 and no 4:5; multi-ratio is an image-run concept).
          ...(() => {
            const medium = extraContext?.forcedTool?.name === 'run_angles_to_campaign' ? 'image' : state.activeProjectId != null
              ? (state.generationMediumByProject[state.activeProjectId] ?? 'image')
              : state.generationMedium;
            if (medium === 'video') {
              const dials = state.activeProjectId != null ? state.videoDialsByProject[state.activeProjectId] : undefined;
              const vr = dials?.aspectRatio ?? state.videoAspectRatio;
              return { selectedAspectRatio: vr, selectedAspectRatios: [vr] };
            }
            return { selectedAspectRatio: state.selectedAspectRatio, selectedAspectRatios: state.selectedAspectRatios };
          })(),
          // v6.22 — per-task medium: a task keeps the mode it was created
          // with; tasks without an entry (all pre-existing ones) are image.
          // The bare global is only used before the task row exists.
          generationMedium: extraContext?.forcedTool?.name === 'run_angles_to_campaign' ? 'image' : state.activeProjectId != null
            ? (state.generationMediumByProject[state.activeProjectId] ?? 'image')
            : state.generationMedium,
          // v7.16 — composer image-model dropdown. Per-task stamp wins; tasks
          // without one follow the current global selection (so the dropdown
          // still works mid-chat on pre-existing tasks).
          ...(() => {
            const selection = canvasImageSelection(
              state.activeProjectId != null ? state.imageModelByProject[state.activeProjectId] ?? state.imageModel : state.imageModel,
              state.activeProjectId != null ? state.imageTierByProject[state.activeProjectId] ?? state.imageTier : state.imageTier,
              state.activeProjectId != null ? state.imageResolutionByProject[state.activeProjectId] : state.imageResolution,
            );
            const medium = state.activeProjectId != null ? state.generationMediumByProject[state.activeProjectId] ?? 'image' : state.generationMedium;
            return {imageModel:medium === 'video' && selection.model === 'gpt-image-2.5-sunburst' ? 'gpt-image-2' : selection.model,imageTier:selection.tier,imageResolution:selection.resolution};
          })(),
          // v7.37 — composer Advanced angle/style filter. Only the axes the
          // user narrowed are sent; an absent axis reads as All server-side.
          ...(() => {
            const f = state.activeProjectId != null
              ? (state.adFilterByProject[state.activeProjectId] ?? state.adFilter)
              : state.adFilter;
            return {
              ...(f?.styles?.length ? { adStyles: f.styles } : {}),
              ...(f?.angles?.length ? { adAngles: f.angles } : {}),
            };
          })(),
          reference_images: selectedReferenceUrls.length > 0 ? selectedReferenceUrls : undefined,
          // v7.35 — video composer dials. Per-task stamp wins (a task keeps
          // what it was created with, switchable mid-chat via the pickers
          // which update the stamp); server re-validates every value. Only
          // meaningful when generationMedium === 'video' — the server's
          // videoModelScope ignores them otherwise.
          ...(() => {
            const dials = state.activeProjectId != null ? state.videoDialsByProject[state.activeProjectId] : undefined;
            return {
              videoModel: dials?.model ?? state.videoModel,
              videoVariant: dials?.variant ?? state.videoVariant,
              videoResolution: dials?.resolution ?? state.videoResolution,
              videoDuration: dials?.duration ?? state.videoDuration,
              videoAudio: dials?.audio ?? state.videoAudio,
            };
          })(),
          ...(state.videoRefs.length > 0 ? { referenceVideos: state.videoRefs.map((r) => ({ url: r.url, durationSec: r.durationSec })) } : {}),
          ...(extraContext?.targetFormats ? { targetFormats: extraContext.targetFormats } : {}),
          // v2.25 — forced-tool dispatch (comment/mark Apply flow). The route
          // validates name/shape and runAgent executes the tool directly,
          // skipping Sonnet routing. Only "run_edit_fixes" is accepted.
          ...(extraContext?.forcedTool ? { forcedTool: extraContext.forcedTool } : {}),
        }),
      });

      // v7.35 — video-ref attachments are message-scoped: once the request is
      // on the wire they're consumed (success or failure — the chips clear so
      // a retry doesn't silently re-bill combined ref-video seconds).
      if (state.videoRefs.length > 0) get().clearVideoRefs();

      if (!response.ok) {
        // Credits gate: 402 CREDITS_EXHAUSTED → raise the modal AND throw a
        // typed error so the catch block can show a credit-specific message
        // instead of the generic "Something went wrong".
        if (response.status === 402) {
          try {
            const body = await response.clone().json();
            if (body?.code === 'CREDITS_EXHAUSTED') {
              triggerCreditsGate(Number(body.balance ?? 0), Number(body.needed ?? 0));
              const err = new Error('CREDITS_EXHAUSTED') as Error & { code?: string; balance?: number; needed?: number };
              err.code = 'CREDITS_EXHAUSTED';
              err.balance = Number(body.balance ?? 0);
              err.needed = Number(body.needed ?? 0);
              throw err;
            }
          } catch (parseErr) {
            // If parsing or the rethrow above propagates, fall through to generic.
            if ((parseErr as Error)?.message === 'CREDITS_EXHAUSTED') throw parseErr;
          }
        }
        throw new Error(`HTTP ${response.status}`);
      }

      if (!response.body) throw new Error('No response stream');
      const reader = response.body.getReader();
      if (streamProjectId != null) setPlaybookStreamActive(streamProjectId, assistantMsgId, true);
      // The outbound request and message thumbnails already own the snapshot.
      // Clear only accepted composer references; HTTP/network failures retain
      // them, and changes made while waiting belong to the next submission.
      if (extraContext?.composerReferences) {
        const submitted = extraContext.composerReferences;
        set(current => consumeComposerReferences(current, submitted, streamProjectId));
      }
      const decoder = new TextDecoder();
      let sseBuffer = '';
      let fullText = '';
      // Track imageSlot index per agent-image task so shimmer → real-image works in chat
      let agentNextSlotIndex = 0;
      const agentTaskToSlot = new Map<string, number>();
      // Batch position tracker: keyed by assistantMsgId so all pending events for the
      // same turn share the same rowY and get consecutive x positions (horizontal row).
      const batchRowTracker = new Map<string, { rowY: number; rowHeight: number; index: number }>();
      // v2.06 — srcs that arrived in THIS stream; drives batch-end compaction.
      const batchSrcs = new Set<string>();

      const updateMsg = (updater: (m: CanvasChatMessage) => CanvasChatMessage) => {
        get()._writeMessages(streamProjectId, (msgs) =>
          msgs.map((m) => m.id === assistantMsgId ? updater(m) : m)
        );
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const blocks = sseBuffer.split('\n\n');
        sseBuffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;
          const event = parseSSEBlock(block);
          if (!event) continue;

          if (
            event.type === 'playbook_started' || event.type === 'playbook_start' ||
            event.type === 'canvas_image_pending' || event.type === 'canvas_image_generating' ||
            event.type === 'canvas_image_ready' || event.type === 'angle_set_card'
          ) {
            sawGenerationProgress = true;
          }
          switch (event.type) {
            case 'playbook_queue': {
              if (streamProjectId != null) {
                const queued = event.data;
                get()._writeMessages(streamProjectId, messages => mergePlaybookJobs(messages, [{
                  ...queued,
                  id: queued.jobId ?? queued.id,
                  projectId: queued.projectId ?? streamProjectId,
                  assistantMessageId: queued.assistantMessageId ?? assistantMsgId,
                }], streamProjectId));
              }
              break;
            }
            case 'agent_thinking': {
              const thinkingText = event.data.text || 'Thinking…';
              updateMsg((m) => {
                // If we already have active task groups (i.e., between tool calls),
                // add a transient thinking step so the UI shows it as a first-class step card
                if (m.agentTaskGroups && m.agentTaskGroups.length > 0) {
                  const lastGroupIdx = m.agentTaskGroups.length - 1;
                  const lastGroup = m.agentTaskGroups[lastGroupIdx];
                  // Only add if the last step is NOT a running real tool step
                  const lastStep = lastGroup.steps[lastGroup.steps.length - 1];
                  const lastStepIsRealToolRunning = lastStep && lastStep.toolName !== '__thinking__' && lastStep.status === 'running';
                  if (!lastStepIsRealToolRunning) {
                    const thinkingStepId = `thinking-${Date.now()}`;
                    const thinkingStep: AgentTaskStep = {
                      id: thinkingStepId,
                      toolName: '__thinking__',
                      friendlyLabel: thinkingText,
                      status: 'running',
                    };
                    const updatedGroups = m.agentTaskGroups.map((g, i) =>
                      i === lastGroupIdx
                        ? {
                            ...g,
                            steps: [
                              ...g.steps.filter((s) => s.toolName !== '__thinking__'),
                              thinkingStep,
                            ],
                          }
                        : g
                    );
                    return { ...m, agentThinking: thinkingText, agentTaskGroups: updatedGroups };
                  }
                }
                return { ...m, agentThinking: thinkingText };
              });
              // Also update the project-scoped agentThinking convenience field
              get()._writeAgentThinking(state.activeProjectId, thinkingText);
              break;
            }
            case 'agent_task_start': {
              const newGroup: AgentTaskGroup = {
                id: event.data.taskGroupId,
                label: event.data.label || 'Working…',
                status: 'active',
                steps: [],
                iterationIndex: event.data.iterationIndex ?? 0,
              };
              updateMsg((m) => ({
                ...m,
                agentThinking: event.data.label || 'Working…',
                conceptsRun: event.data.conceptsRun ?? m.conceptsRun,
                agentTaskGroups: [
                  ...(m.agentTaskGroups || []).map((g) =>
                    g.status === 'active' ? { ...g, status: 'completed' as const } : g
                  ),
                  newGroup,
                ],
              }));
              break;
            }
            case 'concept_draft_progress': {
              updateMsg((m) => ({ ...m, conceptDraftProgress: event.data }));
              break;
            }
            case 'agent_step': {
              const newStep: AgentTaskStep = {
                id: event.data.stepId,
                toolName: event.data.toolName,
                friendlyLabel: event.data.friendlyLabel || event.data.toolName,
                status: 'running',
                contextDetail: event.data.detail || undefined,
              };
              updateMsg((m) => ({
                ...m,
                agentThinking: event.data.friendlyLabel || 'Working…',
                agentTaskGroups: (m.agentTaskGroups || []).map((g) =>
                  g.id === event.data.taskGroupId
                    ? {
                        ...g,
                        // Remove any transient thinking step, then add the real tool step
                        steps: [
                          ...g.steps.filter((s) => s.toolName !== '__thinking__'),
                          newStep,
                        ],
                      }
                    : g
                ),
              }));
              break;
            }
            case 'agent_step_result': {
              if (event.data.success === true && ['update_identity', 'update_competitor'].includes(event.data.toolName)) {
                void queryClient.invalidateQueries({predicate: query => /^\/api\/(?:brands|products|competitors|competitor-)/.test(String(query.queryKey[0] ?? ''))});
              }
              updateMsg((m) => ({
                ...m,
                agentTaskGroups: (m.agentTaskGroups || []).map((g) =>
                  g.id === event.data.taskGroupId
                    ? {
                        ...g,
                        steps: g.steps.map((s) =>
                          s.id === event.data.stepId
                            ? {
                                ...s,
                                status: event.data.success ? 'success' as const : 'error' as const,
                                summary: event.data.summary,
                                detail: event.data.detail,
                                durationMs: typeof event.data.durationMs === 'number' ? event.data.durationMs : undefined,
                              }
                            : s
                        ),
                      }
                    : g
                ),
              }));
              break;
            }
            case 'agent_step_progress': {
              const { stepId: progressStepId, message: progressMsg } = event.data as { stepId: string; message: string };
              if (progressStepId && progressMsg) {
                updateMsg((m) => ({
                  ...m,
                  agentTaskGroups: (m.agentTaskGroups || []).map((g) => ({
                    ...g,
                    steps: g.steps.map((s) =>
                      s.id === progressStepId
                        ? { ...s, contextDetail: progressMsg }
                        : s
                    ),
                  })),
                }));
              }
              break;
            }
            case 'workflow_phase_progress': {
              const { phaseId, name: phaseName, status: workerStatus, durationMs: workerDuration } = event.data as {
                phaseId: string;
                name: string;
                status: 'started' | 'completed' | 'failed';
                durationMs?: number;
              };
              updateMsg((m) => {
                const existing = m.workerProgress || [];
                if (workerStatus === 'started') {
                  const entry: WorkerProgressEntry = { id: phaseId, name: phaseName, status: 'running' };
                  const idx = existing.findIndex((e) => e.id === phaseId);
                  const updated = idx >= 0
                    ? existing.map((e, i) => (i === idx ? entry : e))
                    : [...existing, entry];
                  return { ...m, workerProgress: updated };
                } else {
                  const updated = existing.map((e) =>
                    e.id === phaseId
                      ? { ...e, name: phaseName || e.name, status: workerStatus === 'completed' ? 'completed' as const : 'failed' as const, durationMs: workerDuration }
                      : e
                  );
                  // If entry wasn't created by a 'started' event, upsert it now
                  if (!updated.some((e) => e.id === phaseId)) {
                    return {
                      ...m,
                      workerProgress: [...updated, { id: phaseId, name: phaseName, status: workerStatus === 'completed' ? 'completed' as const : 'failed' as const, durationMs: workerDuration }],
                    };
                  }
                  return { ...m, workerProgress: updated };
                }
              });
              break;
            }
            case 'plan_update': {
              const pd = event.data as { planId?: string; title?: string; steps?: Array<{ id: string; label: string; status: 'pending' | 'running' | 'completed' | 'failed' }> };
              if (pd.planId && pd.steps) {
                updateMsg((m) => ({
                  ...m,
                  agentPlan: {
                    ...m.agentPlan,
                    id: pd.planId!,
                    // Preserve existing title if the update doesn't include one
                    title: pd.title ?? m.agentPlan?.title,
                    steps: pd.steps!,
                  },
                }));
              }
              break;
            }
            case 'agent_task_complete': {
              updateMsg((m) => ({
                ...m,
                conceptsRun: event.data.conceptsRun ?? m.conceptsRun,
                agentTaskGroups: (m.agentTaskGroups || []).map((g) =>
                  g.id === event.data.taskGroupId
                    ? { ...g, status: event.data.success ? 'completed' as const : 'failed' as const }
                    : g
                ),
              }));
              break;
            }
            case 'agent_document': {
              const doc: AgentDocument = {
                title: event.data.title || 'Research Report',
                content: event.data.content || '',
                summary: event.data.summary || '',
              };
              updateMsg((m) => ({
                ...m,
                agentDocument: doc,
              }));
              break;
            }
            case 'agent_tool_start': {
              const card: AgentActionCard = {
                id: event.data.toolCallId || `card-${Date.now()}`,
                toolName: event.data.toolName,
                friendlyLabel: event.data.friendlyLabel || event.data.toolName,
                status: 'running',
              };
              updateMsg((m) => ({
                ...m,
                agentThinking: event.data.friendlyLabel || 'Working…',
                agentCards: [...(m.agentCards || []), card],
              }));
              break;
            }
            case 'agent_tool_result': {
              updateMsg((m) => ({
                ...m,
                agentCards: (m.agentCards || []).map((c) =>
                  c.id === event.data.toolCallId
                    ? { ...c, status: 'success' as const, summary: event.data.summary }
                    : c
                ),
              }));
              if (event.data.toolName === 'create_brand' && event.data.success !== false) {
                queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
              }
              if (event.data.toolData && typeof event.data.toolData === 'object') {
                const toolData = event.data.toolData as Record<string, unknown>;
                const projectKey2 = state.activeProjectId ?? 'new';
                const adEntries = extractAdViewEntries(toolData);
                if (adEntries.length > 0) {
                  set((s) => {
                    const existing = s.adDataCacheByProject[projectKey2] || {};
                    const newEntries: Record<string, AdViewData> = {};
                    for (const entry of adEntries) {
                      newEntries[entry.id] = entry;
                      if (entry.name && entry.name !== entry.id) {
                        newEntries[entry.name] = entry;
                      }
                    }
                    return {
                      adDataCacheByProject: {
                        ...s.adDataCacheByProject,
                        [projectKey2]: { ...existing, ...newEntries },
                      },
                    };
                  });
                }
              }
              break;
            }
            case 'agent_tool_error': {
              updateMsg((m) => ({
                ...m,
                agentCards: (m.agentCards || []).map((c) =>
                  c.id === event.data.toolCallId
                    ? { ...c, status: 'error' as const, error: event.data.error }
                    : c
                ),
              }));
              break;
            }
            case 'agent_text': {
              fullText += event.data.text || '';
              const streamDisplay = fullText.replace(/\[SIDEBAR_OPEN:\s*\{[\s\S]*?\}\s*\]/g, '').trim();
              updateMsg((m) => {
                // Don't overwrite content if we already have a document summary
                if (m.agentDocument) return m;
                return { ...m, content: streamDisplay, agentThinking: undefined };
              });
              break;
            }
            case 'agent_done': {
              sawAgentDone = true;
              const rawFinalText = event.data.text || fullText;
              let sidebarDoc: { title: string; content: string } | undefined;
              let finalTextCleaned = rawFinalText;
              // Use balanced-brace extraction to correctly handle nested JSON in SIDEBAR_OPEN tags
              const sidebarPrefix = '[SIDEBAR_OPEN:';
              const sidebarTagIdx = rawFinalText.indexOf(sidebarPrefix);
              if (sidebarTagIdx !== -1) {
                const jsonStart = sidebarTagIdx + sidebarPrefix.length;
                let jsonIdx = jsonStart;
                while (jsonIdx < rawFinalText.length && rawFinalText[jsonIdx] === ' ') jsonIdx++;
                if (rawFinalText[jsonIdx] === '{') {
                  let depth = 0, inStr = false, esc = false, end = -1;
                  for (let i = jsonIdx; i < rawFinalText.length; i++) {
                    const ch = rawFinalText[i];
                    if (esc) { esc = false; continue; }
                    if (ch === '\\' && inStr) { esc = true; continue; }
                    if (ch === '"') { inStr = !inStr; continue; }
                    if (inStr) continue;
                    if (ch === '{') depth++;
                    else if (ch === '}') { depth--; if (depth === 0) { end = i; break; } }
                  }
                  if (end !== -1) {
                    const jsonStr = rawFinalText.slice(jsonIdx, end + 1);
                    let closingIdx = end + 1;
                    while (closingIdx < rawFinalText.length && rawFinalText[closingIdx] === ' ') closingIdx++;
                    if (rawFinalText[closingIdx] === ']') {
                      try {
                        const parsed = JSON.parse(jsonStr);
                        if (parsed && typeof parsed.title === 'string' && typeof parsed.content === 'string') {
                          sidebarDoc = { title: parsed.title, content: parsed.content };
                        }
                      } catch {}
                      finalTextCleaned = (rawFinalText.slice(0, sidebarTagIdx) + rawFinalText.slice(closingIdx + 1)).trim();
                    }
                  }
                }
              }
              updateMsg((m) => {
                // If an agent_document was received, replace the streamed report with just its summary
                const doc = m.agentDocument;
                const displayContent = doc
                  ? (doc.summary || '').trim()
                  : (finalTextCleaned || m.content);
                return {
                  ...m,
                  content: displayContent,
                  isStreaming: false,
                  agentThinking: undefined,
                  agentTaskGroups: (m.agentTaskGroups || []).map((g) =>
                    g.status === 'active' ? { ...g, status: 'completed' as const } : g
                  ),
                  ...(sidebarDoc ? { sidebarDoc } : {}),
                };
              });
              const newAgentHistory = [
                ...activeProj(get()).conversationHistory,
                { role: 'user' as const, content },
                { role: 'assistant' as const, content: finalTextCleaned || fullText },
              ];
              get()._writeConversationHistory(streamProjectId, newAgentHistory);
              set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
              // Persist conversation immediately so it survives page refreshes and project switches.
              // Scoped to streamProjectId so a click-away can't redirect this save to the wrong chat.
              debouncedSaveConversation(() => {
                if (streamProjectId != null) {
                  get().saveConversationHistory(streamProjectId).catch(() => {});
                }
              });
              break;
            }
            case 'agent_sidebar_open': {
              if (event.data.title && event.data.content) {
                const sidebarPayload = { title: String(event.data.title), content: String(event.data.content) };
                console.log(`[canvas-store] agent_sidebar_open received: title="${sidebarPayload.title}", content length=${sidebarPayload.content.length}`);
                updateMsg((m) => ({
                  ...m,
                  sidebarDoc: sidebarPayload,
                }));
              }
              break;
            }
            case 'agent_suggestions': {
              // v3.86 — prefer the rich `chips` payload (label + chat/view
              // action); fall back to legacy string suggestions.
              const richChips = Array.isArray((event.data as any).chips)
                ? ((event.data as any).chips as Array<{ label: string; action: "chat" | "view"; value: string }>)
                    .filter((c) => c && typeof c.label === "string" && typeof c.value === "string")
                : [];
              const suggestions = Array.isArray(event.data.suggestions) ? event.data.suggestions as string[] : [];
              if (richChips.length > 0) {
                set({ followUpChips: richChips.slice(0, 8) });
              } else if (suggestions.length > 0) {
                set({ followUpChips: suggestions.slice(0, 8) });
              }
              break;
            }
            case 'angle_stage_picker': {
              const availableStages = (event.data as any)?.availableStages;
              if (availableStages && typeof availableStages === 'object') {
                set({ anglePickerStages: availableStages as Record<string, number> });
              }
              break;
            }
            case 'variants_confirm_required': {
              const pd = event.data as {
                correlationId: string;
                sourceCount: number;
                formats: string[];
                total: number;
                estimatedCredits: number;
              };
              set({
                pendingVariantsConfirm: {
                  correlationId: pd.correlationId,
                  sourceCount: pd.sourceCount,
                  formats: Array.isArray(pd.formats) ? pd.formats : [],
                  total: pd.total,
                  estimatedCredits: pd.estimatedCredits,
                },
              });
              break;
            }
            case 'agent_suggestion_selected': {
              const selected=event.data;
              if(typeof selected?.messageId==='string' && ('selection' in selected)) get()._writeMessages(streamProjectId,messages=>messages.map(message=>message.id===selected.messageId?{...message,suggestionSelection:selected.selection}:message));
              break;
            }
            case 'agent_suggestion_pills': {
              const pillData = event.data as { question?: string; pills?: Array<{ label: string; description?: string; value: string; displayContent?: string; followUpContext?: CanvasFollowUpOrigin; productId?: number; forcedTool?: {name:string;arguments:Record<string,unknown>} }> };
              if (pillData.pills && pillData.pills.length > 0) {
                updateMsg((m) => ({
                  ...m,
                  suggestionPills: {
                    question: pillData.question || 'What would you like to do?',
                    pills: pillData.pills!,
                  },
                }));
              }
              break;
            }
            case 'workflow_plan_start': {
              updateMsg((m) => ({
                ...m,
                agentThinking: event.data.title || 'Generating…',
                workflowPlanTitle: event.data.title as string | undefined,
                workflowTaskCards: [],
              }));
              break;
            }
            case 'workflow_task_start': {
              const newCard: WorkflowTaskCardData = {
                taskId: String(event.data.taskId || ''),
                label: String(event.data.label || 'Working…'),
                toolName: String(event.data.toolName || ''),
                status: 'running',
                progressMessage: event.data.progressMessage as string | undefined,
              };
              updateMsg((m) => ({
                ...m,
                agentThinking: event.data.label as string || 'Working…',
                workflowTaskCards: [...(m.workflowTaskCards || []), newCard],
              }));
              break;
            }
            case 'workflow_task_progress': {
              updateMsg((m) => ({
                ...m,
                workflowTaskCards: (m.workflowTaskCards || []).map((c) =>
                  c.taskId === event.data.taskId
                    ? { ...c, progressMessage: event.data.progressMessage as string | undefined }
                    : c
                ),
              }));
              break;
            }
            case 'workflow_task_complete': {
              updateMsg((m) => ({
                ...m,
                workflowTaskCards: (m.workflowTaskCards || []).map((c) =>
                  c.taskId === event.data.taskId
                    ? {
                        ...c,
                        status: 'completed' as const,
                        summary: event.data.summary as string | undefined,
                        thumbnails: Array.isArray(event.data.thumbnails) ? event.data.thumbnails as string[] : c.thumbnails,
                      }
                    : c
                ),
              }));
              break;
            }
            case 'workflow_task_error': {
              updateMsg((m) => ({
                ...m,
                workflowTaskCards: (m.workflowTaskCards || []).map((c) =>
                  c.taskId === event.data.taskId
                    ? { ...c, status: 'failed' as const, error: event.data.error as string | undefined }
                    : c
                ),
              }));
              break;
            }
            case 'workflow_plan_complete': {
              updateMsg((m) => ({
                ...m,
                agentThinking: undefined,
              }));
              break;
            }
            case 'canvas_images_reload': {
              // Server pre-inserted canvas_images rows (e.g. competitor-ads
              // source ads) and wants the client to render them immediately.
              // Fetches the project's images and merges anything missing into
              // the store. Mirrors the safety-net logic in canvas_image_ready
              // but fires eagerly instead of waiting 1500ms after the last
              // clone finishes (which can be 2+ min after sources are ready).
              if (streamProjectId) {
                const reloadProjectId = streamProjectId;
                (async () => {
                  try {
                    const resp = await fetch(`/api/canvas/projects/${reloadProjectId}/images`);
                    if (!resp.ok) return;
                    // Empty rows reserve slots; only actual images belong in this merge.
                    // A late refresh must not resurrect a completed reservation.
                    const dbImages: Array<{ clientId: string; src: string; x: number; y: number; displayWidth: number; displayHeight: number; originalWidth?: number; originalHeight?: number; colorIndex?: number; prompt?: string; name?: string }> = (await resp.json()).filter((row: { src?: string }) => !!row.src);
                    if (dbImages.length === 0) return;
                    if (get().activeProjectId !== reloadProjectId) return;
                    const storeIds = new Set(activeProj(get()).images.filter((i) => !isVirtualImage(i)).map((i) => i.id));
                    const missing = dbImages.filter((r) => !storeIds.has(r.clientId));
                    // v6.92 — REPOINTED tiles (same clientId, NEW src) are not
                    // "missing": server-side frame repoints (chat person swap,
                    // frame edit) rewrote the DB but this early-return left the
                    // canvas showing the OLD frame — Dan: "it said it did but
                    // clearly did not" while the script popup had the new image.
                    // Update src in place before deciding there's nothing to do.
                    const dbById = new Map(dbImages.map((r) => [r.clientId, r]));
                    let repointed = 0;
                    get()._writeImages(reloadProjectId, (imgs) => imgs.map((i) => {
                      const r = dbById.get(i.id);
                      if (r && r.src && r.src !== i.src) { repointed++; return { ...i, src: r.src }; }
                      return i;
                    }));
                    if (repointed > 0) console.log('[canvas_images_reload] repointed', repointed, 'tile(s) to fresh src');
                    if (missing.length === 0) return;
                    console.log('[canvas_images_reload] eager fetch:', missing.length, 'new images from DB — merging into store');
                    const dbIds = new Set(dbImages.map((r) => r.clientId));
                    const localExtra = activeProj(get()).images.filter((i) => !dbIds.has(i.id) && !isVirtualImage(i));
                    const merged = [
                      ...dbImages.map((r) => ({
                        id: r.clientId,
                        name: r.name ?? 'generated',
                        src: r.src,
                        x: r.x,
                        y: r.y,
                        displayWidth: r.displayWidth,
                        displayHeight: r.displayHeight,
                        originalWidth: r.originalWidth ?? r.displayWidth,
                        originalHeight: r.originalHeight ?? r.displayHeight,
                        colorIndex: r.colorIndex ?? 0,
                        prompt: r.prompt,
                        adStyle: (r as any).adStyle,
                        adAngle: (r as any).adAngle,
                        wavespeedPrompt: (r as any).wavespeedPrompt,
                        conceptName: (r as any).conceptName,
                        headline: (r as any).headline,
                        bodyCopy: (r as any).bodyCopy,
                        ctaText: (r as any).ctaText,
                        qaPassed: (r as any).qaPassed,
                        qaReason: (r as any).qaReason,
                        // v4.87 — THE runId destroyer: this merge rebuilt every
                        // in-memory tile WITHOUT workflowRunId/adAngleId, and the
                        // next full sync persisted the stripped state — every run
                        // since the first competitor test lost its section frame.
                        workflowRunId: (r as any).workflowRunId,
                        adAngleId: (r as any).adAngleId,
                        videoUrl: (r as any).videoUrl,
                      })),
                      ...localExtra,
                    ];
                    get()._writeImages(reloadProjectId, () => merged);
                  } catch (err) {
                    console.warn('[canvas_images_reload] fetch failed:', err);
                  }
                })();
              }
              break;
            }
            case 'canvas_image_pending': {
              const originProjectId = streamProjectId;
              const { requestId, prompt: imgPrompt, width: imgW, height: imgH, serverManaged, x: pendServerX, y: pendServerY, conceptIndex: pendConceptIndex, exactLayout, workflowRunId: pendingRunId } = event.data as { exactLayout?: boolean; workflowRunId?: number; requestId: string; prompt?: string; width?: number; height?: number; serverManaged?: boolean; x?: number; y?: number; conceptIndex?: number };
              if (requestId) {
                const taskId = `agent-img-${requestId}`;
                const displayScale = Math.min(DEFAULT_WIDTH / (imgW || 1080), DEFAULT_HEIGHT / (imgH || 1080), 1);
                const displayWidth = Math.round((imgW || 1080) * displayScale);
                const displayHeight = Math.round((imgH || 1080) * displayScale);

                // Server may pass explicit x/y to override the batch tracker —
                // used by playbooks that own canvas layout (e.g. competitor-ads,
                // which places each clone tile at a deterministic slot beside
                // its source ad). When provided, skip the batch tracker entirely.
                let rowY: number;
                let posX: number;
                // v4.64 — the tile's fixed slot: server conceptIndex first,
                // else the claimed box's own slotIndex. Carried onto the
                // agent-img placeholder so repacks never reshuffle it.
                let claimedSlotIndex: number | undefined = typeof pendConceptIndex === 'number' ? pendConceptIndex : undefined;
                if (pendServerX !== undefined && pendServerY !== undefined) {
                  // v2.72 — Server sent a slot. Grid batches (campaign-v2/
                  // targeted/v3) AND explicit-placement playbooks both do this.
                  // CRITICAL FIX: PREFER replacing the queued box the user is
                  // watching. Claim THIS batch's leftmost intent-ph and use ITS
                  // position — so the image lands exactly where its loading box
                  // was and replaces it 1:1. The old code used the server's
                  // CONCEPT-ORDER coords while deleting the LEFTMOST box, so a
                  // box vanished at slot A while the image appeared at slot B —
                  // that's the "tiles randomly move around during generation"
                  // the user reported. Server coords are now only a FALLBACK for
                  // batches with no boxes (e.g. competitor-ads clone placement).
                  let claimedBox = false;
                  if (originProjectId != null) {
                    const projForClaim = get().projects[originProjectId];
                    // v4.63 — deterministic claim first: the server sends this
                    // job's conceptIndex and box i was built for job i, so the
                    // image lands in the EXACT box the user is watching — in
                    // the previewed order, immune to arrival-order races that
                    // scrambled multi-ratio batches.
                    const indexedBox = typeof pendConceptIndex === 'number' && projForClaim
                      ? projForClaim.canvasPlaceholders[`intent-ph-${assistantMsgId}-${pendConceptIndex}`]
                      : undefined;
                    const boxes = indexedBox
                      ? [indexedBox]
                      : projForClaim
                        ? Object.values(projForClaim.canvasPlaceholders)
                            .filter((p) => p.taskId.startsWith('intent-ph-') && p.batchId === assistantMsgId)
                            .sort((a, b) => a.y === b.y ? a.x - b.x : a.y - b.y)
                        : [];
                    // v4.62 — mixed-ratio batches have per-ratio-shaped boxes;
                    // claim one whose SHAPE matches this image (leftmost-first
                    // put 9:16 images in square slots). Uniform batches keep
                    // the old leftmost claim untouched. No shape match → fall
                    // through to server coords and leave the box for its ratio
                    // sibling (orphans are swept at playbook_complete).
                    const boxAspect = (b: { width: number; height: number }) => b.width / Math.max(b.height, 1);
                    const mixedShapes = boxes.length > 1 && boxes.some((b) => Math.abs(boxAspect(b) - boxAspect(boxes[0])) > 0.05);
                    const imgAspect = (imgW || 1080) / Math.max(imgH || 1080, 1);
                    const claimPool = mixedShapes
                      ? boxes.filter((b) => Math.abs(boxAspect(b) - imgAspect) / imgAspect < 0.12)
                      : boxes;
                    if (claimPool.length > 0) {
                      const claim = claimPool[0];
                      posX = claim.x;
                      rowY = claim.y;
                      claimedBox = true;
                      if (claimedSlotIndex === undefined && typeof claim.slotIndex === 'number') claimedSlotIndex = claim.slotIndex;
                      get()._writeCanvasPlaceholders(originProjectId, (m) => {
                        const next = { ...m };
                        delete next[claim.taskId];
                        return next;
                      });
                    }
                  }
                  if (!claimedBox) {
                    posX = pendServerX;
                    rowY = pendServerY;
                  }
                } else {
                  // Try to claim an existing intent_resolved placeholder for this
                  // batch — the one dropped earliest at the leftmost unfilled
                  // position. This makes the real image's agent-img placeholder
                  // land at the EXACT same spot the user has been staring at
                  // for the past ~20s, instead of slightly offset.
                  //
                  // CRITICAL: filter by batchId === assistantMsgId so we only
                  // claim placeholders from THIS batch. Without this, when
                  // multiple "make N ads" requests run concurrently, the
                  // FIRST image to come back (which might be from a later
                  // batch that finished faster) lands in an EARLIER batch's
                  // slot — visually jumping to the top of the canvas — and
                  // the earlier batch's images then steal slots from each
                  // other or fall back to wrong positions.
                  let claimedFromIntent = false;
                  if (originProjectId != null) {
                    const projForClaim = get().projects[originProjectId];
                    const intentPhs = projForClaim
                      ? Object.values(projForClaim.canvasPlaceholders)
                          .filter((p) => p.taskId.startsWith('intent-ph-') && p.batchId === assistantMsgId)
                          // Stable order: top-row first, then left-to-right
                          .sort((a, b) => a.y === b.y ? a.x - b.x : a.y - b.y)
                      : [];
                    // v4.76 — index-first here too: variations (and any other
                    // no-server-coords path) send conceptIndex since v4.63,
                    // but this branch still claimed leftmost-by-arrival while
                    // packActiveBatch re-packed by slot — every arrival
                    // teleported tiles between the two layouts.
                    const idxBox = typeof pendConceptIndex === 'number' && projForClaim
                      ? projForClaim.canvasPlaceholders[`intent-ph-${assistantMsgId}-${pendConceptIndex}`]
                      : undefined;
                    if (idxBox || intentPhs.length > 0) {
                      const claim = idxBox ?? intentPhs[0];
                      posX = claim.x;
                      rowY = claim.y;
                      claimedFromIntent = true;
                      if (claimedSlotIndex === undefined && typeof claim.slotIndex === 'number') claimedSlotIndex = claim.slotIndex;
                      // Drop the intent-ph at this slot — the agent-img we
                      // create below replaces it 1:1. No visual gap.
                      get()._writeCanvasPlaceholders(originProjectId, (m) => {
                        const next = { ...m };
                        delete next[claim.taskId];
                        return next;
                      });
                      // Make sure the batch tracker is initialized so the row
                      // is registered for downstream layout (later batches
                      // should land BELOW this one).
                      let batchEntry = batchRowTracker.get(assistantMsgId);
                      if (!batchEntry) {
                        batchEntry = { rowY, rowHeight: displayHeight, index: 1 };
                        batchRowTracker.set(assistantMsgId, batchEntry);
                        if (!activeProj(get()).generationRows.some(r => r.id === `row-${assistantMsgId}`)) {
                          get().addGenerationRow({
                            id: `row-${assistantMsgId}`,
                            batchIndex: 0,
                            prompt: imgPrompt || '',
                            imageIds: [],
                            rowY,
                            rowHeight: displayHeight,
                          });
                        }
                      } else {
                        batchEntry.index += 1;
                      }
                    }
                  }
                  if (!claimedFromIntent) {
                    // Fall through to the original batch-tracker layout
                    // (no intent-ph existed — e.g. a playbook that didn't emit
                    // intent_resolved, or a fresh request mid-stream).
                    // Determine batch position: all pending events for the same assistant turn
                    // share the same rowY and get consecutive x slots (horizontal row layout).
                    let batchEntry = batchRowTracker.get(assistantMsgId);
                    let batchIndex: number;
                    if (!batchEntry) {
                      // First image in this batch — compute rowY from current state and register
                      // a generation row immediately so subsequent batches start below this one.
                      const { images, generationRows } = activeProj(get());
                      rowY = getNextRowY(generationRows, images);
                      batchIndex = 0;
                      batchEntry = { rowY, rowHeight: displayHeight, index: 1 };
                      batchRowTracker.set(assistantMsgId, batchEntry);
                      // Register the row now so getNextRowY for later batches sees it
                      // (guard against duplicate — only add if no row with this id exists)
                      if (!activeProj(get()).generationRows.some(r => r.id === `row-${assistantMsgId}`)) {
                        get().addGenerationRow({
                          id: `row-${assistantMsgId}`,
                          batchIndex: 0,
                          prompt: imgPrompt || '',
                          imageIds: [],
                          rowY,
                          rowHeight: displayHeight,
                        });
                      }
                    } else {
                      // Subsequent image in the same batch — reuse rowY, advance x index
                      rowY = batchEntry.rowY;
                      batchIndex = batchEntry.index;
                      batchEntry.index += 1;
                      // Update rowHeight in case images have different heights
                      if (displayHeight > batchEntry.rowHeight) {
                        batchEntry.rowHeight = displayHeight;
                        // Keep generationRows in sync so next batch starts at the right y
                        get().updateGenerationRow(`row-${assistantMsgId}`, { rowHeight: displayHeight });
                      }
                    }
                    posX = START_X + batchIndex * (displayWidth + IMAGE_GAP);
                  }
                }
                // Planned server slots arrive before expensive generation work.
                // Claim the optimistic slot, then align it once to the durable
                // layout so reload and later image arrival use the same frame.
                if (exactLayout && pendServerX !== undefined && pendServerY !== undefined) {
                  posX = pendServerX;
                  rowY = pendServerY;
                }
                const pos = { x: posX, y: rowY };

                // Register this image as a slot for chat shimmer tracking
                const slotIndex = agentNextSlotIndex++;
                agentTaskToSlot.set(taskId, slotIndex);
                // Add a shimmer slot to the assistant message — resolved when image arrives
                get()._writeMessages(streamProjectId, (msgs) =>
                  msgs.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, imageSlots: [...(m.imageSlots || []), { index: slotIndex }] }
                      : m
                  )
                );
                get()._writeCanvasPlaceholders(originProjectId, (m) => ({
                  ...m,
                  [taskId]: { taskId, x: pos.x, y: pos.y, width: displayWidth, height: displayHeight, prompt: imgPrompt, genWidth: imgW || 1080, genHeight: imgH || 1080, filename: `agent_gen_${Date.now()}.jpg`, batchId: assistantMsgId, slotIndex: claimedSlotIndex, workflowRunId: pendingRunId ?? get().projects[originProjectId!]?.messages.find(message => message.id === assistantMsgId)?.playbookProgress?.workflowRunId },
                }));
                {
                  const sCurrent = get();
                  const pendingProjId = sCurrent.activeProjectId;
                  const pendingProj = activeProj(sCurrent);
                  const pendingNewPh = { x: pos.x, y: rowY, width: displayWidth, height: displayHeight };
                  const rowMembers: Array<{ x: number; w: number; h: number }> = [
                    { x: pendingNewPh.x, w: pendingNewPh.width, h: pendingNewPh.height },
                  ];
                  for (const ph of Object.values(pendingProj.canvasPlaceholders)) {
                    if (ph.y === rowY) rowMembers.push({ x: ph.x, w: ph.width, h: ph.height });
                  }
                  for (const img of pendingProj.images) {
                    if (!isVirtualImage(img) && img.y === rowY) rowMembers.push({ x: img.x, w: img.displayWidth, h: img.displayHeight });
                  }
                  const rowBbox = {
                    minX: Math.min(...rowMembers.map((m) => m.x)),
                    minY: rowY,
                    maxX: Math.max(...rowMembers.map((m) => m.x + m.w)),
                    maxY: Math.max(...rowMembers.map((m) => rowY + m.h)),
                  };
                  if (pendingProjId != null) {
                    set((s) => ({
                      taskCanvasState: { ...s.taskCanvasState, [pendingProjId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const } },
                    }));
                    get()._writeProjectView(pendingProjId, {
                      isCanvasOpen: true,
                      activeView: 'chat-canvas' as const,
                      pendingFitToRow: rowBbox,
                    });
                  }
                }
                if (!serverManaged) (async () => {
                  let resolved = false;
                  // v7.34 — 400 × 3s (20 min, was 80/4min) to match the server's
                  // poll budget: GPT Image 2 renders routinely outlive 4-6 min,
                  // and this loop failing early painted "Image generation timed
                  // out" on tiles whose render was still completing. Early-exits
                  // every tick once SSE resolves the placeholder, so the longer
                  // ceiling costs nothing on the happy path.
                  for (let attempt = 0; attempt < 400; attempt++) {
                    await new Promise((r) => setTimeout(r, 3000));
                    // If already resolved (SSE delivered the image while we were waiting), stop
                    if (resolved) return;
                    // Read placeholder from the ORIGINATING project's bucket so the
                    // poll continues to work after the user navigates away. The
                    // image will land in the originating chat via streamProjectId.
                    const projForPoll = originProjectId != null ? get().projects[originProjectId] : null;
                    const ph = projForPoll?.canvasPlaceholders[taskId];
                    // Placeholder is being resolved by canvas_image_ready SSE path — let it finish
                    if (ph?.resolving) { resolved = true; return; }
                    // Placeholder was never registered or was externally cleared — stop
                    if (!ph) return;
                    try {
                      const statusRes = await fetch(`/api/canvas/image-status/${requestId}`);
                      if (!statusRes.ok) continue;
                      const statusData = await statusRes.json();
                      if (statusData.status === 'completed' && statusData.imageUrl) {
                        // Re-check: if SSE handler raced ahead and already resolved, bail out
                        const projForCheck = originProjectId != null ? get().projects[originProjectId] : null;
                        const stillPending = projForCheck?.canvasPlaceholders[taskId];
                        if (stillPending?.resolving) { resolved = true; return; }
                        // Dedup guard: if canvas_image_ready SSE already claimed this
                        // requestId, don't call addImageToCanvas a second time.
                        if (handledImageRequests.has(requestId)) { resolved = true; return; }
                        handledImageRequests.add(requestId);
                        resolved = true;
                        // Capture placeholder position before resolving it
                        const pollPhX = stillPending?.x;
                        const pollPhY = stillPending?.y;
                        if (stillPending) get().resolveCanvasPlaceholder(taskId, originProjectId ?? undefined);
                        await get().addImageToCanvas(statusData.imageUrl, imgW || 1080, imgH || 1080, `agent_gen_${Date.now()}.jpg`, imgW || 1080, imgH || 1080, imgPrompt, pollPhX, pollPhY, undefined, streamProjectId ?? undefined);
                        // Resolve the shimmer slot in the assistant message with the real image URL
                        const polledUrl = statusData.imageUrl as string;
                        const pollSlotIdx = agentTaskToSlot.get(taskId) ?? -1;
                        get()._writeMessages(streamProjectId, (msgs) =>
                          msgs.map((m) => {
                            if (m.id !== assistantMsgId) return m;
                            if (pollSlotIdx >= 0 && m.imageSlots && m.imageSlots.length > 0) {
                              // Resolve the slot — shimmer → real image thumbnail
                              return { ...m, imageSlots: m.imageSlots.map((slot) => slot.index === pollSlotIdx ? { ...slot, resolved: polledUrl } : slot) };
                            }
                            // Fallback: no imageSlot tracked (shouldn't happen), add to thumbnails
                            return { ...m, thumbnails: Array.from(new Set([...(m.thumbnails || []), polledUrl])) };
                          })
                        );
                        // Force canvas open after polling resolves the image
                        const pollCanvasState = streamProjectId
                          ? { ...get().taskCanvasState, [streamProjectId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const } }
                          : get().taskCanvasState;
                        set({ taskCanvasState: pollCanvasState });
                        get()._writeProjectView(streamProjectId, { isCanvasOpen: true, activeView: 'chat-canvas' });
                        return;
                      } else if (statusData.status === 'failed') {
                        get().failCanvasPlaceholder(taskId, statusData.error || 'Image generation failed', originProjectId ?? undefined);
                        return;
                      }
                    } catch {}
                  }
                  if (!resolved) {
                    const projForFinal = originProjectId != null ? get().projects[originProjectId] : null;
                    const finalPh = projForFinal?.canvasPlaceholders[taskId];
                    if (finalPh && !finalPh.resolving) {
                      get().failCanvasPlaceholder(taskId, 'Image generation timed out', originProjectId ?? undefined);
                    }
                  }
                })();
              }
              break;
            }
            case 'canvas_image_ready': {
              // NOTE: intent_resolved placeholders are now claimed 1:1 by
              // canvas_image_pending as each image starts generating, so we
              // do NOT bulk-clear them here. Bulk-clearing caused the
              // "grey box disappears + reappears later" bug: the first
              // canvas_image_ready wiped out all intent-ph for the batch,
              // leaving images #2-N with no placeholder until their own
              // canvas_image_pending fired (often 10-30s later).
              const { requestId, imageUrl: readyUrl, prompt: readyPrompt, width: readyW, height: readyH, adStyle: readyAdStyle, adAngle: readyAdAngle, wavespeedPrompt: readyWavespeedPrompt, conceptName: readyConceptName, headline: readyHeadline, bodyCopy: readyBodyCopy, ctaText: readyCtaText, x: serverX, y: serverY, displayWidth: serverDW, displayHeight: serverDH, workflowRunId: readyRunId, videoUrl: readyVideoUrl } = event.data as { requestId: string; imageUrl?: string; prompt?: string; width?: number; height?: number; adStyle?: string; adAngle?: string; wavespeedPrompt?: string; conceptName?: string; headline?: string; bodyCopy?: string; ctaText?: string; x?: number; y?: number; displayWidth?: number; displayHeight?: number; workflowRunId?: number; videoUrl?: string };
              console.log('[canvas_image_ready] SSE event received, requestId=', requestId, 'readyUrl=', readyUrl?.slice(0, 60));
              if (requestId && readyUrl) {
                batchSrcs.add(readyUrl);
                // Tag this image with its batch so the completion compaction can
                // group it (keeps concurrent runs as separate ordered blocks).
                if (assistantMsgId) {
                  _imageBatchId.set(readyUrl, assistantMsgId);
                  if (!_batchFirstSeen.has(assistantMsgId)) _batchFirstSeen.set(assistantMsgId, _batchSeenCounter++);
                }
                // Track requestId → image URL so QA badge can find the image.
                // Also reconcile any pending badge that arrived before the URL was known.
                // Capture any pending QA badge BEFORE the set() so we can persist it after addImageToCanvas.
                const projForBadge = streamProjectId != null ? get().projects[streamProjectId] : null;
                const pendingQaBadge = projForBadge?.imageQaBadges[`__reqid__${requestId}`];
                get()._writeImageRequestIdToUrl(streamProjectId, (map) => ({ ...map, [requestId]: readyUrl }));
                get()._writeQaBadges(streamProjectId, (badges) => {
                  const pendingKey = `__reqid__${requestId}`;
                  const pendingBadge = badges[pendingKey];
                  if (!pendingBadge) return badges;
                  const copy = { ...badges };
                  delete copy[pendingKey];
                  copy[readyUrl] = pendingBadge;
                  return copy;
                });
                const taskId = `agent-img-${requestId}`;
                // Read placeholder from the ORIGINATING project so the resolve
                // lands in the correct chat even when the user has navigated.
                const projForReadyPh = streamProjectId != null ? get().projects[streamProjectId] : null;
                const ph = projForReadyPh?.canvasPlaceholders[taskId];
                console.log('[canvas_image_ready] ph=', ph ? 'found' : 'null', 'origin=', streamProjectId, 'current images.length=', projForReadyPh?.images.length);
                // Capture placeholder position BEFORE resolving it so we can place the image there
                let readyPhX = ph?.x;
                let readyPhY = ph?.y;
                if (typeof ph?.slotIndex === 'number') _slotBySrc.set(readyUrl, ph.slotIndex);
                // Resolve and remove the placeholder if it still exists
                if (ph) {
                  get().resolveCanvasPlaceholder(taskId, streamProjectId ?? undefined);
                }
                const imgW = readyW || (ph?.genWidth) || 1080;
                const imgH = readyH || (ph?.genHeight) || 1080;

                // v2.72 — Use server coords ONLY as a fallback when there is no
                // placeholder to land on. If a placeholder exists (the loading
                // box the user has been watching), the image MUST replace it in
                // place — using the server's concept-order coords here is what
                // made tiles jump from their box to a different slot. Server
                // coords still drive explicit-placement playbooks (competitor-
                // ads) where no box was claimed, so readyPhX/Y are undefined.
                if ((readyPhX === undefined || readyPhY === undefined) && serverX !== undefined && serverY !== undefined) {
                  readyPhX = serverX;
                  readyPhY = serverY;
                }
                // When placeholder is missing (user switched away and back, clearing placeholders),
                // compute a horizontal batch position so images don't stack vertically.
                if (readyPhX === undefined || readyPhY === undefined) {
                  const displayScale = Math.min(DEFAULT_WIDTH / imgW, DEFAULT_HEIGHT / imgH, 1);
                  const displayWidth = Math.round(imgW * displayScale);
                  const displayHeight = Math.round(imgH * displayScale);
                  let batchEntry = batchRowTracker.get(assistantMsgId);
                  if (!batchEntry) {
                    const { images, generationRows } = activeProj(get());
                    const rowY = getNextRowY(generationRows, images);
                    batchEntry = { rowY, rowHeight: displayHeight, index: 1 };
                    batchRowTracker.set(assistantMsgId, batchEntry);
                    if (!activeProj(get()).generationRows.some(r => r.id === `row-${assistantMsgId}`)) {
                      get().addGenerationRow({
                        id: `row-${assistantMsgId}`,
                        batchIndex: 0,
                        prompt: readyPrompt || '',
                        imageIds: [],
                        rowY,
                        rowHeight: displayHeight,
                      });
                    }
                    readyPhX = START_X;
                    readyPhY = rowY;
                  } else {
                    readyPhX = START_X + batchEntry.index * (displayWidth + IMAGE_GAP);
                    readyPhY = batchEntry.rowY;
                    batchEntry.index += 1;
                    if (displayHeight > batchEntry.rowHeight) {
                      batchEntry.rowHeight = displayHeight;
                      get().updateGenerationRow(`row-${assistantMsgId}`, { rowHeight: displayHeight });
                    }
                  }
                }
                // Dedup guard: if the canvas_image_pending polling loop already
                // claimed this requestId (it detected WaveSpeed completion at the
                // same instant the SSE fired), skip addImageToCanvas here.
                // The polling loop has already saved the image to DB and the store.
                if (handledImageRequests.has(requestId)) {
                  console.log('[canvas_image_ready] requestId already handled — skipping addImageToCanvas');
                } else {
                  handledImageRequests.add(requestId);
                  await get().addImageToCanvas(readyUrl, imgW, imgH, `agent_gen_${Date.now()}.jpg`, imgW, imgH, readyPrompt, readyPhX, readyPhY, { adStyle: readyAdStyle, adAngle: readyAdAngle, wavespeedPrompt: readyWavespeedPrompt, conceptName: readyConceptName, headline: readyHeadline, bodyCopy: readyBodyCopy, ctaText: readyCtaText, workflowRunId: readyRunId, videoUrl: readyVideoUrl }, streamProjectId ?? undefined);
                  console.log('[canvas_image_ready] addImageToCanvas done. store images.length=', activeProj(get()).images.length);
                  // If a QA badge was pending for this requestId (arrived before the URL was known),
                  // persist it to the DB now that the image is in the store.
                  if (pendingQaBadge) {
                    if (streamProjectId) {
                      const img = activeProj(get()).images.find((i) => i.src === readyUrl);
                      if (img) {
                        fetch(`/api/canvas/projects/${streamProjectId}/images/${img.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ qaPassed: pendingQaBadge.passed, qaReason: pendingQaBadge.reason }),
                        }).catch(() => {});
                      }
                    }
                  }
                }

                // v3.23 — keep THIS batch's grid dense on every image so mid-run
                // holes (unclaimed reserved slots + fallback-placed images from
                // QA-retry / failure cadence shifts) never persist. Scoped to the
                // batch, so no other tiles move. The 1500ms safety-net below still
                // does the final global settle + leftover-placeholder purge.
                get().packActiveBatch(streamProjectId, assistantMsgId);

                // DB-reload safety net: debounced — resets each time a new image arrives,
                // fires once 1500 ms after the LAST image in the batch.
                // This prevents N×2 redundant DB fetches + fitView() calls when N images
                // complete simultaneously (old approach scheduled 2 timers per image).
                if (streamProjectId) {
                  // Cancel any previously scheduled safety-net for the same project.
                  if (_safetyNetTimer !== null && _safetyNetProjectId === streamProjectId) {
                    clearTimeout(_safetyNetTimer);
                  }
                  _safetyNetProjectId = streamProjectId;
                  _safetyNetTimer = setTimeout(async () => {
                    _safetyNetTimer = null;
                    if (get().activeProjectId !== streamProjectId) return;
                    // v2.06 — batch-end compaction. Mid-stream placeholder churn
                    // leaves reserved-slot gaps ("random holes") while images land
                    // at fallback coords. One reflow when the batch settles:
                    // re-pack this stream's images into a tight grid, ordered by
                    // current visual position, anchored at the batch's top-left,
                    // then persist via the standard images snapshot.
                    const compactBatch = () => {
                      // v4.77 — while this batch still has live generating
                      // placeholders, the run is MID-FLIGHT: purging its
                      // queued boxes strands later arrivals at server coords,
                      // and the global relayout below rearranges the entire
                      // canvas the user is watching. Both are end-of-batch
                      // cleanups — playbook_complete runs the real finalize.
                      const stillGenerating = streamProjectId != null && Object.values(
                        get().projects[streamProjectId]?.canvasPlaceholders ?? {}
                      ).some((p) => p.batchId === assistantMsgId && p.taskId.startsWith('agent-img-') && !p.failed);
                      if (stillGenerating) {
                        get().packActiveBatch(streamProjectId, assistantMsgId);
                        return;
                      }
                      // v2.15 — purge any leftover intent placeholders for
                      // this batch (claimed count can undershoot when QA
                      // retries or failures shift the pending/image cadence).
                      get()._writeCanvasPlaceholders(streamProjectId, (m) => {
                        let changed = false;
                        const next = { ...m };
                        for (const k of Object.keys(next)) {
                          const ph = next[k];
                          if (ph?.taskId?.startsWith('intent-ph-') && ph.batchId === assistantMsgId) {
                            delete next[k];
                            changed = true;
                          }
                        }
                        return changed ? next : m;
                      });
                      get().packActiveBatch(streamProjectId, assistantMsgId);
                    };
                    try {
                      const resp = await fetch(`/api/canvas/projects/${streamProjectId}/images`);
                      if (!resp.ok) return;
                      // Empty rows reserve slots; only actual images belong in this merge.
                      // A late refresh must not resurrect a completed reservation.
                      const dbImages: Array<{ clientId: string; src: string; x: number; y: number; displayWidth: number; displayHeight: number; originalWidth?: number; originalHeight?: number; colorIndex?: number; prompt?: string; name?: string }> = (await resp.json()).filter((row: { src?: string }) => !!row.src);
                      if (dbImages.length === 0) return;
                      // Short-circuit: if the store already has all DB images, nothing was lost.
                      const storeIds = new Set(activeProj(get()).images.filter((i) => !isVirtualImage(i)).map((i) => i.id));
                      // v4.64 — a row missing from the store whose concept still
                      // has a LIVE "Generating…" placeholder is NOT lost — the
                      // server persists each attempt BEFORE QA finishes, so
                      // mid-run the DB is always ahead of SSE. Injecting those
                      // rows early is what doubled tiles and dragged the grid
                      // to server coords. Skip them; their canvas_image_ready
                      // is on the way (and playbook_complete reconciles if not).
                      // taskId match spans ALL batches (a row generating under a
                      // concurrent run is just as in-flight); the slot match is
                      // scoped to THIS batch, whose slot numbering it shares.
                      const allAgentPhs = streamProjectId != null
                        ? Object.values(get().projects[streamProjectId]?.canvasPlaceholders ?? {}).filter(
                            (p) => p.taskId.startsWith('agent-img-'))
                        : [];
                      const inFlight = (r: { clientId: string; colorIndex?: number }) =>
                        allAgentPhs.some((p) => p.taskId === `agent-img-${r.clientId}` ||
                          (p.batchId === assistantMsgId && typeof r.colorIndex === 'number' && p.slotIndex === r.colorIndex));
                      // v4.66 — only rows belonging to THIS batch's run may claim
                      // its boxes / take its batch stamp: a concurrent run's row
                      // could otherwise steal a queued box via a colliding
                      // colorIndex. Foreign rows fall through to DB coords.
                      const netRunId = readyRunId;
                      const rowIsOurs = (r: { clientId: string }): boolean => {
                        const rid = (r as any).workflowRunId;
                        return netRunId == null || rid == null || rid === netRunId;
                      };
                      const trulyMissing = dbImages.filter((r) => !storeIds.has(r.clientId) && !inFlight(r));
                      if (trulyMissing.length === 0) {
                        console.log('[canvas_image_ready] safety-net: store has all DB images (or in-flight) — skipping set()');
                        compactBatch();
                        return;
                      }
                      console.log('[canvas_image_ready] safety-net: DB has', dbImages.length, 'images,', trulyMissing.length, 'truly missing from store — reloading');
                      if (get().activeProjectId !== streamProjectId) return;
                      // In-flight rows stay OUT of the merge — their SSE ready
                      // will add them into their claimed slot.
                      const mergeRows = dbImages.filter((r) => storeIds.has(r.clientId) || !inFlight(r));
                      const dbIds = new Set(mergeRows.map((r) => r.clientId));
                      // v2.04 — dedupe localExtra BY SRC, not just by id. The
                      // playbook persists each generated image under a
                      // concept-* clientId; this handler added the SAME image
                      // as an img-gen-* node. Comparing ids only, the merge
                      // kept BOTH (db concept-* row + local img-gen-* twin)
                      // and every ad rendered twice on canvas ("5 ads + 2
                      // duplicates" — however many twins landed before this
                      // reload). The DB row is canonical (server-merged
                      // metadata + layout), so drop local twins whose src a
                      // DB row already covers.
                      const dbSrcs = new Set(mergeRows.map((r) => r.src));
                      const localExtra = activeProj(get()).images.filter(
                        (i) => !dbIds.has(i.id) && !isVirtualImage(i) && !dbSrcs.has(i.src)
                      );
                      // v4.64 — the DB row's x/y are the SERVER's grid (its own
                      // baseline). Overwriting live tiles with them yanked every
                      // settled image out of the box the user watched it land in,
                      // every time this net fired mid-run. Live position wins for
                      // tiles already on canvas; a recovered (SSE-dropped) image
                      // claims its own intent box (colorIndex = server conceptIndex)
                      // and only falls back to DB coords when no box remains.
                      const liveById = new Map(activeProj(get()).images.filter((i) => !isVirtualImage(i)).map((i) => [i.id, i]));
                      const liveBySrc = new Map(activeProj(get()).images.filter((i) => !isVirtualImage(i)).map((i) => [i.src, i]));
                      const claimBoxFor = (r: { src: string; colorIndex?: number }): { x: number; y: number } | null => {
                        if (typeof r.colorIndex !== 'number' || streamProjectId == null) return null;
                        const boxId = `intent-ph-${assistantMsgId}-${r.colorIndex}`;
                        const box = get().projects[streamProjectId]?.canvasPlaceholders[boxId];
                        if (!box) return null;
                        _slotBySrc.set(r.src, r.colorIndex);
                        get()._writeCanvasPlaceholders(streamProjectId, (m) => {
                          const next = { ...m };
                          delete next[boxId];
                          return next;
                        });
                        return { x: box.x, y: box.y };
                      };
                      const merged = [
                        ...mergeRows.map((r) => {
                          const live = liveById.get(r.clientId) ?? liveBySrc.get(r.src);
                          const ours = rowIsOurs(r);
                          const claimed = live || !ours ? null : claimBoxFor(r);
                          const pos = live ? { x: live.x, y: live.y } : claimed ?? { x: r.x, y: r.y };
                          if (!live && ours) {
                            // Recovered row: stamp batch + slot so the final
                            // compaction groups it WITH its batch (not as
                            // "__prior__" content that sorts to the top-left)
                            // and later repacks can slot it.
                            _imageBatchId.set(r.src, assistantMsgId);
                            if (typeof r.colorIndex === 'number') _slotBySrc.set(r.src, r.colorIndex);
                          }
                          return {
                          id: r.clientId,
                          name: r.name ?? 'generated',
                          src: r.src,
                          x: pos.x,
                          y: pos.y,
                          displayWidth: r.displayWidth,
                          displayHeight: r.displayHeight,
                          originalWidth: r.originalWidth ?? r.displayWidth,
                          originalHeight: r.originalHeight ?? r.displayHeight,
                          colorIndex: r.colorIndex ?? 0,
                          prompt: r.prompt,
                          adStyle: (r as any).adStyle,
                          adAngle: (r as any).adAngle,
                          wavespeedPrompt: (r as any).wavespeedPrompt,
                          conceptName: (r as any).conceptName,
                          headline: (r as any).headline,
                          bodyCopy: (r as any).bodyCopy,
                          ctaText: (r as any).ctaText,
                          qaPassed: (r as any).qaPassed,
                          qaReason: (r as any).qaReason,
                          // v4.64 — keep run attribution through the merge; losing
                          // it made the section frame flicker off on every net fire.
                          workflowRunId: (r as any).workflowRunId,
                          adAngleId: (r as any).adAngleId,
                          videoUrl: (r as any).videoUrl,
                          };
                        }),
                        ...localExtra,
                      ];
                      // Do NOT set pendingFitToView — that causes fitView() to run for every
                      // image in a batch. fit-to-row is already handled by addImageToCanvas.
                      get()._writeImages(streamProjectId, () => merged);
                      get()._writeProjectView(streamProjectId, { isCanvasOpen: true, activeView: 'chat-canvas' as const });
                      compactBatch();
                    } catch {
                      // Safety net — ignore failures
                    }
                  }, 1500);
                }

                // Resolve the shimmer slot in the assistant message with the real image URL
                const readySlotIdx = agentTaskToSlot.get(taskId) ?? -1;
                get()._writeMessages(streamProjectId, (msgs) =>
                  msgs.map((m) => {
                    if (m.id !== assistantMsgId) return m;
                    if (readySlotIdx >= 0 && m.imageSlots && m.imageSlots.length > 0) {
                      // Resolve the slot — shimmer → real image thumbnail
                      return { ...m, imageSlots: m.imageSlots.map((slot) => slot.index === readySlotIdx ? { ...slot, resolved: readyUrl } : slot) };
                    }
                    // Fallback: no imageSlot tracked (old messages), add to thumbnails
                    return { ...m, thumbnails: Array.from(new Set([...(m.thumbnails || []), readyUrl])) };
                  })
                );
                // Auto-open canvas panel when image is ready (same pattern as canvas_image_pending)
                const newReadyCanvasState = streamProjectId
                  ? { ...get().taskCanvasState, [streamProjectId]: { isCanvasOpen: true, activeView: 'chat-canvas' as const } }
                  : get().taskCanvasState;
                set(s => ({ taskCanvasState: newReadyCanvasState, ...taskPanelPatch(s, streamProjectId, { showDocumentOverlay: false }) }));
                get()._writeProjectView(streamProjectId, { isCanvasOpen: true, activeView: 'chat-canvas' });
              }
              break;
            }
            case 'canvas_image_failed': {
              const { requestId, error: imgError } = event.data as { requestId: string; error?: string };
              if (requestId) {
                const taskId = `agent-img-${requestId}`;
                get().failCanvasPlaceholder(taskId, imgError || 'Image generation failed');
                // Mark the imageSlot as failed so chat shimmer shows error state
                const failSlotIdx = agentTaskToSlot.get(taskId) ?? -1;
                if (failSlotIdx >= 0) {
                  get()._writeMessages(streamProjectId, (msgs) =>
                    msgs.map((m) =>
                      m.id === assistantMsgId
                        ? { ...m, imageSlots: (m.imageSlots || []).map((slot) => slot.index === failSlotIdx ? { ...slot, failed: true, errorMsg: imgError || 'Generation failed' } : slot) }
                        : m
                    )
                  );
                }
              }
              break;
            }
            case 'canvas_image_qa_badge': {
              const { requestId: badgeRequestId, passed: badgePassed, overallScore, badgeType, reason, attempt: badgeAttempt } = event.data as {
                requestId: string;
                passed: boolean;
                overallScore: number;
                badgeType: string;
                reason: string;
                attempt: number;
                conceptIndex: number;
              };
              if (badgeRequestId) {
                const projForBadge = streamProjectId != null ? get().projects[streamProjectId] : null;
                const imageUrl = projForBadge?.imageRequestIdToUrl[badgeRequestId];
                if (imageUrl) {
                  get()._writeQaBadges(streamProjectId, (badges) => ({
                    ...badges,
                    [imageUrl]: { passed: badgePassed, score: overallScore, badgeType, reason, attempt: badgeAttempt },
                  }));
                  // Persist QA result to DB so it survives page reload
                  if (streamProjectId) {
                    const img = activeProj(get()).images.find((i) => i.src === imageUrl);
                    if (img) {
                      fetch(`/api/canvas/projects/${streamProjectId}/images/${img.id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ qaPassed: badgePassed, qaReason: reason }),
                      }).catch(() => {});
                    }
                  }
                } else {
                  // URL not yet recorded — store by requestId temporarily, will be resolved when URL is known
                  get()._writeQaBadges(streamProjectId, (badges) => ({
                    ...badges,
                    [`__reqid__${badgeRequestId}`]: { passed: badgePassed, score: overallScore, badgeType, reason, attempt: badgeAttempt },
                  }));
                }
              }
              break;
            }
            case 'qa_start': {
              const { stepId, attempt, label } = event.data as { stepId: string; imageUrl?: string; attempt?: number; status?: string; label?: string };
              updateMsg((m) => ({
                ...m,
                agentTaskGroups: (m.agentTaskGroups || []).map((g) =>
                  g.status === 'active'
                    ? {
                        ...g,
                        qaStatus: { stepId, status: 'reviewing', attempt: attempt ?? 0, label: label || 'Reviewing creative…' },
                      }
                    : g
                ),
              }));
              break;
            }
            case 'qa_result': {
              const { stepId, passed, issues, issueCategory, attempt, label, status } = event.data as {
                stepId: string;
                imageUrl?: string;
                passed?: boolean;
                issues?: string[];
                issueCategory?: string;
                confidence?: number;
                recommendation?: string;
                attempt?: number;
                label?: string;
                status?: string;
              };
              updateMsg((m) => ({
                ...m,
                agentTaskGroups: (m.agentTaskGroups || []).map((g) =>
                  g.status === 'active' || (g.qaStatus && g.qaStatus.stepId === stepId)
                    ? {
                        ...g,
                        qaStatus: {
                          stepId,
                          status: (status as 'approved' | 'regenerating' | 'skipped') || (passed ? 'approved' : 'regenerating'),
                          attempt: attempt ?? 0,
                          label: label || (passed ? 'Approved' : 'Regenerating…'),
                          passed,
                          issues: issues || [],
                          issueCategory: issueCategory || 'none',
                        },
                      }
                    : g
                ),
              }));
              break;
            }
            case 'open_brand_modal': {
              // Emitted by the brand-onboarding playbook when the user
              // pastes "here is my brand <URL>". Stash the URL so
              // canvas-brands-view can open AnalyzeBrandModal pre-filled,
              // then switch the view so the user actually sees it.
              const pd = event.data as { url?: string };
              if (pd.url) {
                set({ pendingBrandModalUrl: pd.url, standaloneActiveView: 'brands' });
                useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
              }
              break;
            }
            case 'intent_resolved': {
              const pd = event.data as { intent?: string; openCanvas?: boolean; adCount?: number; aspectRatios?: string[] };
              if (pd.openCanvas) {
                get()._writeProjectView(streamProjectId, { isCanvasOpen: true, activeView: 'chat-canvas' as const });
              }
              // Create placeholder boxes on canvas immediately so the user
              // sees something right away instead of a blank canvas. Drop the
              // new row BELOW any existing content (images + open placeholders)
              // so a second prompt in the same chat doesn't stack on the first.
              const count = pd.adCount ?? 5;
              const placeholders: Record<string, CanvasPlaceholder> = {};
              // These grid constants MUST match the server's grid math
              // (playbookExecutor.ts: 40px horizontal gap, shared 80px row gap,
              // ~300px tile, 5 cols). The grey placeholders are positioned
              // here; the REAL images land at server-computed coords. If the
              // gap or tile size differ, a follow-up batch's grey boxes don't
              // line up with the previous batch's images — column N drifts left
              // by gap_delta*N (20px·N with the old gap=20). v2.67.
              const COLUMN_GAP = 40;
              const SECTION_GAP = 120;  // == the server __batchBaselineY gap (v4.78: was 60; the run frame's 52px label band overlapped the section above)
              // Shape the placeholder tile to the selected aspect ratio the same
              // way the server scales a generated image (fit inside a 300×300
              // box) so non-square ads (9:16, 4:5, 16:9) get correctly-shaped
              // AND correctly-spaced placeholders, not square 300×300 ones.
              const FIT = 300;
              // v4.62 — multi-ratio runs: the server emits adCount already
              // multiplied (N×M) plus the ratio list; boxes cycle ratios in
              // expansion order (box i → ratios[i % M]) and stride by the
              // batch CELL (max dims) so mixed shapes stay on-grid — the
              // exact same math as the server's slot placement. Single-ratio
              // runs reduce to the old per-shape math byte-for-byte.
              const ratioList = Array.isArray(pd.aspectRatios) && pd.aspectRatios.length > 1
                ? pd.aspectRatios
                : [get().selectedAspectRatio || '1:1'];
              const boxDims = ratioList.map((r) => {
                const m = /^(\d+)\s*:\s*(\d+)$/.exec(r || '1:1');
                const w = m && parseInt(m[1], 10) > 0 ? parseInt(m[1], 10) : 1;
                const h = m && parseInt(m[2], 10) > 0 ? parseInt(m[2], 10) : 1;
                const sc = Math.min(FIT / w, FIT / h);
                return { w: Math.round(w * sc), h: Math.round(h * sc) };
              });
              // Cell pinned to FIT (300) — same grid as the server's slot math
              // and packActiveBatch's DEFAULT_WIDTH stride. One geometry.
              const CELL_W = FIT;
              const CELL_H = FIT;
              const COLS = Math.min(count, 5);
              const startX = 60;

              const currentState = get();
              const currentProj = activeProj(currentState);
              const existingImageBottoms = currentProj.images
                .filter((img) => typeof img.y === 'number' && typeof img.displayHeight === 'number')
                .map((img) => (img.y as number) + (img.displayHeight as number));
              const existingPlaceholderBottoms = Object.values(currentProj.canvasPlaceholders)
                .map((p) => p.y + p.height);
              const allBottoms = [...existingImageBottoms, ...existingPlaceholderBottoms];
              const startY = allBottoms.length > 0
                ? Math.max(...allBottoms) + SECTION_GAP
                : 60;

              // Tag every intent_resolved placeholder with the current
              // assistantMsgId so canvas_image_pending only claims slots
              // belonging to THIS batch (not someone else's loading row),
              // and so playbook_complete only sweeps THIS batch's orphans.
              // Without batchId, two concurrent "make 5 ads" requests
              // cross-contaminate: batch B's image lands in batch A's
              // slot, and batch B's playbook_complete wipes A's loaders.
              const intentBatchId = assistantMsgId;
              // v2.76 — record batch ORDER here, at intent_resolved (when the
              // loading boxes are placed = the order you SENT the prompts), NOT
              // when images first arrive. A small batch ("make 10 santa") often
              // finishes before a big one ("make 20") it was sent AFTER, so
              // ordering by arrival put santa first and bumped it to the top.
              // intent_resolved fires in send order, so this is stable + correct.
              if (intentBatchId && !_batchFirstSeen.has(intentBatchId)) {
                _batchFirstSeen.set(intentBatchId, _batchSeenCounter++);
              }
              for (let i = 0; i < count; i++) {
                const col = i % COLS;
                const row = Math.floor(i / COLS);
                const d = boxDims[i % boxDims.length];
                const taskId = `intent-ph-${intentBatchId}-${i}`;
                placeholders[taskId] = {
                  taskId,
                  x: startX + col * (CELL_W + COLUMN_GAP),
                  y: startY + row * (CELL_H + CANVAS_AD_ROW_GAP),
                  width: d.w,
                  height: d.h,
                  prompt: 'Generating...',
                  batchId: intentBatchId,
                  slotIndex: i,
                };
              }
              {
                get()._writeCanvasPlaceholders(streamProjectId, (m) => ({ ...m, ...placeholders }));
              }
              break;
            }
            case 'agent_reasoning': {
              // v6.77 — real thinking now streams (routing decisions + the
              // model's inter-tool text). append:true accumulates across the
              // turn; the optimistic submit placeholder is always REPLACED by
              // the first real event, never appended to.
              const pd = event.data as { text?: string; append?: boolean; classificationTimeMs?: number };
              const incoming = (pd.text ?? '').trim();
              if (!incoming) break;
              get()._writeMessages(streamProjectId, (msgs) =>
                msgs.map((m) => {
                  if (m.id !== assistantMsgId) return m;
                  const prev = m.classifierReasoning ?? '';
                  const prevIsPlaceholder =
                    prev === "Let me think about what you're looking for…" || prev === 'Reading your message…';
                  const next = pd.append && prev && !prevIsPlaceholder ? `${prev}\n\n${incoming}` : incoming;
                  return { ...m, classifierReasoning: next, classificationTimeMs: pd.classificationTimeMs ?? m.classificationTimeMs };
                })
              );
              break;
            }
            case 'angle_set_card': {
              // v6.77 — chat-created angle set is ready: attach the doc card
              // and pop the Angles doc sidebar open.
              const pd = event.data as { setId?: number; title?: string; angleCount?: number; building?: boolean };
              if (typeof pd.setId !== 'number') break;
              const setId = pd.setId;
              // v7.25 — concepts-first auto-build: mark the set as building
              // (6-min ceiling) so the doc panel's Build button stands down.
              if (pd.building) {
                set((s) => ({ angleSetBuildingUntil: { ...s.angleSetBuildingUntil, [setId]: Date.now() + 6 * 60_000 } }));
              }
              get()._writeMessages(streamProjectId, (msgs) =>
                msgs.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, angleSetCard: { setId, title: pd.title ?? 'Angle Set', angleCount: pd.angleCount } }
                    : m
                )
              );
              get().openSidebarPanel({ type: 'angle_set', title: pd.title ?? 'Angle Set', angleSetId: setId }, streamProjectId);
              break;
            }
            case 'agent_ack': {
              const pd = event.data as { text?: string };
              get()._writeMessages(streamProjectId, (msgs) =>
                msgs.map((m) =>
                  m.id === assistantMsgId ? { ...m, ackText: pd.text ?? undefined } : m
                )
              );
              break;
            }
            // playbook_start is emitted when executePlaybook begins (playbook path, not skeleton path).
            case 'playbook_start': {
              const pd = event.data as { productId?: number; productName?: string; playbookName?: string; totalPhases?: number; phases?: Array<{ id: string; name: string; phaseType: string }>; workflowRunId?: number; brandIdentityLock?: boolean };
              updateMsg((m) => ({
                ...m,
                agentThinking: 'Starting campaign…',
                playbookProgress: {
                  workflowRunId: pd.workflowRunId,
                  playbookName: pd.playbookName ?? 'Campaign',
                  productId: pd.productId,
                  productName: pd.productName,
                  totalPhases: pd.totalPhases ?? (pd.phases?.length ?? 0),
                  phases: pd.phases ?? [],
                  phaseStatuses: Object.fromEntries((pd.phases ?? []).map((p) => [p.id, 'pending' as const])),
                  phaseStatusTexts: {},
                  activePhaseId: null,
                  qaResults: [],
                  conceptProgress: null,
                  completedSummary: null,
                  isDone: false,
                  brandIdentityLock: pd.brandIdentityLock,
                },
              }));
              // Dual-write project-scoped playbookProgress slice.
              {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                get()._writePlaybookProgress(numericKey, {
                  workflowRunId: pd.workflowRunId,
                  playbookName: pd.playbookName ?? 'Campaign',
                  productId: pd.productId,
                  productName: pd.productName,
                  totalPhases: pd.totalPhases ?? (pd.phases?.length ?? 0),
                  phases: pd.phases ?? [],
                  phaseStatuses: Object.fromEntries((pd.phases ?? []).map((p) => [p.id, 'pending' as const])),
                  phaseStatusTexts: {},
                  activePhaseId: null,
                  qaResults: [],
                  conceptProgress: null,
                  completedSummary: null,
                  isDone: false,
                  brandIdentityLock: pd.brandIdentityLock,
                });
              }
              // Capture workflowRunId so the stop handler can cancel it server-side.
              if (pd.workflowRunId) {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                get()._writeActiveWorkflowRunId(numericKey, Number(pd.workflowRunId));
                get()._writeCanvasPlaceholders(numericKey, current => Object.fromEntries(Object.entries(current).map(([key, p]) => [key, p.batchId === assistantMsgId ? { ...p, workflowRunId: Number(pd.workflowRunId) } : p])));
              }
              break;
            }
            case 'playbook_phase_start': {
              const pd = event.data as { phaseId?: string; statusText?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, activePhaseId: pd.phaseId ?? null, phaseStatuses: { ...m.playbookProgress.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: 'running' as const } : {}) }, phaseStatusTexts: { ...m.playbookProgress.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) } } };
              });
              // Dual-write project-scoped playbookProgress slice.
              {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                const current = get().projects[numericKey ?? -1]?.playbookProgress;
                if (current) {
                  get()._writePlaybookProgress(numericKey, {
                    ...current,
                    activePhaseId: pd.phaseId ?? null,
                    phaseStatuses: { ...current.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: 'running' as const } : {}) },
                    phaseStatusTexts: { ...current.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) },
                  });
                }
              }
              break;
            }
            case 'playbook_phase_progress': {
              const pd = event.data as { phaseId?: string; statusText?: string; conceptIndex?: number; totalConcepts?: number };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, conceptProgress: pd.totalConcepts != null ? { current: pd.conceptIndex ?? 0, total: pd.totalConcepts } : m.playbookProgress.conceptProgress, phaseStatusTexts: { ...m.playbookProgress.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) } } };
              });
              // Dual-write project-scoped playbookProgress slice.
              {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                const current = get().projects[numericKey ?? -1]?.playbookProgress;
                if (current) {
                  get()._writePlaybookProgress(numericKey, {
                    ...current,
                    conceptProgress: pd.totalConcepts != null ? { current: pd.conceptIndex ?? 0, total: pd.totalConcepts } : current.conceptProgress,
                    phaseStatusTexts: { ...current.phaseStatusTexts, ...(pd.phaseId && pd.statusText ? { [pd.phaseId]: pd.statusText } : {}) },
                  });
                }
              }
              break;
            }
            case 'playbook_phase_complete': {
              const pd = event.data as { phaseId?: string; success?: boolean };
              const newStatus = pd.success === false ? 'failed' as const : 'completed' as const;
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, phaseStatuses: { ...m.playbookProgress.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: newStatus } : {}) }, activePhaseId: m.playbookProgress.activePhaseId === pd.phaseId ? null : m.playbookProgress.activePhaseId } };
              });
              // Dual-write project-scoped playbookProgress slice.
              {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                const current = get().projects[numericKey ?? -1]?.playbookProgress;
                if (current) {
                  get()._writePlaybookProgress(numericKey, {
                    ...current,
                    phaseStatuses: { ...current.phaseStatuses, ...(pd.phaseId ? { [pd.phaseId]: newStatus } : {}) },
                    activePhaseId: current.activePhaseId === pd.phaseId ? null : current.activePhaseId,
                  });
                }
              }
              break;
            }
            case 'playbook_qa_result': {
              const pd = event.data as { conceptIndex?: number; attempt?: number; score?: number; passed?: boolean; status?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                const existing = m.playbookProgress.qaResults.filter((r) => r.conceptIndex !== (pd.conceptIndex ?? -1));
                return { ...m, playbookProgress: { ...m.playbookProgress, qaResults: [...existing, { conceptIndex: pd.conceptIndex ?? 0, attempt: pd.attempt ?? 1, score: pd.score ?? 0, passed: pd.passed ?? false, status: (pd.status ?? 'needs_review') as any }].sort((a, b) => a.conceptIndex - b.conceptIndex) } };
              });
              break;
            }
            case 'playbook_complete': {
              const pd = event.data as { summary?: string };
              updateMsg((m) => {
                if (!m.playbookProgress) return m;
                return { ...m, playbookProgress: { ...m.playbookProgress, isDone: true, activePhaseId: null, completedSummary: pd.summary ?? null } };
              });
              // Clear project-scoped playbookProgress slice when playbook finishes.
              {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                get()._writePlaybookProgress(numericKey, null);
              }
              // v4.47 — persist the chat NOW, not only at stream `done`: a tab
              // closed/slept between playbook completion and stream end used
              // to lose the whole run card from history.
              if (streamProjectId != null) {
                get().saveConversationHistory(streamProjectId).catch(() => {});
              }
              // v2.73 — FINALIZE the batch so it ends perfectly clean: every box
              // becomes its image, no leftover loading boxes, no gaps. The old
              // handler only swept QUEUED (intent-ph) boxes — it left GENERATING
              // (agent-img) boxes whose ready-event was SSE-dropped, plus the
              // holes those leave. Now, in order:
              //   1. reconcile — pull any SSE-dropped images back from the DB so
              //      every generated ad is on the canvas before we clear boxes.
              //   2. clear ALL of THIS batch's loading boxes (queued + generating),
              //      scoped by batchId so concurrent batches keep theirs.
              //   3. compact every tile into a contiguous, gap-free grid.
              // Scoped to this batch's boxes; the compact spans the canvas.
              const finalizePid = streamProjectId;
              const finalizeBatch = assistantMsgId;
              (async () => {
                try { await get().reconcileImagesFromServer(finalizePid); } catch { /* non-fatal */ }
                get()._writeCanvasPlaceholders(finalizePid, (m) => {
                  const next = { ...m };
                  let didClear = false;
                  for (const key of Object.keys(next)) {
                    const ph = next[key];
                    if (ph?.batchId === finalizeBatch &&
                        (ph.taskId.startsWith('intent-ph-') || ph.taskId.startsWith('agent-img-'))) {
                      delete next[key];
                      didClear = true;
                    }
                  }
                  return didClear ? next : m;
                });
                get().packActiveBatch(finalizePid, finalizeBatch);
                if (finalizePid != null) get().saveImagesSnapshot(finalizePid).catch(() => {});
                // v5.07 — ALWAYS end the run with the camera on the finished
                // batch. Mid-run layout drift used to leave the viewport parked
                // on empty grid while the ads sat off-screen until a hard
                // refresh (run 1020): nothing on the completion path re-fit the
                // viewport. Fit to this batch's tiles; fall back to every tile.
                if (finalizePid != null) {
                  const projFit = get().projects[finalizePid];
                  const allReal = (projFit?.images ?? []).filter((im) => !isVirtualImage(im));
                  const batchTiles = allReal.filter((im) => _imageBatchId.get(im.src) === finalizeBatch);
                  const fitTiles = (batchTiles.length > 0 ? batchTiles : allReal).filter(
                    // finite-only, and ignore server spacer rows parked at x=-999999
                    (t) => Number.isFinite(t.x) && Number.isFinite(t.y) && t.x > -100_000,
                  );
                  if (fitTiles.length > 0) {
                    get()._writeProjectView(finalizePid, {
                      pendingFitToRow: {
                        minX: Math.min(...fitTiles.map((t) => t.x)),
                        minY: Math.min(...fitTiles.map((t) => t.y)),
                        maxX: Math.max(...fitTiles.map((t) => t.x + (t.displayWidth || DEFAULT_WIDTH))),
                        maxY: Math.max(...fitTiles.map((t) => t.y + (t.displayHeight || DEFAULT_HEIGHT))),
                      },
                    });
                  }
                }
              })();
              break;
            }
            // workflow_nudge and workflow_confirmation are no longer emitted by the server
            // (replaced by immediate playbook execution in messageOrchestrator)
            case 'workflow_started': {
              updateMsg((m) => ({
                ...m,
                agentThinking: 'Starting workflow…',
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'running' }
                  : { status: 'running', totalPhases: event.data.totalPhases } as WorkflowState,
              }));
              // Capture server-side workflowRunId for server-side cancellation on stop.
              if (event.data.workflowRunId) {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                get()._writeActiveWorkflowRunId(numericKey, Number(event.data.workflowRunId));
              }
              break;
            }
            case 'workflow_phase_start': {
              updateMsg((m) => ({
                ...m,
                agentThinking: `Running phase: ${event.data.phaseName}…`,
                workflowState: m.workflowState
                  ? {
                      ...m.workflowState,
                      status: 'running',
                      currentPhaseIndex: event.data.phaseIndex,
                      currentPhaseName: event.data.phaseName,
                      totalPhases: event.data.totalPhases ?? m.workflowState.totalPhases,
                    }
                  : ({
                      status: 'running',
                      currentPhaseIndex: event.data.phaseIndex,
                      currentPhaseName: event.data.phaseName,
                      totalPhases: event.data.totalPhases,
                    } as WorkflowState),
              }));
              break;
            }
            case 'workflow_phase_complete': {
              updateMsg((m) => ({
                ...m,
                agentThinking: `Completed: ${event.data.phaseName}`,
              }));
              break;
            }
            case 'workflow_checkpoint': {
              updateMsg((m) => ({
                ...m,
                isStreaming: false,
                agentThinking: undefined,
                workflowState: m.workflowState
                  ? {
                      ...m.workflowState,
                      status: 'paused',
                      checkpointId: event.data.checkpointId,
                      checkpointType: event.data.checkpointType,
                      checkpointPayload: event.data.payload,
                    }
                  : ({
                      status: 'paused',
                      checkpointId: event.data.checkpointId,
                      checkpointType: event.data.checkpointType,
                      checkpointPayload: event.data.payload,
                      workflowRunId: event.data.workflowRunId,
                    } as WorkflowState),
              }));
              set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
              break;
            }
            case 'workflow_resumed': {
              updateMsg((m) => ({
                ...m,
                isStreaming: true,
                agentThinking: 'Resuming workflow…',
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'running', checkpointId: undefined, checkpointPayload: undefined }
                  : ({ status: 'running' } as WorkflowState),
              }));
              break;
            }
            case 'workflow_complete': {
              const completedImageCount = (event.data as Record<string, unknown>)?.imageCount as number | undefined;
              const n = completedImageCount ?? 0;
              const completionText = n > 0
                ? `Campaign launch complete — ${n} ad variant${n === 1 ? ' is' : 's are'} on your canvas. You can edit, rearrange, or ask me to modify any of them.`
                : `Campaign launch complete. Your ads are ready on the canvas.`;
              updateMsg((m) => ({
                ...m,
                content: completionText,
                isStreaming: false,
                agentThinking: undefined,
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'completed', checkpointId: undefined, checkpointPayload: undefined }
                  : ({ status: 'completed' } as WorkflowState),
              }));
              set((s) => ({
                typingByProject: { ...s.typingByProject, [projectKey]: false },
              }));
              // Persist conversation immediately after workflow completion — scoped to streamProjectId
              debouncedSaveConversation(() => {
                if (streamProjectId != null) {
                  get().saveConversationHistory(streamProjectId).catch(() => {});
                }
              });
              {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                // v5.07 — this fit used to be written to the store ROOT
                // (set({pendingFitToView:true})), which no selector reads —
                // they read projects[id].pendingFitToView — so the completion
                // fit silently never fired. Write it per-project.
                get()._writeProjectView(numericKey, { pendingFitToView: true });
                get()._writeActiveWorkflowRunId(numericKey, null);
              }
              break;
            }
            case 'workflow_cancelled': {
              updateMsg((m) => ({
                ...m,
                isStreaming: false,
                agentThinking: undefined,
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'cancelled' }
                  : ({ status: 'cancelled' } as WorkflowState),
              }));
              set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
              {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                get()._writeActiveWorkflowRunId(numericKey, null);
              }
              break;
            }
            case 'workflow_error': {
              updateMsg((m) => ({
                ...m,
                isStreaming: false,
                agentThinking: undefined,
                workflowState: m.workflowState
                  ? { ...m.workflowState, status: 'error', errorMessage: event.data.error }
                  : ({ status: 'error', errorMessage: event.data.error } as WorkflowState),
              }));
              set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
              {
                const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
                get()._writeActiveWorkflowRunId(numericKey, null);
              }
              break;
            }
            case 'agent_error': {
              updateMsg((m) => ({
                ...m,
                content: `Something went wrong: ${event.data.error || 'Unknown error'}`,
                isStreaming: false,
                agentThinking: undefined,
              }));
              set((s) => ({
                typingByProject: { ...s.typingByProject, [projectKey]: false },
                sseError: true,
              }));
              // PERSIST the error message immediately. Otherwise, if the user
              // navigates away before the debounced save runs, the DB keeps
              // the empty content and the chat reopens with silent gaps where
              // the agent died — "broken chat no canvas or images." Users
              // need to see WHY a turn failed when they come back.
              if (streamProjectId != null) {
                get().saveConversationHistory(streamProjectId).catch(() => {});
              }
              break;
            }
            case 'edit_fix_result': {
              // v2.25 — per-item resolution for the comment/mark Apply flow
              // (forced run_edit_fixes dispatch). The edited image itself
              // arrives via the standard canvas_image_pending/ready events;
              // this event only settles the source image's annotations:
              // "applied" resolves its pending comments/marks, "failed"
              // flags the comments so the Apply bar shows Retry. Marks have
              // no failed state — they stay pending and ride along on the
              // next Apply (same as the old client-side pipeline).
              const d = event.data as { imageClientId: string; status: 'applied' | 'failed'; newImageClientId?: string; error?: string };
              const s = get();
              if (d.status === 'applied') {
                const dispatched = _editFixDispatched.get(d.imageClientId);
                if (dispatched) {
                  // Resolve EXACTLY what runApply dispatched for this image
                  // (retry sends only failed comments, no marks; fresh applies
                  // exclude failed comments). applyPending* re-filter to
                  // still-pending ids, so stale ids are harmless.
                  if (dispatched.commentIds.length > 0) s.applyPendingComments(dispatched.commentIds);
                  if (dispatched.markIds.length > 0) s.applyPendingMarks(dispatched.markIds);
                } else {
                  // No registry entry (e.g. stream reconnect after reload) —
                  // fall back to resolving everything pending on the image.
                  const commentIds = s.comments.filter((c) => c.imageId === d.imageClientId && c.isPending).map((c) => c.id);
                  const markIds = s.marks.filter((m) => m.imageId === d.imageClientId && m.isPending).map((m) => m.id);
                  if (commentIds.length > 0) s.applyPendingComments(commentIds);
                  if (markIds.length > 0) s.applyPendingMarks(markIds);
                }
              } else {
                // v3.87 — Apply resolves sent comments optimistically, so the
                // isPending-based markCommentsFailed no longer matches them.
                // Re-open the EXACT dispatched ids for Retry when we have them.
                const dispatchedFail = _editFixDispatched.get(d.imageClientId);
                if (dispatchedFail && dispatchedFail.commentIds.length > 0) {
                  s.markCommentsFailedByIds(dispatchedFail.commentIds);
                } else {
                  s.markCommentsFailed(d.imageClientId);
                }
              }
              _editFixDispatched.delete(d.imageClientId);
              // Per-item progress for the Apply bar (total set by runApply
              // before dispatch; runApply's finally resets it to 0/0).
              const { done, total } = get().applyProgress;
              s.setApplyProgress({ done: Math.min(done + 1, total), total });
              break;
            }
            case 'ad_cards': {
              const incomingCards: AdCardData[] = Array.isArray(event.data.cards) ? event.data.cards : [];
              if (incomingCards.length > 0) {
                updateMsg((m) => {
                  const existing = m.adCards || [];
                  const existingIds = new Set(existing.map((c) => c.id));
                  const newCards = incomingCards.filter((c) => !existingIds.has(c.id));
                  return { ...m, adCards: [...existing, ...newCards] };
                });
              }
              break;
            }
            case 'analytics_cards': {
              const incoming = Array.isArray(event.data.cards) ? event.data.cards : [];
              if (incoming.length > 0) {
                updateMsg((m) => ({
                  ...m,
                  toolResults: [...(m.toolResults || []), ...incoming],
                }));
              }
              break;
            }
            case 'trigger_copy_generation': {
              const {
                imageClientIds,
                variationsPerAd = 5,
                additionalDirection = '',
              } = event.data as { imageClientIds?: string[]; variationsPerAd?: number; additionalDirection?: string };
              const currentState = get();
              const projectId = streamProjectId;
              const targetIds = (imageClientIds && imageClientIds.length > 0)
                ? imageClientIds
                : submittedSelection;
              if (projectId && targetIds.length > 0) {
                const capturedIds = [...targetIds];
                set((s) => {
                  const nextSet = new Set(s.adCopyGeneratingImageIds);
                  capturedIds.forEach((id) => nextSet.add(id));
                  return { showCopySidebar: true, adCopyGeneratingImageIds: nextSet };
                });
                (async () => {
                  try {
                    const copyRes = await fetch('/api/canvas/generate-ad-copy', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ projectId, imageClientIds: capturedIds, variationsPerAd, additionalDirection }),
                    });
                    if (!copyRes.ok || !copyRes.body) return;
                    const copyReader = copyRes.body.getReader();
                    const copyDecoder = new TextDecoder();
                    let copyBuf = '';
                    while (true) {
                      const { done: d, value: v } = await copyReader.read();
                      if (d) break;
                      copyBuf += copyDecoder.decode(v, { stream: true });
                      const copyBlocks = copyBuf.split('\n\n');
                      copyBuf = copyBlocks.pop() ?? '';
                      for (const block of copyBlocks) {
                        if (!block.trim()) continue;
                        const copyEvent = parseSSEBlock(block);
                        if (!copyEvent) continue;
                        if (copyEvent.type === 'copy_complete' && copyEvent.data?.variation) {
                          const variation = copyEvent.data.variation as AdCopyVariation;
                          const imgId = (copyEvent.data.imageClientId as string) || capturedIds[0];
                          get().appendCanvasAdCopyVariation(imgId, variation);
                        }
                      }
                    }
                  } catch (err) {
                    console.error('[canvas-store] trigger_copy_generation error:', err);
                  } finally {
                    set((s) => {
                      const newSet = new Set(s.adCopyGeneratingImageIds);
                      capturedIds.forEach((id) => newSet.delete(id));
                      return { adCopyGeneratingImageIds: newSet };
                    });
                  }
                })();
              }
              break;
            }
          }
        }
      }

      clearTimeout(staleThinkingTimer);
      _activeRequestByProject.delete(projectKey);
      {
        const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
        get()._writeActiveWorkflowRunId(numericKey, null);
      }
      get()._writeMessages(streamProjectId, (msgs) =>
        msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false, agentThinking: undefined } : m)
      );
      // EOF can arrive without agent_done while the durable concept run continues.
      if (!sawAgentDone && streamProjectId != null) {
        recoverDetachedConceptRun(streamProjectId, assistantMsgId,
          get().projects[streamProjectId]?.messages ?? [],
          update => get()._writeMessages(streamProjectId, update),
          (projectId, messageId, setId) => get().watchAngleSetRun(projectId, messageId, setId));
      }
      // Stream loop exited normally — clear any sseError flag that an
      // earlier `agent_error` event set mid-stream so the "Connection lost"
      // banner doesn't linger after a successful run. User report:
      // "ran all 20 that time but came back and it still says Connection
      // lost." Symptom: agent_error fires for a non-fatal sub-step (e.g.
      // a single concept's QA evaluator timing out), client sets
      // sseError=true, but the overall run keeps going and finishes
      // successfully — the banner stayed lit forever because nothing
      // cleared it on the success path.
      set((s) => ({
        typingByProject: { ...s.typingByProject, [projectKey]: false },
        sseError: false,
        sseConnected: true,
      }));
    } catch (err: any) {
      clearTimeout(staleThinkingTimer);
      _activeRequestByProject.delete(projectKey);
      {
        const numericKey = typeof projectKey === 'number' ? projectKey : (parseInt(projectKey as string, 10) || null);
        get()._writeActiveWorkflowRunId(numericKey, null);
      }
      if (err.name === 'AbortError') {
        get()._writeMessages(streamProjectId, (msgs) =>
          msgs.map((m) => m.id === assistantMsgId ? { ...m, isStreaming: false, agentThinking: undefined } : m)
        );
        set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
        return;
      }
      const isCreditsExhausted = (err as Error & { code?: string })?.code === 'CREDITS_EXHAUSTED';
      // Resume the existing durable-state poller for this exact concept turn.
      // Keep its pending state instead of claiming generation failed with transport.
      if (!isCreditsExhausted && !sawAgentDone && streamProjectId != null && recoverDetachedConceptRun(
        streamProjectId, assistantMsgId, get().projects[streamProjectId]?.messages ?? [],
        update => get()._writeMessages(streamProjectId, update),
        (projectId, messageId, setId) => get().watchAngleSetRun(projectId, messageId, setId),
      )) {
        set(s => ({ typingByProject: { ...s.typingByProject, [projectKey]: false }, sseError: false, sseConnected: false }));
        return;
      }
      // v7.25 — a drop AFTER the turn finished (agent_done seen) is not an
      // error at all: keep the finished message untouched and quietly
      // reconcile. A drop mid-generation keeps an honest-but-calm message —
      // the run continues server-side and the reconcile pass lands the tiles
      // (SSE-freeze law: dropped stream ≠ broken generation).
      console.error('[canvas-store] agent stream error:', err, { sawAgentDone, sawGenerationProgress });
      if (!isCreditsExhausted && sawAgentDone) {
        get()._writeMessages(streamProjectId, (msgs) =>
          msgs.map((m) =>
            m.id === assistantMsgId
              ? { ...m, isStreaming: false, agentThinking: undefined }
              : m
          )
        );
        set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false }, sseError: false }));
        void get().reconcileImagesFromServer(streamProjectId);
        return;
      }
      const errMsg = isCreditsExhausted
        ? "You've run out of credits. Add credits or upgrade your plan to keep generating."
        : sawGenerationProgress
          ? 'The live connection dropped, but your ads are still generating — they will land on this canvas shortly (refresh if you want to check now).'
          : 'Something went wrong. Please try again.';
      get()._writeMessages(streamProjectId, (msgs) =>
        msgs.map((m) =>
          m.id === assistantMsgId
            ? { ...m, content: errMsg, isStreaming: false, agentThinking: undefined }
            : m
        )
      );
      if (sawGenerationProgress && !isCreditsExhausted) {
        void get().reconcileImagesFromServer(streamProjectId);
      }
      set((s) => ({
        typingByProject: { ...s.typingByProject, [projectKey]: false },
        // CREDITS_EXHAUSTED is a known-state user signal, not a transient SSE
        // failure — don't set sseError so the chat reconnect banner doesn't
        // also flash. The OutOfCreditsModal (raised via triggerCreditsGate)
        // is the canonical surface.
        sseError: !isCreditsExhausted,
      }));
    } finally {
      if (streamProjectId != null) setPlaybookStreamActive(streamProjectId, assistantMsgId, false);
    }
  },

  clearStaleTypingForProject: (projectKey) => {
    const state = get();
    if (!state.typingByProject[projectKey]) return;
    // Only clear if there is no active in-flight sendAgentMessage request for
    // this project key. _activeRequestByProject covers both playbook and normal
    // chat turns so this check is authoritative for all SSE streams.
    if (_activeRequestByProject.has(projectKey)) return;
    console.warn('[canvas-store] clearing stale typing state on mount for project:', projectKey);
    // Use the function's own parameter, not the current active project — after a
    // project switch, activeProjectId may point to a different project and we would
    // clear the wrong one.
    const numericKey = typeof projectKey === 'number'
      ? projectKey
      : (typeof projectKey === 'string' ? (parseInt(projectKey, 10) || null) : null);
    get()._writeMessages(numericKey, (msgs) =>
      msgs.map((m) => m.isStreaming ? { ...m, isStreaming: false, agentThinking: undefined } : m)
    );
    set((s) => ({ typingByProject: { ...s.typingByProject, [projectKey]: false } }));
  },

  _ensureProject: (projectId: number): void => {
    set((s) => {
      if (projectId in s.projects) return {};
      return { projects: { ...s.projects, [projectId]: defaultProjectState(projectId) } };
    });
  },

  _updateProject: (
    projectId: number,
    updater: (p: ProjectState) => ProjectState,
  ): void => {
    set((s) => {
      const current = s.projects[projectId] ?? defaultProjectState(projectId);
      const next = updater(current);
      return { projects: { ...s.projects, [projectId]: next } };
    });
  },

  _writeMessages: (
    projectId: number | null,
    updater: (msgs: CanvasChatMessage[]) => CanvasChatMessage[],
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      const newMessages = reconcileRecoveredRunCards(updater(proj.messages));
      return {
        projects: {
          ...s.projects,
          [projectId]: { ...proj, messages: newMessages },
        },
      };
    });
  },

  _writeConversationHistory: (
    projectId: number | null,
    history: Array<{ role: 'user' | 'assistant'; content: string }>,
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      return {
        projects: {
          ...s.projects,
          [projectId]: { ...proj, conversationHistory: history },
        },
      };
    });
  },

  _writeAgentThinking: (
    projectId: number | null,
    text: string | null,
  ): void => {
    set((s) => {
      if (projectId == null) return {};
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      return {
        projects: { ...s.projects, [projectId]: { ...proj, agentThinking: text } },
      };
    });
  },

  _writeImages: (
    projectId: number | null,
    updater: (imgs: CanvasImage[]) => CanvasImage[],
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      const newImages = updater(proj.images);
      return {
        projects: {
          ...s.projects,
          [projectId]: { ...proj, images: newImages, hasPersistedImages: newImages.length > 0 },
        },
      };
    });
  },

  _writeQaBadges: (
    projectId: number | null,
    updater: (badges: Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }>) =>
              Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }>,
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      const newBadges = updater(proj.imageQaBadges);
      return {
        projects: {
          ...s.projects,
          [projectId]: { ...proj, imageQaBadges: newBadges },
        },
      };
    });
  },

  _writeImageRequestIdToUrl: (
    projectId: number | null,
    updater: (map: Record<string, string>) => Record<string, string>,
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      const newMap = updater(proj.imageRequestIdToUrl);
      return {
        projects: {
          ...s.projects,
          [projectId]: { ...proj, imageRequestIdToUrl: newMap },
        },
      };
    });
  },

  _writeCanvasAdCopy: (
    projectId: number | null,
    updater: (m: Record<string, { variations: AdCopyVariation[]; activeVariationIndex: number }>) =>
              Record<string, { variations: AdCopyVariation[]; activeVariationIndex: number }>,
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      const newAdCopy = updater(proj.canvasAdCopy);
      return {
        projects: {
          ...s.projects,
          [projectId]: { ...proj, canvasAdCopy: newAdCopy },
        },
      };
    });
  },

  _writeCanvasPlaceholders: (
    projectId: number | null,
    updater: (m: Record<string, CanvasPlaceholder>) => Record<string, CanvasPlaceholder>,
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      const newPlaceholders = updater(proj.canvasPlaceholders);
      return {
        projects: {
          ...s.projects,
          [projectId]: { ...proj, canvasPlaceholders: newPlaceholders },
        },
      };
    });
  },

  _writeGenerationRows: (
    projectId: number | null,
    updater: (rows: CanvasGenerationRow[]) => CanvasGenerationRow[],
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      const newRows = updater(proj.generationRows);
      return {
        projects: {
          ...s.projects,
          [projectId]: { ...proj, generationRows: newRows },
        },
      };
    });
  },

  _writePlaybookProgress: (
    projectId: number | null,
    progress: PlaybookProgressState | null,
  ): void => {
    set((s) => {
      const updates: Partial<StoreState> = {};
      if (projectId != null) {
        const proj = s.projects[projectId] ?? defaultProjectState(projectId);
        updates.projects = {
          ...s.projects,
          [projectId]: { ...proj, playbookProgress: progress },
        };
      }
      // No flat-field equivalent — playbookProgress lives on individual messages,
      // not on the store directly. This helper only updates the project-scoped slice.
      return updates;
    });
  },

  _writeWorkflowState: (
    projectId: number | null,
    wfState: WorkflowState | null,
  ): void => {
    set((s) => {
      if (projectId == null) return {};
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      return {
        projects: { ...s.projects, [projectId]: { ...proj, workflowState: wfState } },
      };
    });
  },

  _writeActiveWorkflowRunId: (
    projectId: number | null,
    runId: number | null,
  ): void => {
    set((s) => {
      if (projectId == null) return {};
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      return {
        projects: { ...s.projects, [projectId]: { ...proj, activeWorkflowRunId: runId } },
      };
    });
  },

  _writeProjectView: (
    projectId: number | null,
    patch: Partial<Pick<ProjectState, 'isCanvasOpen' | 'activeView' | 'pendingPanTarget' | 'pendingFitToView' | 'pendingFitToRow'>>,
  ): void => {
    if (projectId == null) return;
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      return {
        projects: { ...s.projects, [projectId]: { ...proj, ...patch } },
      };
    });
  },

  _writeProjectMetadata: (
    projectId: number,
    patch: Partial<Pick<ProjectState, 'brandId' | 'adAccountId' | 'parentId' | 'loadedAt' | 'loading'>>,
  ): void => {
    set((s) => {
      const proj = s.projects[projectId] ?? defaultProjectState(projectId);
      return {
        projects: { ...s.projects, [projectId]: { ...proj, ...patch } },
      };
    });
  },
})));

// ─── E2E hook (dev builds only) ──────────────────────────────────────────────
// Playwright's video-clone flow test (tests/flows/50-video-clone.test.ts)
// drives store actions directly — switchProject / openSidebarPanel /
// addImageToCanvas / setActiveView — because the only UI path into the
// VideoScriptDoc is the PAID /api/video-clone/prepare SSE run, which e2e must
// never fire. import.meta.env.DEV keeps this out of production bundles.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __canvasStore?: typeof useCanvasStore }).__canvasStore = useCanvasStore;
}

// Persist only composer selectors; workflow history and draft model stamps are independent.
if (typeof window !== 'undefined') {
  const key = 'canvas-image-selectors-v1';
  const fields = ['inspire','inspireByProject','imageModel','imageModelByProject','imageTier','imageTierByProject','imageResolution','imageResolutionByProject'] as const;
  try {
    const saved = JSON.parse(window.localStorage.getItem(key) || 'null');
    if (saved && typeof saved === 'object') {
      const restored: Record<string, unknown> = {};
      const valid = (field: string, value: unknown) => field.startsWith('inspire') ? typeof value === 'boolean' : field.startsWith('imageModel') ? ['nano-banana-2','gpt-image-2','gpt-image-2.5-sunburst'].includes(value as string) : field.startsWith('imageResolution') ? ['1k','2k','4k'].includes(value as string) : value === null || ['low','medium','high','xhigh','max','1k','2k','4k'].includes(value as string);
      for (const field of fields) {
        const value = saved[field];
        if (field.endsWith('ByProject')) {
          if (value && typeof value === 'object' && !Array.isArray(value)) restored[field] = Object.fromEntries(Object.entries(value).filter(([id,entry]) => /^\d+$/.test(id) && valid(field,entry)));
        } else if (valid(field,value)) restored[field] = value;
      }
      if (restored.imageModel === 'gpt-image-2' && !saved.imageResolution) restored.imageResolution = '4k';
      useCanvasStore.setState(restored as Partial<CanvasStore>);
    }
  } catch { /* Private browsing and malformed saved settings use defaults. */ }
  useCanvasStore.subscribe((state, previous) => {
    if (!fields.some(field => state[field] !== previous[field])) return;
    try { window.localStorage.setItem(key, JSON.stringify(Object.fromEntries(fields.map(field => [field,state[field]])))); } catch {}
  });
}
